/**
 * The English source of truth for UI copy.
 *
 * Every screen asks for an id, never for a literal string, so a missing
 * translation is a failing test rather than a page half in English. Chinese
 * lives in `zh.ts` under the same ids.
 *
 * WHAT IS DELIBERATELY NOT HERE — and must never be added:
 *   · product names, collection names, colour names, supplier names — catalogue
 *     data, not UI copy. Jacky, 2026-09-16: "product name, supplier name no
 *     translation".
 *   · customer names, addresses, room names, notes — the customer's own words.
 *   · engine-generated warnings and notices. The engine is a shared domain
 *     package; its English strings cross the boundary as data. `noticeKey()`
 *     maps the ones a customer reads onto an id here and passes the rest
 *     through in English rather than silently dropping them.
 */
export const EN = {
  "proposal.link.customer": "Customer Proposal link",
  "proposal.link.readFailed": "Could not read the saved customer link.",
  "proposal.link.retry": "Reload saved link",
  "proposal.sourceLoading": "Loading the enquiry or job…",
  "proposal.link.loading": "Reading the saved customer link…",
  "service.standard": "Standard service",
  "service.custom": "Custom exception",
  "service.choose": "Choose a registered service",
  "service.execution": "Who does this work?",
  "service.artemis": "Artemis",
  "service.contractor": "Contractor",
  "service.zero": "Artemis labour is $0. Materials and disposal are separate job costs.",
  "service.conditions": "Conditions and inclusions",
  "service.settings": "Register or edit standard services and rates",
  "service.loading": "Loading registered services…",
  "service.readFailed": "Registered services could not be loaded. Refresh before choosing standard work.",
  "service.unregistered": "Not registered",
  "service.search": "Find a standard service",
  "service.add": "Add standard service",
  "service.newName": "New standard service",
  "service.sell": "Customer rate · EX GST per unit",
  "service.artemisLabour": "Artemis labour cost · EX GST",
  "service.otherCost": "Artemis materials / disposal / other cost per unit",
  "service.contractorCost": "Complete contractor cost per unit · EX GST",
  "service.contractorReference": "Registered contractor standard service id (when no amount is entered)",
  "service.customerNote": "Customer scope note",
  "service.configureCost": "Register cost and conditions",
  "service.evidence": "Cost source or operator instruction",
  "service.approved": "Cost basis approved for this exact standard work",
  "service.materialsNotice": "Leave unknown costs blank. A labour-only service excludes materials supplied and costed separately; a complete service must include every required material and expense once.",
  "service.advanced": "Service options, measurements and advanced configuration",
  "service.libraryKicker": "Products & services",
  "service.libraryTitle": "Service library",
  "service.libraryLede": "Register the reusable work the team can add to a Proposal: quantity basis, customer wording, EX GST rate, labour and costs.",
  "service.libraryDescription": "One shared catalogue for Proposal quick picks and EmmA. Changes save as a new audited configuration version.",
  "service.libraryVersion": "{title} · version {version} · edited by {by} {date}",
  "service.libraryDefaults": "{title} · on defaults",
  "service.libraryLoadFailed": "Could not load the saved service library, so saving is off. Reload the page.",
  "service.libraryLoading": "Loading the saved service library…",
  "service.changeReason": "What changed",
  "service.changeReasonPlaceholder": "What changed, in a sentence (kept with the version)",
  "service.saveVersion": "Save as version {version}",
  "service.save": "Save service library",
  "service.resetSaved": "Reset to saved",
  "service.saveNote": "Checked against the schema on save · previous versions kept · scope: {scope}",

  'customer.plan.recordedDimensions': '{count} recorded dimensions',
  "cost.prompt": "Review the available cost estimates for this quotation without blocking issue or acceptance. Keep unknown amounts blank. After invoicing, confirm actual supplier invoices, material usage and crew fees on the Job, preserve accepted prices and released copies, and record the internal actual-cost follow-up. Send nothing.",
  "cost.confirmActual": "Confirm actual costs",
  "cost.openTasks": "Open internal confirmation tasks",
  "cost.details": "Work items and cost detail",
  "cost.previous": "Previous costs",
  "cost.next": "More costs",
  "cost.engine": "Configured complete work cost",
  "cost.catalogue": "Current supplier material cost",
  "cost.standard": "Approved work-item rate",
  "cost.truncated": "Showing {count} of {total} work items. EmmA can read further items when reviewing costs.",
  "cost.title": "Internal cost review \u00b7 EX GST",
  "cost.needsYou": "EmmA needs you · actual costs after invoicing",
  "cost.unresolved": "{count} cost estimates remain unknown · issue is available",
  "cost.readFailed": "Cost estimates are unavailable. Quotation issue is still available.",
  "cost.releaseBlocked": "Actual costs are confirmed after invoicing.",
  "cost.discuss": "Discuss costs with EmmA",
  "cost.saveFirst": "Save the current work before reviewing its cost.",
  "cost.ready": "All offered work has a supported cost.",
  "cost.work": "Work item",
  "cost.basis": "Cost basis",
  "cost.amount": "Cost \u00b7 EX GST",
  "cost.missing": "Needs you \u00b7 cost basis missing",
  "cost.covered": "Covered by the evidenced package",
  "cost.base": "Base quotation",
  "cost.option": "Alternative",
  "cost.variation": "Provisional work",
  "cost.baseCost": "Base cost",
  "cost.profit": "Base profit",

  "site.projects.intro": "Our latest finished floors from the past 30 days, plus every project from 2026. Installation photos from our own crews, with the floor and colour linked.",
  "site.projects.period": "Project period",
  "site.projects.recent": "Past 30 days",
  "site.projects.year": "All {year}",
  "site.projects.count": "{count} projects · up to {size} per page",
  "site.projects.empty": "No projects in this view yet",
  "site.projects.emptyBody": "We are reviewing finished-floor photos. Choose another period to see more of our work.",
  "site.projects.pagination": "Project pages",
  "site.projects.previous": "← Previous",
  "site.projects.next": "Next →",
  "site.projects.page": "Page {page} of {pages}",

 "customer.pages.method":"How Artemis lays a floor",
 "customer.pages.document":"Your flooring Proposal",
 "customer.pages.pdf":"Open / download Proposal PDF",
 "customer.pages.choices":"Choose your product and colour / confirm your Proposal",
 "customer.pages.zoom":"Open page {page} to zoom",
 "customer.pages.failed":"This page could not load. Open the reviewed PDF or expand the choices below.",

 "woLink.title":"Installer Work Order",
 "woLink.help":"Keep this link. It opens the current work order without signing in.",
 "woLink.unavailable":"This work-order link is unavailable. Contact the office.",
 "woLink.open":"Open work order",
 "woLink.copy":"Copy work-order link",
 "woLink.copied":"Work-order link copied",
 "woLink.loading":"Loading work order…",
 "woLink.empty":"Assign an installer to a saved day to make a work-order link available.",
 "woLink.sms":"SMS work order",
 "woLink.recipient":"Installer mobile",
 "woLink.message":"SMS message",
 "woLink.send":"Send SMS through the gateway",
 "woLink.sending":"Sending…",
 "woLink.sent":"Queued to the SMS gateway as Artemis FLR. Delivery shows on the Communications desk.",
 "woLink.pendingSend":"Saved; the send is waiting on the Communications desk.",
 "woLink.sendFailed":"Not sent: {message}",
 "woLink.noRecipient":"No mobile number is saved for this crew. Add one on the installer profile.",
 "woLink.smsHelp":"Leaves through the SMS gateway (sender Artemis FLR) and is recorded on the Communications desk.",
 "woLink.smsBody":"Hi {crew}, your work order for {name}, {address}:\n{url}\nPlease keep this link for the schedule and job updates.",
 "woLink.site":"Site and access",
 "woLink.maps":"Navigate to site",
 "woLink.days":"Assigned work days",
 "woLink.done":"Day complete",
 "woLink.complete":"Mark day complete",
 "woLink.scopeTick":"I completed the assigned work shown above",
 "woLink.accept":"Accept assigned work",
 "woLink.accepted":"Assigned work accepted",
 "woLink.acceptFirst":"Accept the assigned work before reporting completion.",
 "woLink.notes":"Notes / issues",
 "woLink.save":"Save progress note",
 "woLink.issue":"Report an issue",
 "woLink.saved":"Saved. Your report is shown below.",
 "woLink.failed":"The report was not saved. Your text is kept; refresh the work order and try again.",
 "woLink.pending":"Report submitted for review; completion is not yet recorded.",
 "woLink.history":"Saved reports",
 "woLink.materials":"Materials",
 "woLink.pickups":"Supplier pickups",
 "woLink.pickupNumber":"Show this number at pickup",
 "woLink.pickupAwaiting":"Supplier pickup number awaiting email confirmation. Ask the office before collecting.",
 "woLink.drawing":"Site drawing · reference only; verify dimensions onsite",
 "woLink.photos":"Add photos",
 "woLink.print":"Print work order",
 "woLink.refresh":"Refresh work order",
 "woLink.noScope":"Office needs to record this day’s scope before it can be completed.",
 "woLink.photoFailed":"Photo upload failed. The report has not been submitted.",
 "woLink.photoView":"Open saved photo",
 "woLink.test":"TEST WORK ORDER — rehearsal data",

  'installDay.office':'Office',
  'installDay.officeClosed':'Closed by office',
  'installDay.reopened':'Day reopened',
  'installDay.unverified':'Past day · office has not closed it',
  'installDay.planned':'Planned',
  'installDay.crewUnknown':'Crew to confirm',
  'installDay.close':'Close day',
  'installDay.reopen':'Reopen day',
  'installDay.note':'Office note',
  'installDay.noteRequired':'Enter an office note before saving.',
  'installDay.closeHint':'What work did the office verify?',
  'installDay.reopenHint':'Why is this day being reopened?',
  'installDay.confirmClose':'Save day closure',
  'installDay.confirmReopen':'Save reopening',
  'installDay.saving':'Saving…',
  'installDay.closed':'Installation day closed by office',
  'installDay.notSaved':'The day was not saved. Refresh and try again.',
  'installDay.installerRecorded':'Installer completion recorded',
  "cash.managedDeposit": "Managed in Payments & receipts. Corrections retain receipt history.",
  "cash.final": "Final balance",
  "cash.open": "Cash payments & receipts",
  "cash.title": "Payments & receipts",
  "cash.close": "Close",
  "cash.loading": "Loading payment records…",
  "cash.failed": "Payment records could not be loaded. Retry.",
  "cash.agreedCash": "Agreed cash amount",
  "cash.noAddedGst": "No GST added",
  "cash.cashHint": "Record the actual cash received against the agreed cash amount. Existing deposits are deducted once. These records do not reconcile Xero or bank payments.",
  "cash.agreedExGst": "Agreed amount (EX GST)",
  "cash.gst": "GST",
  "cash.payable": "Agreed amount payable",
  "cash.received": "Total received",
  "cash.balance": "Balance remaining",
  "cash.credit": "Credit remaining",
  "cash.hint": "Record the actual cash received. The current Job agreement and recorded GST treatment determine the payable amount. Existing deposits are deducted once. These records do not reconcile Xero or bank payments.",
  "cash.kind": "Payment for",
  "cash.deposit": "Deposit",
  "cash.part": "Part payment",
  "cash.amount": "Cash received · AUD",
  "cash.date": "Date received · Sydney",
  "cash.note": "Receipt note",
  "cash.record": "Record cash & create receipt",
  "cash.opening": "Existing deposit",
  "cash.openingHint": "Already included in total received. Issue its receipt without recording another payment.",
  "cash.issue": "Issue cash receipt",
  "cash.unknownDate": "Date not recorded",
  "cash.unknownMethod": "Method not recorded",
  "cash.voided": "Voided",
  "cash.preview": "Receipt / Print PDF",
  "cash.email": "Email receipt",
  "cash.sms": "TXT receipt",
  "cash.correct": "Correct record",
  "cash.issueHint": "Confirm the actual date this existing cash payment was received. This does not add a payment.",
  "cash.cancel": "Cancel",
  "cash.voidHint": "Void an incorrect record with a reason. Its receipt stays in history marked VOID; this does not refund cash.",
  "cash.reason": "Correction reason",
  "cash.void": "Void incorrect payment",
  "cash.saved": "Cash recorded and receipt saved. Nothing sent.",
  "cash.issued": "Receipt saved for the existing payment. Nothing sent.",
  "cash.queued": "Receipt queued for delivery. This is not yet delivery confirmation.",
  "cash.notSent": "Receipt was not sent.",

 'commercial.editOptions':'Edit service options',
 'commercial.optionLabel':'Option description',
 'commercial.addOption':'+ Service option',
 'commercial.offers':'Offer',
 'commercial.copy':'Copy',
 'commercial.duplicateOffer':'Duplicate offer / package',
 'commercial.history':'Revision history',
 'commercial.historyHelp':'Restoring a working revision creates a new version when saved. Issued copies and accepted agreements remain unchanged.',
 'commercial.historyFailed':'Revision history is unavailable.',
 'commercial.issued':'Issued copy',
 'commercial.restored':'Earlier working revision restored. Review and save as a new version.',
 'commercial.autoAssessment':'Auto-update assessment',

 'commercial.offerRole':'Offer role',
 'commercial.replaces':'Alternative for',

 "commercial.workingRevision":"Working revision",
 "commercial.details":"Proposal details",
 "commercial.reading":"Reading assessment…",
 "commercial.latest":"Update measurements",
 "commercial.followAll":"Follow assessment on every line",
 "commercial.followAllDone":"{followed} lines now follow the assessment.",
 "commercial.followAllUnresolved":"No measurement for: {names} — left as typed.",
 "commercial.measurePlusWaste":"Measured {measured} m² + {percent}% wastage ({waste} m²) = {quantity} m²",
 "commercial.quantityOverride":"Quoted quantity overridden to {quantity} m²",
 "commercial.measureUnavailable":"Measured area unavailable · quoted quantity shown",
 "commercial.wasteUnavailable":"Measured {measured} m² · wastage rule unavailable",
 "commercial.rollBasis":"Measured {measured} m² · roll {length} LM × {width} m = {area} m²",
 "commercial.preview":"Preview / document",
 "commercial.scope":"Scope & price",
 "commercial.addProduct":"Products & services",
 "commercial.serviceHelp":"Add any service, including furniture moving. Enter your own description, quantity and price.",
 "commercial.serviceExample":"e.g. Move furniture",
 "commercial.serviceInvalid":"Enter a service description and valid quantity and rate.",
 "commercial.addWork":"+ Add service",
 "commercial.duplicate":"Duplicate",
 "commercial.undo":"Undo",
 "commercial.measurements":"Quantity details",
 "commercial.description":"Scope / description",
 "commercial.unit":"Unit",
 "commercial.rate":"Rate · EX GST",
 "commercial.amount":"Amount · EX GST",
 "commercial.product":"Selected product",
 "commercial.follow":"Follow assessment",
 "commercial.manual":"Manual override",
 "commercial.alternative":"Alternative · outside total",
 "commercial.resetRate":"Use suggested rate",
 "commercial.included":"Included",
 "commercial.excluded":"Excluded",
 "commercial.variation":"Variation · outside total",
 "commercial.optional":"Optional · outside total",
 "commercial.empty":"Add a product or custom work to begin.",
 "commercial.measured":"Measured quantity",
 "commercial.scotiaDrawing":"Latest canvas: {rooms} Scotia {length} LM across {count} traces ({status}).",
 "commercial.scotiaEstimate":"drawing estimate; onsite check needed",
 "commercial.scotiaConfirmed":"measurement confirmed",
 "commercial.scotiaSeparate":"The pieces above are material allowance, not installation metres.",
 "commercial.scotiaNewer":"This drawing is newer than the saved Proposal; review Update measurements.",
 "commercial.source":"Quantity source",
 "commercial.carpetBasis":"Floor area follows assessment. Purchased roll length stays separate.",
 "commercial.allowance":"Measured area plus {percent}% allowance.",
 "commercial.treatment":"Offer treatment",
 "commercial.overrideHelp":"Manual quantities and selling rates survive drawing updates. Factual measurements are unchanged.",
 "commercial.selectLine":"Select a line to see its measurement and quantity source.",
 "commercial.assessment":"Open assessment",
 "commercial.total":"FULL OFFER · EX GST",
 "commercial.save":"Save working scope",
 "commercial.deliver":"Send / private link",
 "commercial.back":"Back to scope & price",
 "commercial.draftPreview":"Private working draft · changes not issued",
"commercial.openBookReleased":"Open-book customer copy · released v{version} · this is what the customer sees",
"commercial.openBookOffered":"{products} products offered · carpet: {carpet}",
"commercial.openBookNoCarpet":"none",
"commercial.openBookNote":"An open-book Proposal is priced at release from catalogue costs and the customer chooses the board on their page. The products and pricing in the builder below are the working draft, not the customer copy; a new release goes through EmmA's open-book publish.",
"commercial.openBookPreview":"Open the customer copy",
 "commercial.print":"Print / save PDF",
 "commercial.recovery":"A recovered working copy is available. Review it before saving.",
 "commercial.restore":"Restore working copy",
 "commercial.discard":"Discard recovery copy",
 "commercial.conflict":"Saving paused. Keep your working copy and reconcile the saved version before retrying.",
 "commercial.storageFailed":"Browser recovery is unavailable. Save before leaving.",
 "commercial.updated":"Latest assessment loaded. Following quantities updated; manual quantities and prices retained.",
 "commercial.noAssessment":"No saved shared assessment is available for this work.",
 "commercial.previewPending":"Pricing must finish before the customer preview can be prepared.",
 "commercial.saveFailed":"Draft was not saved. Your working copy is retained.",
 "commercial.copyOffer":"Prepare another offer",

 'installationNotice.title':'Installation service information',
 'installationNotice.preview':'Exact customer copy',
 'installationNotice.stale':'The saved quote scope changed. Your wording is retained. Compare it with the current scope below before saving.',
 'installationNotice.recheck':'I have checked the current scope — keep my wording',
 'installationNotice.recoveryFound':'Your previous failed save retained a recovery copy. Nothing was sent.',
 'installationNotice.restore':'Restore my write-up',
 'installationNotice.emailMissing':'Email not saved yet — save it in Contact, then return here',
 'installationNotice.phoneMissing':'Mobile number not saved yet',
 'installationNotice.editContact':'Open Contact — wording stays here',
 'installationNotice.recoveryUnavailable':'Saved-draft recovery is temporarily unavailable. Your working copy below is not changed.',
 'installationNotice.kept':'Working copy kept in this tab as you type. You can visit Contact and return, or reload. Not yet saved to the Job; signing out or closing this tab removes this local copy.',
 'installationNotice.storageFailed':'This browser cannot keep a recovery copy. Copy your wording before leaving or refreshing this page.',
 'installationNotice.sendFailed':'Sending was not confirmed. Review the refreshed recipient, copy and status before retrying.',
 'installationNotice.help':'A Job-linked customer notice. No signature or reply required. Preparing a draft sends nothing and changes no acceptance, payment or booking.',
 'installationNotice.loading':'Loading notices…',
 'installationNotice.unavailable':'Notices are unavailable. Refresh before preparing or sending.',
 'installationNotice.sourceError':'The saved quote scope needs reconciliation before preparing a notice. Previous copies remain below.',
 'installationNotice.emma':'Ask EmmA to prepare',
 'installationNotice.prepare':'Prepare notice',
 'installationNotice.reviewScope':'Check this saved quote against what the customer actually agreed. Corrections here describe the notice only; they do not repair the quote or installation instructions.',
 'installationNotice.scope':'Agreed scope — one item per line',
 'installationNotice.details':'Confirmed job-specific arrangements',
 'installationNotice.detailsHelp':'For example: underlay by floor, finishing, customer skip and agreed payment arrangements. No new fees or obligations.',
 'installationNotice.evidence':'Internal agreement evidence — not sent to customer',
 'installationNotice.language':'Notice language',
 'installationNotice.email':'Email',
 'installationNotice.sms':'Text message',
 'installationNotice.smsHelp':'SMS is limited to 1,600 characters including the service information. Longer notices must use email; nothing is truncated.',
 'installationNotice.confirm':'These scope and job-specific details match the customer agreement; this notice introduces no new obligations.',
 'installationNotice.save':'Save draft & preview',
 'installationNotice.saved':'Notice draft saved; nothing sent',
 'installationNotice.notSaved':'Not saved to the Job. Your wording remains in the editor. Fix the issue below, then retry.',
 'installationNotice.cancel':'Close editor — keep wording',
 'installationNotice.empty':'No installation notice saved for this Job yet.',
 'installationNotice.history':'Saved copies',
 'installationNotice.from':'From',
 'installationNotice.deliveryHelp':'The saved text is the customer copy. Submitted means the provider accepted the send, not that the customer read or agreed to it.',
 'installationNotice.uncertain':'Sending outcome is uncertain. Check delivery evidence before any resend.',
 'installationNotice.state.draft':'Draft — not sent',
 'installationNotice.superseded':'Older copy — a newer notice is available',
 'installationNotice.state.queued':'Queued',
 'installationNotice.state.sending':'Sending',
 'installationNotice.state.submitted':'Submitted to provider',
 'installationNotice.state.uncertain':'Delivery uncertain',
 'installationNotice.state.failed':'Send failed',
  "installer.guide.eyebrow": "ARTEMIS FLOORING · INSTALLER GUIDE",
  "installer.guide.title": "Your workday, on your phone.",
  "installer.guide.intro": "Welcome to Artemis Installer. Sign in with your own mobile number, then add Artemis to your home screen for easy access.",
  "installer.guide.open": "Open installer portal",
  "installer.guide.free": "No App Store or Google Play download needed.",
  "installer.guide.login": "1. Sign in with your mobile",
  "installer.guide.login1": "Open the portal in Safari on iPhone or Chrome on Android. If this guide opened inside WeChat or another app, choose Open in browser first.",
  "installer.guide.login2": "Enter the mobile number that received your welcome text. Australian numbers can start with 04 or +61. Tap Send code.",
  "installer.guide.login3": "Enter the code from your SMS and confirm. No password is needed. Keep your code private.",
  "installer.guide.login4": "Use your own account. Workers see assigned work; company managers can manage their team and allocate workers to company work.",
  "installer.guide.install": "2. Add Artemis to your home screen",
  "installer.guide.installintro": "Do these steps on the installer portal, not this guide. This creates an app icon that opens your workspace.",
  "installer.guide.apple": "Apple · iPhone",
  "installer.guide.apple1": "In Safari, open install.artemisflooring.com. Tap Share (the square with an upward arrow); it may be inside the More menu.",
  "installer.guide.apple2": "Choose Add to Home Screen. If missing, scroll down the share sheet and use Edit Actions to add it.",
  "installer.guide.apple3": "Keep Open as Web App turned on if shown, then tap Add. Open the Artemis icon from your home screen.",
  "installer.guide.android": "Android · Chrome",
  "installer.guide.android1": "In Chrome, open install.artemisflooring.com. Tap the three-dot menu beside the address bar.",
  "installer.guide.android2": "Choose Install and create shortcut → Install. Some versions say Install app or Add to Home screen.",
  "installer.guide.android3": "Confirm the prompts, then open Artemis from your home screen or app drawer.",
  "installer.guide.after": "The first time you open the home-screen app, it may ask you to sign in again. Use the same mobile number. Stay connected when signing in, checking work and saving updates.",
  "installer.guide.help": "Need a hand?",
  "installer.guide.code": "No code or unable to sign in?",
  "installer.guide.codebody": "Check the number and mobile reception. Use the latest code. If it still fails, contact the Artemis office or your company manager so they can check your account and saved number. Do not create a second account.",
  "installer.guide.empty": "Signed in, but no jobs?",
  "installer.guide.emptybody": "Your access may be ready before work is assigned. Ask the office or your company manager to check your allocation.",
  "installer.guide.reply": "The Artemis FLR welcome text is one-way. Contact the office or your manager through your usual contact method for help.",
  "installer.guide.sources": "Phone setup help: Apple · Google",
  "installer.guide.pageTitle": "Getting started · Artemis Installer",

  'job.workingProposal': 'Current saved Proposal · EX GST',
  'job.recordedValue': 'Recorded Job value',
  'job.quoteProposal': 'Quote & Proposal',
  'job.profit.title': 'Installer fee & profitability',
  'job.tab.quote': 'Quote & payments',
  'job.tab.notes': 'Notes & history',
  'jobPage.title': "Customer's job page",
  'jobPage.help': 'A private link the customer opens to see their floor, the colours and the install date, and to record a choice. Every open and choice is logged here.',
  'jobPage.live': 'The customer page is available in the live workspace.',
  'jobPage.loading': 'Loading the customer page…',
  'jobPage.status.active': 'Active',
  'jobPage.status.revoked': 'Revoked',
  'jobPage.status.expired': 'Expired',
  'jobPage.createdAt': 'Created',
  'jobPage.opens': 'Opens',
  'jobPage.lastOpened': 'last',
  'jobPage.neverOpened': 'never opened',
  'jobPage.expires': 'Expires',
  'jobPage.choice': "Customer's choice",
  'jobPage.noChoice': 'not yet',
  'jobPage.create': 'Create page link',
  'jobPage.replace': 'Replace link',
  'jobPage.revoke': 'Revoke',
  'jobPage.copy': 'Copy link',
  'jobPage.copyNow': 'Copy it now: only its fingerprint is stored, so it cannot be shown again. Replacing the link turns this one off.',
  'jobPage.confirmReplace': "Replace the customer's link? The one they have stops working.",
  'jobPage.confirmRevoke': "Revoke the customer's link? The page stops answering.",
  'jobPage.noContact': 'Save the customer on the job first.',
  'jobPage.created': 'Customer page link created.',
  'jobPage.revoked': 'Customer page link revoked.',
  'jobPage.message.draft': 'Draft email to customer',
  'jobPage.message.subject': 'Your flooring job with Artemis',
  'jobPage.message.body': 'Hi{name},\n\nHere is your private page for your flooring job: {url}\n\nIt shows your floor and the colours to choose from, and your installation date. Tap a colour and confirm it there, or reply to this email.',
  'jobPage.message.drafted': 'Email draft saved; send it from Communications.',
  'jobPage.message.review': 'The email is drafted. Open Communications to review and send it.',
  'jobPage.log': 'Page activity',
  'jobPage.actor.customer': 'Customer',
  'jobPage.actor.staff': 'Office',
  'jobPage.event.open': 'opened the page',
  'jobPage.event.choice': 'chose {colour} for {product}',
  'jobPage.event.created': 'created the link',
  'jobPage.event.revoked': 'revoked the link',
  'jobPage.event.escalation': 'asked for the office: {what}',
  'job.ov.accepted': 'Accepted value',
  'job.ov.noQuote': 'no accepted quote',
  'job.ov.payments': 'Payments',
  'job.ov.deposit': 'deposit {amount} · {date}',
  'job.ov.noDeposit': 'no deposit recorded',
  'job.ov.paidXero': 'paid in Xero {amount} · {due} outstanding · {invoice}',
  'job.ov.paid': 'paid in full {date}',
  'job.ov.fee': 'Installer fee',
  'job.ov.feeNone': 'no subcontractor fee',
  'job.ov.feeUnresolved': '{count} line(s) need a fee',
  'job.ov.feeConfirmed': 'confirmed, text {state}',
  'job.ov.feeUnconfirmed': 'not yet confirmed to the crew',
  'job.ov.profit': 'Gross profit',
  'job.ov.install': 'Installation',
  'job.ov.nextDay': 'next crew-day {date} · {crew}',
  'job.ov.lastDay': 'last crew-day {date} · {crew}',
  'job.ov.noDay': 'no crew-day booked',
  'job.ov.materials': 'Materials',
  'job.ov.orders': '{count} order(s) · {committed} sent',
  'job.ov.noOrders': 'no purchase order',
  'job.ov.open': 'Open',
  'job.profit.loading': 'Reading purchase orders and crews…',
  'job.profit.unavailable': 'Profitability could not be read.',
  'job.profit.revenue': 'Accepted value',
  'job.profit.materials': 'Materials',
  'job.profit.materialsOrders': '{committed} sent · {planned} in draft',
  'job.profit.materialsQuote': 'Engine cost on the accepted quote (materials and labour)',
  'job.profit.materialsSuperseded': '{count} superseded draft(s) not counted',
  'po.cancelDraft': 'Cancel this draft',
  'po.cancelDraftReason': 'Why it is withdrawn, e.g. superseded by the order sent on 29 Sep',
  'po.cancelDraftConfirm': 'Cancel draft',
  'po.cancelSent': 'Cancel with supplier',
  'po.cancelSentHelp': 'Marks this order cancelled and drafts the cancellation email to {supplier}. The email waits on Communications for your approval.',
  'po.cancelSentReason': 'Why it is cancelled (the supplier reads this)',
  'po.cancelSentConfirm': 'Cancel order and draft email',
  'po.cancelSentDone': 'Order cancelled. The email to {supplier} is drafted and waiting for your approval.',
  'po.cancelSentOpenComms': 'Open Communications',
  'orders.title': 'Orders',
  'orders.lede': 'What needs a job, what is coming, and what is done. Orange only when a job is about to start and the supplier has said nothing.',
  'orders.group.needsJob': 'Needs a job',
  'orders.group.needsJobSub': 'recorded from an invoice or WeChat, not ordered through a PO',
  'orders.group.toSend': 'To send',
  'orders.group.toSendSub': 'drafts not yet sent',
  'orders.group.onTheWay': 'Ready or on the way',
  'orders.group.onTheWaySub': 'sent to the supplier',
  'orders.group.done': 'Done',
  'orders.state.cancelled': 'cancelled',
  'orders.state.collected': 'collected',
  'orders.state.delivered': 'delivered',
  'orders.state.noJob': 'not on a job yet',
  'orders.state.draft': 'draft, not sent',
  'orders.state.ready': 'ready to collect',
  'orders.state.confirmed': 'confirmed',
  'orders.state.noWord': 'no word from {supplier} yet',
  'orders.theSupplier': 'the supplier',
  'orders.noJobYet': 'no job yet',
  'orders.multiJob': '{count} jobs',
  'orders.fact.sent': 'sent {date}',
  'orders.fact.pickup': 'pickup {ref}',
  'orders.fact.installs': 'installs {date}',
  'orders.fact.credit': 'on credit terms',
  'orders.tag.check': 'Check today',
  'orders.tag.done': 'Done',
  'orders.tag.draft': 'Draft',
  'orders.tag.confirmed': 'Confirmed',
  'orders.tag.sent': 'Sent',
  'orders.open': 'Open',
  'orders.assignTo': 'Assign to {job}',
  'orders.otherJob': 'Other job…',
  'orders.chooseJob': 'Choose a job…',
  'orders.stock': 'Stock',
  'orders.suggest': 'Looks like {job}.',
  'orders.showDone': 'Show done',
  'orders.newOrder': 'New order for a Job',
  'orders.tools': 'Prepare an order, decisions waiting, supplier replies',
  'job.profit.misc.title': 'Miscellaneous material costs',
  'job.profit.misc.planned': 'Not ordered',
  'job.profit.misc.ordered': 'Already ordered',
  'job.profit.misc.received': 'Received',
  'job.profit.misc.estimate': 'Estimate',
  'job.profit.misc.reference': 'Reference price; invoice unverified',
  'job.profit.misc.actual': 'Verified purchase cost',
  'job.profit.misc.replaced': 'Counted in the matching PO',
  'job.profit.gap.material_cost_unresolved': 'Some material costs are not established; profit remains partial.',
  'job.profit.materialsNone': 'No purchase order or engine cost yet',
  'job.profit.labour': 'Installer labour',
  'job.profit.labourBasis': 'Subcontractor work carries a fee. Own-crew salaries are included in fixed operating costs.',
  'job.profit.profit': 'Gross profit',
  'job.profit.complete': 'complete',
  'job.profit.partial': 'partial · see gaps',
  'job.profit.noCrew': 'No crew-day on the plan yet.',
  'job.profit.costUnresolved': 'fee not set',
  'job.profit.kind.crew': 'Artemis crew',
  'job.profit.kind.subcontractor': 'Subcontractor · declared rates',
  'job.profit.kind.staff': 'Staff · no Job cost',
  'job.profit.kind.unknown': 'Crew without a labour basis',
  'job.profit.work.matched': 'matched',
  'job.profit.work.quantity_missing': 'quantity missing',
  'job.profit.work.unit_mismatch': 'unit differs from the rate',
  'job.profit.work.ambiguous': 'more than one rate fits',
  'job.profit.work.missing_rate': 'no approved rate',
  'job.profit.work.unmapped': 'no matching service',
  'job.profit.unassigned': '{count} work line(s) not assigned to anyone yet · no labour cost counted',
  'job.profit.gap.no_accepted_quote': 'No accepted quote on the Job.',
  'job.profit.gap.no_material_cost': 'No purchase order yet: material cost unknown.',
  'job.profit.gap.orders_not_sent': 'Purchase orders are still drafts; the material figure is planned, not committed.',
  'job.profit.gap.labour_unresolved': 'An installer line has no fee yet: pick a rate or type the fee. Labour is partial until then.',
  // Estimate-then-actual costing (Jacky, 23 Oct 2026).
  'job.profit.kind.estimate': 'Estimate',
  'job.profit.gap.labour_estimated': 'Some work has no crew yet: its labour is estimated at the Artemis standard subcontractor rates.',
  'job.profit.gap.materials_estimated': 'Some material costs use catalogue, reference or provisional estimates.',
  'job.profit.est': 'est.',
  'job.profit.estimate.title': 'Not yet assigned · Artemis standard rates',
  'job.profit.estimate.total': 'Estimated labour',
  'job.profit.estimate.noRate': 'no standard rate',
  'job.profit.estimate.help': 'Replaced by the real fee once a crew is assigned and its fee is set; an own crew costs $0.',
  'job.profit.gap.labour_in_quote_cost': 'The engine cost already includes labour; crew rates are shown, not added.',
  'job.profit.gap.work_unassigned': 'Some work lines are not assigned.',
  'job.profit.work.none': 'recorded fee',
  'job.profit.basis.override': 'typed',
  'job.profit.basis.rate_book': 'rate book',
  'job.profit.basis.agreed': 'agreed extra work',
  'job.profit.basis.proposed': 'proposed from rate book',
  'job.profit.needsFee': 'needs a fee',
  'job.profit.edit': 'Edit',
  'job.profit.clear': 'Clear',
  'job.profit.save': 'Save',
  'job.profit.cancel': 'Cancel',
  'job.profit.pickRate': 'Pick a rate…',
  'job.profit.feeInput': 'Fee (AUD ex GST)',
  'job.profit.removeReason': 'Fee cleared on the Job card',
  'job.profit.addExtra': 'Add a fee line payable to the crew — description, quantity × value — for work the customer is not charged for (becomes a plan task)',
  'job.profit.extraParty': 'Installer',
  'job.profit.extraLabel': 'Description',
  'job.profit.extraQty': 'Qty',
  'job.profit.extraUom': 'Unit',
  'job.profit.work.included': 'laid under the floor installation rate',
  'job.profit.basis.included': 'included in floor rate',
  'job.profit.basis.standard': 'Artemis standard rate',
  'job.profit.basis.pass_through': 'passed through at the customer charge',
  'job.profit.basis.own_crew': 'own crew · not a cost', // 16 Oct 2026: own crew always $0
  'job.profit.standardRate': 'Artemis standard',
  'job.profit.qtyInput': 'Qty',
  'job.profit.assignOwn': 'Assign work to an own crew',
  'job.profit.assignOwnHelp': 'An employee crew needs no fee confirmation. The item goes onto their crew-day on this Job, or a new day on the plan\'s first date; the Installation tab shows the result.',
  'job.profit.assignBtn': 'Assign',
  'job.profit.assigned': 'Assigned to {name}: nothing to confirm',
  'job.profit.assignNeedsPlan': 'Plan this Job on the Installation tab first (a crew-day without a named crew cannot be changed from here).',
  'job.profit.ownCrewUnrated': 'own crew · no standard rate set',
  'job.profit.ownCrewNoRate': 'own crew · no fee to confirm · standard rate not set',
  'job.profit.ownCrewCost': 'Own crew · labour is not a cost',
  'job.profit.gap.own_crew_unrated': 'An own-crew line has no standard rate yet: set one in Settings → Installer standard rates. Nothing to confirm with the crew; profit leaves that labour out until then.',
  'job.profit.feeTitle': 'Installer fee',
  'job.profit.payable': 'Payable to {name}',
  'job.profit.exGst': 'ex GST',
  'job.profit.pay.cash': 'paid in cash',
  'job.profit.pay.transfer': 'paid by bank transfer',
  'job.profit.pay.unset': 'payment preference not recorded on the installer account',
  'job.profit.col.work': 'Work',
  'job.profit.col.qty': 'Qty',
  'job.profit.col.rate': 'Rate',
  'job.profit.col.fee': 'Fee',
  'job.profit.col.basis': 'Basis',
  'job.profit.extraValue': 'Value per unit',
  'job.profit.extraLabelHint': 'e.g. Move fridge and washing machine',
  'job.profit.confirmFee': 'Confirm fee & text {name}',
  'job.profit.confirm.needsLines': 'Every line needs a fee before the crew is told',
  'job.profit.confirm.preparing': 'Fixing the lines and preparing the text…',
  'job.profit.confirm.failed': 'Could not prepare the text: {message}',
  'job.profit.confirm.title': 'Text to the crew on confirm',
  'job.profit.confirm.to': 'To',
  'job.profit.confirm.hold': 'Quiet hours: leaves {time} Sydney',
  'job.profit.confirm.now': 'Leaves now',
  'job.profit.confirm.send': 'Confirm {total} and send',
  'job.profit.confirmed': 'Fee confirmed {date} · {total} · text {state}',
  'job.profit.confirmedChanged': 'lines changed since; confirm again to text the update',
  'job.profit.recorded': 'recorded at {quantity} · {fee}; follows the Proposal now',
  'job.profit.comm.queued': 'queued',
  'job.profit.comm.sending': 'sending',
  'job.profit.comm.submitted': 'sent',
  'job.profit.comm.failed': 'failed',
  'job.profit.comm.uncertain': 'uncertain',
  'job.profit.variation.open': 'Variation raised by the crew {date}',
  'job.profit.variation.proposed': 'they expect {amount}',
  'job.profit.variation.accept': 'Accept',
  'job.profit.variation.decline': 'Decline',
  'job.profit.variation.acceptDefault': 'Accepted; adjusting the fee lines',
  'job.profit.variation.declineNeedsReason': 'Type the reason the crew will read before declining',
  'job.profit.variation.notePlaceholder': 'Reply the crew will read…',
  'job.profit.variation.help': 'Accepting changes nothing by itself: adjust the lines, then confirm again and the correction is texted. Declining keeps the confirmed fee and sends your reason to their portal.',
  'job.profit.variation.accepted': 'Variation accepted {date}: adjust the lines and confirm again',
  'job.profit.variation.declined': 'Variation declined {date}',
  'communications.discard': 'Discard draft',
  'communications.discardReason': 'Will not be sent: discarded on the Communications desk',
  'job.savedQuotes': 'Saved quote history',
  'job.workingUnavailable': 'Current Proposal could not be loaded.',
  'job.workingNone': 'No saved working Proposal',
  'job.workingUnpriced': 'Pricing unavailable / updating',
  'job.workingHelp': 'Latest saved working scope. Unsaved edits are not shown here. Accepted quotes and the recorded Job value remain separate.',
  'proposal.accept.revisionRule': 'Record the customer’s go-ahead for working version {version}. This updates the existing Job and sends no message or private link.',
  'proposal.accept.confirmRevision': 'Record revised acceptance',
  'proposal.accept.savedRevision': 'Revision accepted at {amount} + GST for existing Job {ref}.',
  'proposal.accept.reviseJob': 'Accept this revision into existing Job {ref}. The previous accepted quote remains in history; the Job value will update.',
  'proposal.source.title': 'Original saved quote · history',
  'proposal.source.explanation': 'Historical record only. Current products, underlay and quantities are shown in Scope & price and Products & pricing. Your edits do not change this original saved quote.',
  'proposal.source.multiple': 'More than one saved quote or revision is recorded. These are separate records, not a combined order.',
  'proposal.source.empty': 'The saved quote has no scope or priced lines. No work or price has been invented.',
  'proposal.source.included': 'Included / no separate charge',
  'proposal.source.totals': 'Subtotal {subtotal} · GST {gst} · Total {total}',

  // ── shared ────────────────────────────────────────────────────────────────
  'job.address.add': 'Add address',
  'job.address.label': 'Job site address',
  'job.address.suggestions': 'Suggested addresses',
  'job.address.failed': 'Address was not saved. Try again.',
  'job.address.manual': 'Suggestions are unavailable. You can still type and save the address.',
  'address.attribution': 'Address data © Geoscape Australia',
  'address.resolving': 'Reading the selected address…',
  'property.show': 'Property facts',
  'property.loading': 'Reading the property record…',
  'property.noMatch': 'No property record matched this address.',
  'property.unavailable': 'Property facts are unavailable right now.',
  'property.notInPlan': 'Property facts need the Geoscape Buildings and Land Parcels products, which are not in the current plan.',
  'property.matched': 'Property record:',
  'property.matchedExact': 'Property record (address and suburb matched):',
  'property.matchedNearest': 'Nearest property record — check it is the right house:',
  'property.dwelling': 'Building',
  'property.building': 'Building',
  'property.noBuilding': 'No building footprint recorded',
  'property.strata': 'strata / several addresses',
  'property.floors': 'Floors',
  'property.floorArea': 'Floor area',
  'property.footprint': 'Footprint',
  'property.lotArea': 'Lot area',
  'property.lotPlan': 'Lot / plan',
  'property.zoning': 'Zoning',
  'property.roof': 'Roof',
  'property.pool': 'Pool',
  'property.solar': 'Solar panels',
  'property.addresses': 'Addresses in building',
  'property.yes': 'Yes',
  'property.no': 'No',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.remove': 'Remove',
  'common.add': 'Add',
  'common.close': 'Close',
  'common.none': 'None',
  'common.notRecorded': 'Not recorded',
  'common.quantity': 'Quantity',
  'common.subtotal': 'Subtotal',
  'common.gst': 'GST',
  'common.total': 'Total',
  'common.material': 'Material',
  'common.labour': 'Labour',
  'common.sell': 'Sell',
  'common.from': 'from {amount}',
  'common.toBeConfirmed': 'To be confirmed',

  // ── the rail ──────────────────────────────────────────────────────────────
  'rail.sections': 'Sections',
  'rail.group.work': 'Work',
  'rail.group.grow': 'Grow',
  'rail.group.supply': 'Supply',
  'rail.group.system': 'System',
  'rail.today': 'Operations',
  'rail.serviceLibrary': 'Service library',
  'rail.website': 'Website',
  'rail.emma': 'EmmA Desk',
  'rail.leads': 'Lead inbox',
  'rail.pipeline': 'Pipeline',
  'rail.catalogue': 'Catalogue',
  'rail.priceMyFloor': 'Price my floor',
  'rail.followUps': 'Follow-ups',
  'rail.contacts': 'Contacts',
  'rail.accounts': 'Accounts',
  'rail.jobs': 'Jobs',
  'rail.documents': 'Documents',
  'rail.calendar': 'Calendar',
  'rail.marketing': 'Marketing',
  'rail.members': 'Members',
  "rail.hipages": "HiPages offers",
  "hipage.title": "HiPages offers",
  "hipage.lede": "EmmA's call on every offer the watcher saw. Advice only — auto-accept is off, so nothing is accepted for you.",
  "hipage.autoAcceptOff": "Auto-accept off",
  "hipage.window": "Last {days} days",
  "hipage.offers": "Offers",
  "hipage.emmaYes": "EmmA: Yes",
  "hipage.emmaNo": "EmmA: No",
  "hipage.emmaUnsure": "EmmA: Unsure",
  "hipage.emmaPending": "Awaiting EmmA",
  "hipage.potentialYes": "Potential revenue on EmmA Yes",
  "hipage.plusGst": "{amount} + GST",
  "hipage.filter.all": "All",
  "hipage.filter.yes": "EmmA Yes",
  "hipage.filter.no": "EmmA No",
  "hipage.filter.pending": "Awaiting EmmA",
  "hipage.credits": "{n} credits",
  "hipage.potential": "Potential",
  "hipage.detail.brief": "Job brief",
  "hipage.detail.timing": "Timing",
  "hipage.detail.action": "HiPages action",
  "hipage.detail.emmaReason": "EmmA's reason",
  "hipage.detail.confidence": "{pct}% confident",
  "hipage.detail.yourRuling": "Your ruling",
  "hipage.detail.noRuling": "Not ruled",
  "hipage.reaction.median": "EmmA's median reaction",
  "hipage.reaction.title": "EmmA's reaction time",
  "hipage.reaction.seconds": "{n} s",
  "hipage.reaction.minutes": "{n} min",
  "hipage.reaction.hours": "{n} h",
  "hipage.reaction.days": "{n} days",
  "hipage.reaction.caughtUp": "caught up later ({time})",
  "hipage.reaction.lateCount": "{n} caught up later, not counted",
  "hipage.ruling.yes": "Take",
  "hipage.ruling.no": "Skip",
  "hipage.ruling.unsure": "Unsure",
  "hipage.detail.noReason": "EmmA has not judged this offer yet.",
  "hipage.basis.trade": "Median of {n} past HiPages jobs in this trade, EX GST. What a job like this has been worth — not a quote.",
  "hipage.basis.all": "Too few past jobs in this trade — median of all {n} past HiPages jobs, EX GST. Not a quote.",
  "hipage.empty": "No offers in this window.",
  "hipage.noBrief": "No brief recorded.",
  "hipage.loading": "Loading offers…",
  "hipage.loadError": "HiPages offers could not be loaded. Refresh before relying on this page.",
  'rail.suppliers': 'Suppliers & stock',
  'rail.inventory': 'Inventory',
  'rail.installers': 'Installers',
  'rail.mail': 'Mail',
  'rail.communications': 'Communications',
  'communications.kicker': 'Office · EmmA prepares, you release',
  'communications.title': 'Communications Office',
  'communications.lede': 'Customer, supplier and installer conversations with their Job, Proposal, PO and Installation context.',
  'communications.loading': 'Loading conversations…',
  'communications.refresh': 'Refresh',
  'communications.filter.all': 'All',
  'communications.filter.customer': 'Customers',
  'communications.filter.supplier': 'Suppliers',
  'communications.filter.installer': 'Installer teams',
  'communications.conversations': 'Conversations',
  'communications.empty': 'No conversations in this view.',
  'communications.choose': 'Choose a conversation.',
  'communications.official': 'Official',
  'communications.supporting': 'Supporting',
  'communications.review': 'Send review',
  'communications.noDraft': 'There is no prepared draft in this conversation.',
  'communications.askDraft': 'Ask EmmA to draft',
  'communications.askRevise': 'Ask EmmA to revise',
  'communications.to': 'Recipient',
  'communications.channel': 'Channel',
  'communications.subject': 'Subject',
  'communications.approveSend': 'Approve & send',
  'communications.requestApproval': 'Send this message',
  'communications.requesting': 'Sending…',
  'communications.sending': 'Queuing…',
  'communications.queued': 'Approved and queued for delivery.',
  'communications.notQueued': 'The message was not queued. Review its current state before trying again.',
  'mail.showMore': 'Show {n} more',
  'mail.fileIt': 'File it',
  'mail.armAndFile': 'Arm rule & file',
  'mail.fileWhere': 'File to',
  'mail.done': 'Done',
  'mail.putBack': 'Put back',
  'mail.handled': 'dealt with',
  'mail.askDraft': 'Copy for chat',
  'mail.copied': 'Copied ✓',
  'mail.draftLead': 'Please draft a reply to this email:',
  'mail.copyFailed': 'Could not copy — open the chat and paste it yourself.',
  'mail.kicker': 'Mail · EmmA read it for you',
  'mail.title': 'What came in, and what she did with it',
  'mail.lede': 'Every mailbox, read and tagged. This shows what needs you, what needs a rule, and what has already been filed.',
  'mail.loading': 'Reading the mail she has already read…',
  'mail.unavailable': 'Mail could not be read: {message}',
  'mail.vocabularyUnavailable': 'Her tag vocabulary could not be read, so the rules panel is empty rather than wrong. The mail below is still hers.',
  'mail.verdictNeed': '{total} emails came in. This many need a person.',
  'mail.verdictClear': 'Nothing needs you. Everything else filed itself.',
  'mail.allBoxes': 'All mailboxes',
  'mail.needsYou': 'Needs you',
  'mail.waitingRule': 'Waiting on your rule',
  'mail.noneNeed': 'Nothing needs you right now.',
  'mail.noneWaiting': 'Every tag has a rule — nothing is waiting on you.',
  'mail.job': 'job',
  'mail.tag.supplier_order_confirmation': 'supplier order',
  'mail.tag.supplier_invoice': 'supplier invoice',
  'mail.tag.supplier_quote': 'supplier quote',
  'mail.tag.supplier_product_update': 'supplier update',
  'mail.tag.customer_job_lead': 'customer lead',
  'mail.tag.action_required_admin': 'needs a person',
  'mail.tag.marketing_newsletter': 'newsletter',
  'mail.tag.payment_confirmation': 'payment',
  'mail.tag.needs_attention': 'needs attention',
  'mail.filedByRule': 'filed by rule',
  'mail.needsRule': 'no rule yet — please decide',
  'mail.needsYourCall': 'needs your call',
  'mail.nothingToDo': 'read, nothing to do',
  'mail.armRule': 'Arm rule',
  'mail.why': 'Why it is here',
  'mail.whyNeeds': 'She tagged it as needing a person — money, a date, a question or a commitment is waiting.',
  'mail.whyFiled': 'An armed rule covers this tag and this mail arrived after you armed it.',
  'mail.whyWaiting': 'She tagged it, but no armed rule covers the tag — so nothing has moved.',
  'mail.readBy': 'Read by',
  'mail.readByUnknown': 'not recorded',
  'mail.rules': 'Cleanup rules',
  'mail.rulesMeta': 'yours to arm',
  'mail.noFolder': 'no folder set',
  'mail.noRules': 'No rule exists yet. She tags; a rule is what files.',
  'mail.vocabulary': 'Her vocabulary',
  'mail.tagCount': '{n} tags',
  'mail.proposed': 'She proposed {n} new tag — “{tag}”. Not applied to anything yet.',
  'mail.filedLine': '{n} filed without asking',
  'mail.filedWhy': 'nothing there needed a person',
  'rail.purchaseOrders': 'Orders',
  'rail.buildTracker': 'Build tracker',
  'rail.settings': 'Settings',

  // ── the top bar ───────────────────────────────────────────────────────────
  'topbar.newLead': '+ New lead',
  'topbar.casesWaiting': '{count} EmmA cases waiting',
  'topbar.search': 'Search people, jobs, suburbs, refs…',
  'topbar.mock': 'mock',
  'topbar.live': 'Live',
  'topbar.testSet': 'Test set…',
  'topbar.production': 'Production',
  'topbar.language': 'Language',
  'topbar.languageSwitch': 'Switch language',
  'topbar.textSize': 'Text size',
  // Bug / Improvement — the header button on every page (Jacky, 12 October 2026).
  'feedback.button': 'Bug / Improve',
  'feedback.title': 'Bug or improvement',
  'feedback.intro': 'Say what is wrong or what you want, in your own words. The page you are on is recorded with it.',
  'feedback.kind.bug': 'Bug',
  'feedback.kind.improvement': 'Improvement',
  'feedback.describe': 'What needs fixing or improving',
  'feedback.placeholder.bug': 'What happened, and what should have happened instead…',
  'feedback.placeholder.improvement': 'What would make this page better…',
  'feedback.point': 'Point at it on the page',
  'feedback.pointing': 'Click the part of the page you mean. Esc to stop.',
  'feedback.pointed': 'Pointed at',
  'feedback.remove': 'Remove',
  'feedback.page': 'Page',
  'feedback.send': 'Record it',
  'feedback.sending': 'Recording…',
  'feedback.recorded': 'Recorded. EmmA has it.',
  'feedback.pending': 'Recorded for review in the EmmA desk.',
  'feedback.failed': 'Not recorded — {reason}',
  'feedback.recent': 'Recent reports',
  'feedback.none': 'No reports yet.',
  'feedback.loadFailed': 'Reports could not be loaded.',
  'feedback.status.open': 'Open',
  'feedback.status.acknowledged': 'Seen',
  'feedback.status.done': 'Done',
  'feedback.status.declined': 'Declined',
  'webTraffic.title': 'Website traffic',
  'webTraffic.meta': 'anonymous page counts · no cookies, no personal data',
  'webTraffic.range.7': '7 days',
  'webTraffic.range.30': '30 days',
  'webTraffic.range.90': '90 days',
  'webTraffic.pageViews': 'Page views',
  'webTraffic.perDay': '{count} a day on average',
  'webTraffic.daily': 'Daily page views',
  'webTraffic.topPages': 'Top pages',
  'webTraffic.topReferrers': 'Where visitors came from',
  'webTraffic.direct': 'Direct or unknown',
  'webTraffic.devices': 'Devices',
  'webTraffic.device.phone': 'Phone',
  'webTraffic.device.tablet': 'Tablet',
  'webTraffic.device.desktop': 'Desktop',
  'webTraffic.device.unknown': 'Unknown',
  'webTraffic.actions': 'Website actions',
  'webTraffic.event.book_submitted': 'Measure bookings submitted',
  'webTraffic.event.self_quote_carried': 'Self-quotes carried to booking',
  'webTraffic.event.member_link_requested': 'Member sign-in links requested',
  'webTraffic.none': 'No page views counted in this range yet.',
  'webTraffic.loading': 'Reading the counter…',
  'webTraffic.failed': 'Website traffic could not be read: {reason}',
  'webTraffic.footnote': 'One count per page load. Visitors are not identified, so these are page totals, not people. Known bots are not counted. Ask EmmA: “how many visits to the hybrid page this week”.',
  'textSize.compact': 'Compact',
  'textSize.standard': 'Standard',
  'textSize.comfortable': 'Comfortable',
  'textSize.large': 'Large',
  'textSize.xlarge': 'Extra large',
  'attention.explanation': 'Approvals, leads and overdue tasks behind the attention badge.',
  'attention.loading': 'Loading attention items…',
  'attention.failed': 'Some attention items could not be loaded. This list may be incomplete.',
  'attention.empty': 'Nothing needs immediate attention.',
  'attention.leads': 'Leads needing attention',
  'attention.openLead': 'Open lead',
  'attention.overdue': 'Overdue follow-ups',
  'attention.openFollowUp': 'Open follow-up',
  'attention.allFollowUps': 'View all follow-ups',
  'attention.materials': 'Materials needing attention',
  'attention.discard': 'Discard',
  'attention.discardReason': 'Will not be sent: discarded from Immediate attention',
  'topbar.immediateAttentionLabel': 'Immediate attention',
  'topbar.immediateAttention': 'Immediate attention · {count} items',

  // ── the Proposal builder ──────────────────────────────────────────────────
  'proposal.kicker': 'Pipeline · Proposal',
  'proposal.lede': 'Customer brief → site assessment → scope. Pricing comes next.',
  'proposal.loading': 'Loading Proposal…',
  'proposal.unavailable': 'Proposal unavailable',
  'proposal.pipeline': 'Pipeline',
  'proposal.unsaved': 'Unsaved changes',
  'proposal.savedVersion': 'Saved version {version}',
  'proposal.customerVersion': 'Customer version {version}',
  'proposal.privateDraft': 'Private draft',
  'proposal.saveDraft': 'Save draft',
  'proposal.layoutPreview': 'Layout preview · saving and customer links are disabled.',
  'proposal.extraWorkSummary': 'Extra work · {count} standalone items',
  // Removal by existing floor (16 Oct 2026): one floor for the site, only the rooms that differ.
  'removal.title': 'Existing floor',
  'removal.hint': 'Most sites have one floor throughout. Set it once, then add only the rooms that are different.',
  'removal.site': 'Existing floor across the site',
  'removal.notRecorded': 'Not recorded',
  'removal.none': 'None (nothing to lift)',
  'removal.exceptions': 'Rooms with a different existing floor',
  'removal.sameAsSite': 'Same as the site ({floor})',
  'removal.remove': 'Remove',
  'removal.addRoom': '+ Add a room that is different',
  'removal.pickRoom': 'Choose a room',
  'removal.pickFloor': 'Choose its existing floor',
  'removal.summary': 'Removal lines: {groups}',
  'removal.noLine': 'Add "Remove & dispose" in the extra work below; its lines will follow these floors.',
  'removal.unrecorded': 'Some rooms have no existing floor recorded and are left out of removal.',
  'proposal.materialAudit': 'Saved material calculation · audit detail',
  'proposal.siteAddress': 'Work site address',
  'proposal.siteAddressHint': 'Start typing, or choose an address already on file for this customer: their own, this job site, or a place we have been to for them.',
  'proposal.tab.proposal': 'Proposal',
  'proposal.tab.brief': 'Customer brief',
  'proposal.tab.assessment': 'Site assessment',
  'proposal.tab.products': 'Products & pricing',
  'proposal.tab.share': 'Share',
  'proposal.acceptedEyebrow': 'Accepted v{version} · {when} · {who}',
  'proposal.acceptedCustomer': 'the customer',
  'proposal.acceptedCost': 'Artemis cost total',
  'proposal.acceptedMargin': 'Gross margin {rate}% (on the price)',
  'proposal.acceptedTotal': 'Total ex GST',
  'proposal.acceptedTitle': 'Accepted proposal',
  'proposal.acceptedAmended': 'Changed after acceptance on {when}: {what}',
  'proposal.acceptedOpen': 'Open the accepted copy',
  'proposal.acceptedJob': 'Job {ref}',
  'proposal.acceptedNote': 'This is the agreement: the customer accepted these lines and this total. The working draft below does not change it. A colour change at the same price is made through EmmA; anything else is a new version for the customer to accept.',
  'proposal.acceptedDraftNote': 'Working draft below — not the agreement.',
  'proposal.overview.blurb': 'The brief and site assessment build this overview. Only add wording for a special request.',
  'proposal.overview.noSelection': 'No product or price has been selected yet.',
  'proposal.overview.selection': '{count} product selected — the figures live under Products & pricing.',
  'proposal.overview.selectionPlural': '{count} products selected — the figures live under Products & pricing.',
  'proposal.customWording': 'Special request · edit Proposal wording',
  'proposal.scope.brief': 'Your brief',
  'proposal.scope.included': 'Proposed scope',
  'proposal.scope.excluded': 'Not included',
  'proposal.scope.customer': 'Customer arrangements',
  'proposal.scope.assumptions': 'Assumptions to verify',
  'proposal.scope.empty': 'Complete the brief or assessment to build this section.',
  'proposal.customPreserved': 'Custom wording is preserved. Turn off special-request editing to use the latest brief and assessment again.',
  'proposal.overview.tail': 'Products, options, LM pricing, Quote PDF and acceptance follow the pricing discussion. The sales budget and private notes stay private.',
  'proposal.brief.blurb': 'Optional · private to sales. Unknown information can stay blank.',
  'proposal.share.title': 'Release a customer copy',
  'proposal.share.blurb': 'The customer sees the introduction, site assessment, scope and — once products are selected — the priced options. The sales brief, the site costs and every margin stay private. The released copy carries a whitelist: sell prices only, no cost, no supplier, no margin, no $0 lines.',
  'proposal.share.saveFirst': 'Save your draft, then release this version. Generating a link sends nothing and does not create a Job.',
  'proposal.share.ready': 'Priced offer ready · {count} product line',
  'proposal.share.readyPlural': 'Priced offer ready · {count} product lines',
  'proposal.share.blocked': 'This offer cannot be released yet',
  'proposal.share.totalExGst': '${amount} + GST',
  'proposal.share.fromExGst': 'from ${amount} + GST',
  'proposal.share.totalTbc': 'Total to be confirmed',
  'proposal.share.publish': 'Create private Proposal link',
  'proposal.share.republish': 'Release version at same private link',
  'proposal.share.originalLink': 'The existing customer link remains active. Paste your original link here to preview or resend it from this browser.',
  'proposal.share.restoreLink': 'Use original link',
  'proposal.share.revoke': 'Revoke customer access',
  // Open-book release (15 Oct 2026): costs from the catalogue, the margin once at the total.
  'proposal.share.openBook': 'Release open-book (cost + 20% GM)',
  'proposal.share.copied': 'Private link copied. Nothing sent.',
  'proposal.share.copyHint': 'Select and copy the link above.',
  'proposal.share.copy': 'Copy link',
  'proposal.share.view': 'View customer copy',
  'proposal.share.printPdf': 'Print / save PDF',
  'proposal.share.pdfDrawing': 'Drawing the PDF…',
  'proposal.pdfBlocked': 'The PDF was drawn but this browser blocked the window; allow pop-ups for the CRM and try again.',
  'proposal.share.pdfFailed': 'The PDF could not be drawn — {reason}',
  'proposal.share.pdfBlocked': 'The PDF was drawn but could not be opened here; open {url} in a browser.',
  'commercial.printDesktopHint': 'Printing is not available in the desktop app. Save the draft, then use Print / save PDF on the Share step.',
  'proposal.share.after': 'Quote PDF and acceptance follow the next discussion. Existing email/SMS tools can carry the copied link after you review the message.',

  // ── 2.1 product selection ─────────────────────────────────────────────────
  'pricing.selection.title': 'What is being laid',
  'pricing.selection.none': 'Nothing selected yet',
  'pricing.selection.count': '{count} selection',
  'pricing.selection.countPlural': '{count} selections',
  'pricing.selection.empty': 'Choose a product from the catalogue on the right. Selecting a product and pricing it are the same step — an unpriced Proposal cannot be sent.',
  'catalogue.documents': 'Product documents',
  'catalogue.specialOrder': 'Special order',
  'catalogue.openPdf': 'Open {title} PDF',
  // Catalogue specifications by family (25 Oct 2026): every field the website's spec tables record, gaps shown.
  'catalogue.spec.notRecorded': 'Not recorded',
  'catalogue.spec.missing': '{count} not recorded',
  'catalogue.spec.yes': 'Yes',
  'catalogue.spec.no': 'No',
  'catalogue.spec.thickness_mm': 'Thickness',
  'catalogue.spec.wear_layer_mm': 'Wear layer',
  'catalogue.spec.length_mm': 'Board length',
  'catalogue.spec.width_mm': 'Board width',
  'catalogue.spec.install_methods': 'Installation',
  'catalogue.spec.install_method': 'Installation',
  'catalogue.spec.subfloor_flatness': 'Subfloor flatness',
  'catalogue.spec.pattern': 'Pattern',
  'catalogue.spec.warranty_residential_yrs': 'Residential warranty',
  'catalogue.spec.warranty_commercial_yrs': 'Commercial warranty',
  'catalogue.spec.species': 'Species',
  'catalogue.spec.grade': 'Grade',
  'catalogue.spec.finish': 'Finish',
  'catalogue.spec.edge_profile': 'Edge profile',
  'catalogue.spec.slip_rating': 'Slip rating',
  'catalogue.spec.roll_width_m': 'Roll width',
  'catalogue.spec.antimicrobial': 'Antimicrobial',
  'catalogue.spec.fibre': 'Fibre',
  'catalogue.spec.fibre_branded_name': 'Branded fibre',
  'catalogue.spec.pile_type': 'Pile type',
  'catalogue.spec.pile_weight_gsm': 'Pile weight',
  'catalogue.spec.total_weight_gsm': 'Total weight',
  'catalogue.spec.backing_type': 'Backing',
  'catalogue.spec.stain_treatment': 'Stain treatment',
  'catalogue.spec.wear_rating': 'Wear rating',
  'catalogue.spec.tile_size_cm': 'Tile size',
  'catalogue.spec.light_reflectance': 'Light reflectance',
  'catalogue.spec.ac_rating': 'AC rating',
  'catalogue.spec.water_resistance': 'Water resistance',
  'catalogue.spec.underlay_attached': 'Underlay attached',
  'catalogue.spec.welded_seams': 'Welded seams',
  'catalogue.spec.years': '{n} years',
  'pricing.catalogue.title': 'Catalogue',
  'pricing.catalogue.costNote': 'Cost is shown to staff only. Never customer-facing.',
  'pricing.catalogue.find': 'Find a product',
  'pricing.catalogue.findPlaceholder': 'name, supplier, type',
  'pricing.catalogue.reading': 'Reading the catalogue…',
  'pricing.catalogue.count': '{shown} of {total} shown · {priced} can be priced',
  'pricing.catalogue.colours': '{count} colour the customer can choose',
  'pricing.catalogue.coloursPlural': '{count} colours the customer can choose',
  'pricing.catalogue.perUnit': 'per {unit}',
  'pricing.catalogue.manualAssessment': 'Manual assessment',
  'pricing.catalogue.noCost': 'No cost yet',
  'pricing.catalogue.pricePerJob': 'price per job',
  'pricing.catalogue.codBasis': 'COD basis',
  'pricing.services.title': 'Removal, disposal and site work',
  'pricing.services.savedLegacy': 'Saved legacy pricing services',
  'pricing.services.blurb': 'Rates come from configuration, not from this screen.',
  'pricing.services.empty': 'No services on this Proposal yet.',
  'pricing.services.addRate': 'Add a rate',
  'pricing.services.choose': 'Choose…',
  'pricing.services.noSellPrice': 'no selling price',
  'pricing.services.jobMinimum': 'Job minimum',
  'pricing.services.notApplied': 'Not applied',
  'pricing.services.customerRemoves': 'Customer removes it',
  'pricing.services.sellPer': '{amount}/{unit}',
  'pricing.services.accessTitle': 'Site access',
  'pricing.services.access': 'Access',
  'pricing.services.house': 'House — ground floor',
  'pricing.services.elevator': 'Lift',
  'pricing.services.stairs': 'Stairs',
  'pricing.services.above3': 'Above three flights',
  'pricing.services.weight': 'Product weight · kg',
  'pricing.services.parking': 'Unload, then drive to park',
  'pricing.line.missingProduct': 'Product no longer in the catalogue',
  'pricing.line.missingProductHint': 'Remove this line and choose a current product',
  'pricing.line.colour': 'Colour',
  'pricing.colour.undecided': 'Customer not yet decided',
  // Special-order colours (26 Oct 2026): builder-only / on-request colours sit in their own group, never by default.
  'pricing.colour.catalogueGroup': 'Catalogue colours',
  'pricing.colour.specialOrderGroup': 'Special order — builder range, not on the website',
  'pricing.line.specialOrderNote': 'Special-order colour: not on the website or the standard price list. Confirm supply and price with the supplier before release.',
  'pricing.line.basis': 'Basis',
  'pricing.line.supplyInstall': 'Supply and install',
  'pricing.line.supply': 'Supply only',
  'pricing.line.install': 'Install only',
  'pricing.line.rangeNotice': 'The offer carries a range until a colour is chosen.',
  'pricing.line.noColours': 'No customer colours recorded; this prices from the cost basis.',
  'pricing.line.onThisLine': 'On this line: {colour}',
  'pricing.line.rooms': 'Rooms',
  'pricing.line.roomsHint': '— the area comes from the assessment, never typed twice',
  'pricing.line.noRooms': 'No rooms recorded in the site assessment yet.',
  'pricing.line.allRooms': 'All rooms',
  'pricing.line.comesWith': 'Comes with this product — tick to include',
  'pricing.line.waste': '{measured} m² measured + {percent}% for cutting and waste = {charged} m² priced. The final charge is by actual measurement.',
  'pricing.line.areaBreakdown': 'Floor area and wastage calculation',
  'pricing.line.netMeasured': 'Flooring area after exclusions',
  'pricing.line.wasteAllowance': 'Cutting and wastage · {percent}%',
  'pricing.line.pricedArea': 'Area to price',
  'pricing.line.quantityDiffers': 'Saved quantity {actual} m² differs from the current calculated {expected} m². Update the product selection before release.',
  'customer.area.drawn': 'Floor drawn, room by room',
  'customer.area.cutouts': 'Fixed fixtures where flooring stops',
  'customer.area.nested': 'Rooms measured separately',
  'customer.area.frames': 'The floor inside the door openings',
  'customer.area.robes': 'Wardrobes — {area} m² measured, provided at 80%',
  'customer.area.measured': 'Flooring area after exclusions',
  'customer.area.wastage': 'Cutting and wastage allowance · {percent}%',
  'customer.area.charged': 'Area charged',
  'customer.price.quantityRate': '{quantity} {unit} × {rate} per {unit}',
  'customer.area.quotedQuantity': 'Quoted product quantity',
  'customer.area.chargedBreakdown': 'Area charged — measured {measured} m² + wastage {waste} m²',
  'customer.proposal.historyMissing': 'The accepted scope and price are shown below. The earlier drawing and document wording were not saved in version history.',
  'customer.carpet.reviewPending': 'The carpet cut plan is awaiting final site review. Artemis will issue a corrected Proposal before acceptance.',
  'customer.carpet.diagramMissing': '{floor}: physical cut geometry has not been saved with this Proposal. The cut lengths below are a schedule, not a diagram.',
  'customer.carpet.reuse': 'Offcut reserved for stair templates',
  'customer.carpet.unallocated': 'Unallocated; waste not yet verified',
  'customer.carpet.included': 'Included',
  // Open-book offer (15 Oct 2026): the items behind a cost line, and the one margin line at the total.
  'customer.openBook.items': 'Cost items',
  'customer.openBook.item': 'Item',
  'customer.openBook.qty': 'Qty',
  'customer.openBook.uom': 'UOM',
  'customer.openBook.unitCost': 'Unit cost',
  'customer.openBook.subtotal': 'Subtotal',
  'customer.openBook.costSubtotal': 'Artemis cost — all lines',
  'customer.openBook.margin': 'Gross margin {percent}% (on the price)',
  // The customer Proposal page in the customer's language (15 Oct 2026). Product, supplier and customer data are never translated.
  'customer.doc.title': 'Flooring Proposal',
  'customer.doc.brandLine': 'Artemis Flooring · Sydney',
  'customer.doc.preparedFor': 'Prepared for',
  'customer.doc.you': 'you',
  'customer.doc.toBeConfirmed': 'To be confirmed',
  'customer.doc.workIncluded': 'Work included in this price',
  'customer.doc.floorProposed': 'The floor we are proposing',
  'customer.doc.perUnit': '{rate} per {unit}',
  'customer.doc.carpetCharged': 'Carpet quantity charged:',
  'customer.doc.carpetChargedCalc': '{lm} whole LM × {width} m full roll width =',
  'customer.doc.carpetCuts': '{cuts} physical cuts total {total} m, including {allowance} m fitting allowance ({parts} m). Whole LM rounding adds {rounding} m.',
  'customer.doc.measuredNote': 'Measured at your place, room by room. The final charge follows the confirmed measurement and agreed material order.',
  'customer.doc.included': 'What is included',
  'customer.doc.notIncluded': 'Not included',
  'customer.doc.nothingExcluded': 'Nothing beyond the scope above is excluded in writing; anything we find on the day is shown to you before it is done.',
  'customer.doc.areasTitle': 'The areas we are laying — marked on your plan',
  'customer.doc.legendFloor': 'Flooring marked on the plan',
  'customer.doc.legendUChannel': 'U-channel trim — colour matched',
  'customer.doc.legendScotia': 'Scotia — colour matched',
  'customer.doc.legendSkirting': 'Skirting',
  'customer.doc.legendCarpetTrim': 'Measured carpet trim',
  'customer.doc.legendNotIn': 'Not in this proposal',
  'customer.doc.carpetLayoutTitle': 'Carpet layout and material use',
  'customer.doc.carpetLayoutIntro': 'One pile direction on each connected floor. Each cut length includes its own fitting allowance; the order is rounded up once to whole LM.',
  'customer.doc.cutA': 'Cut 1 / 3',
  'customer.doc.cutB': 'Cut 2 / 4',
  'customer.doc.carpetCut': '{label}: {length} m × {width} m roll; includes {allowance} m allowance',
  'customer.doc.purchasedCarpet': 'Purchased carpet',
  'customer.doc.flatFloorUsed': 'Flat floor used',
  'customer.doc.awaitingArea': 'Awaiting measured area',
  'customer.doc.stairsFromOffcuts': 'Stairs used from offcuts',
  'customer.doc.awaitingStairs': 'Awaiting checked stair templates',
  'customer.doc.remainingWaste': 'Remaining waste',
  'customer.doc.wasteAfterStairs': 'Calculated after stair templates are checked',
  'customer.doc.options': 'Your options',
  'customer.doc.chooseOne': 'Please choose on the highlighted option below — or tell us "not this time". Nothing is submitted until then.',
  'customer.doc.chooseMany': 'Please choose on the {count} highlighted options below — or tell us "not this time". Nothing is submitted until then.',
  'customer.doc.optional': 'optional',
  'customer.doc.chooseBadge': 'choose one',
  'customer.doc.notThisTime': 'Not this time',
  'customer.doc.previewMoves': 'This is how the price moves as they choose.',
  'customer.doc.totalUpdates': 'Your total at the top updates as you choose. Nothing is charged until you accept.',
  'customer.doc.ifNeeded': 'If needed on the day',
  'customer.doc.rateToConfirm': 'rate to confirm',
  'customer.doc.variationAbout': ' (about {amount} at {qty} {unit})',
  'customer.doc.variationLine': '{label} — {rate}{about}, approved by you first and only charged if we use it.',
  'customer.doc.howTitle': 'How the work runs',
  'customer.doc.stepsUnconfirmed': 'Installation steps have not been confirmed in this saved Proposal. Review the site conditions and agreed scope before work is arranged.',
  'customer.doc.step1': 'Remove the existing floor covering, room by room.',
  'customer.doc.step2': 'Check the subfloor and tell you what we find.',
  'customer.doc.step3': 'Pass go — nothing is laid until you approve what we found.',
  'customer.doc.step4': 'Moisture barrier where the floor needs one.',
  'customer.doc.step5': 'Underlay, then the floor itself.',
  'customer.doc.step6': 'Scotia and trims, colour matched, then we clean up.',
  'customer.doc.price': 'Your price',
  'customer.doc.carpetPurchasedRow': 'Carpet purchased — {lm} whole LM × {width} m roll width',
  'customer.doc.totalExGst': 'Total ex GST',
  'customer.doc.deposit': '50% deposit required before material order.',
  'customer.doc.footer': 'Artemis Flooring · Jacky Shao · 0432 399 295',
  'customer.doc.viewAria': 'Profile image view',
  'customer.doc.viewShape': 'Shape',
  'customer.doc.viewProduct': 'Product',
  'customer.doc.viewInstalled': 'Installed',
  'customer.doc.illustrativeDrawn': 'Illustrative installed look — drawn',
  'customer.doc.illustrative': 'Illustrative installed look',
  'customer.doc.chooseHeight': 'Choose height',
  'customer.doc.heightIncluded': 'Included in your current Proposal',
  'customer.doc.heightDelta': '{perLm}/LM · {total} + GST for this Proposal',
  'customer.doc.priceBeforeOrder': 'Price confirmed before ordering',
  'customer.doc.optionalLater': 'Optional now — Artemis can confirm this with you after the Proposal, during the Job.',
  'customer.page.signInFailed': 'Sign-in could not finish. Your private link still works; try signing in again.',
  'customer.page.usePrivateLink': 'Use your private link to accept.',
  'customer.page.acceptFailed': 'Acceptance was not completed. Please try again.',
  'customer.page.requestFailed': 'Could not complete this request',
  'customer.page.saveFailed': 'Saving was not completed. Please try again.',
  'customer.page.saved': 'Saved to your account. This does not accept the Proposal.',
  'customer.page.nameRequired': 'Please enter your full name.',
  'customer.page.mobileInvalid': 'Enter an Australian mobile or an international number starting with +.',
  'customer.page.emailSent': 'Check your email for the sign-in link. Open it in this browser to return here. If it contains a code, enter it below.',
  'customer.page.codeSent': 'Enter the code sent to {destination}.',
  'customer.page.signedIn': 'Signed in. Save this Proposal below.',
  'customer.page.unavailable': 'Proposal unavailable',
  'customer.page.unavailableHint': 'Use your original private link, or ask Artemis for a current copy.',
  'customer.page.opening': 'Opening your Proposal…',
  'customer.page.print': 'Print or save PDF',
  'customer.page.latestStatus': 'Latest status',
  'customer.page.jobAccepted': 'Job accepted',
  'customer.page.choicesLocked': 'Your choices are now the agreed scope and are shown as accepted. To change anything — a colour too — ask EmmA or call Artemis and we will update it with you.',
  'customer.page.jobRef': 'Job reference:',
  'customer.page.schedule': 'Installation schedule',
  'customer.page.datePending': 'Your installation date is pending. Artemis will contact you when it is ready to schedule.',
  'customer.page.emmaTitle': 'Questions about your Proposal? Ask EmmA',
  'customer.page.emmaPlaceholder': 'e.g. Is the underlay included?',
  'customer.page.emmaAttach': 'Add a photo',
  'customer.page.emmaPastRender': 'A picture EmmA made for you',
  'customer.page.emmaAttachHint': 'Send EmmA a photo of a room (or paste one) and ask to see it with a floor from this Proposal. The picture takes a few minutes and appears here.',
  'customer.page.emmaRemovePhoto': 'Remove photo',
  'customer.page.emmaExpand': 'Open',
  'customer.page.emmaCollapse': 'Close',
  'customer.page.emmaDemoYou': 'You send a photo like this',
  'customer.page.emmaDemoHer': 'EmmA sends this back',
  'customer.page.emmaDemoSay': 'You say: “Show the Living room in White Oak.” EmmA: “On its way — about three minutes, it will appear here.”',
  'customer.page.emmaWriting': 'EmmA is writing…',
  'customer.page.emmaSend': 'Ask',
  'customer.page.emmaNote': 'EmmA is an AI assistant. Prices are as written in this Proposal; anything she cannot answer goes to the Artemis office.',
  'customer.page.emmaRendering': 'EmmA is making the picture of {room} — about {minutes} minutes. It appears here when ready.',
  'customer.page.emmaRenderFailed': 'The picture did not come through. Ask EmmA to try again.',
  // The customer's plan page (17 Oct 2026): the plan, the tiles EmmA lays out on it.
  'customer.page.planLink': 'Your floor plan',
  'customer.plan.title': 'Your floor plan',
  'customer.plan.back': 'Back to your Proposal',
  'customer.plan.saved': 'Drawn from your builder\'s plans · version {save}',
  'customer.plan.backdrop': 'Show the builder\'s drawing',
  'customer.plan.fit': 'Whole floor',
  'customer.plan.zoomIn': 'Zoom in',
  'customer.plan.zoomOut': 'Zoom out',
  'customer.plan.layouts': 'Tile layouts EmmA drew',
  'customer.plan.noLayouts': 'Ask EmmA how many tiles you need — she lays them out on your plan here.',
  'customer.plan.tiles': '{total} tiles: {full} whole, {cut} cut',
  'customer.plan.smallest': 'smallest cut {mm} mm',
  'customer.plan.reused': '{n} cut pieces from offcuts',
  'customer.plan.walls': 'Walls, unfolded',
  'customer.plan.wall': 'Wall {n}: {length} × {height} mm',
  'customer.plan.legendWhole': 'Whole tile',
  'customer.plan.legendCut': 'Cut tile',
  'customer.plan.loading': 'Loading your floor plan…',
  'customer.plan.noPlan': 'Your floor plan is not ready yet. Artemis will let you know.',
  'customer.plan.unavailable': 'This page is not available. Please call us for a fresh link.',
  'customer.plan.emmaTitle': 'Plan your tiles with EmmA',
  'customer.plan.emmaIntro': 'Tell EmmA your tile size and how you want it laid. She works it out on your real floor plan and draws the layout here.',
  'customer.plan.chip1': 'How many tiles for the bathroom floor?',
  'customer.plan.chip2': 'Tile the bathroom walls too',
  'customer.plan.chip3': 'All tiled floors, how many boxes?',
  'customer.plan.placeholder': 'e.g. 800 × 800 tiles, straight lay',
  'customer.plan.note': 'Quantities are worked out on the plan your builder drew. Have your tiler confirm before you order.',
  'customer.plan.chip4': 'How much skirting do I need?',
  'customer.plan.skirtingSummary': '{lm} LM · {lengths} lengths of {stock} mm',
  'customer.plan.cutPlan': 'Skirting cut plan',
  'customer.plan.length': 'Length {n}',
  'customer.plan.left': '{mm} mm left',
  'customer.plan.legendInt': 'Internal corner (mitre)',
  'customer.plan.legendExt': 'External corner (mitre)',
  'customer.plan.legendReturn': 'Returned end',
  'customer.plan.legendDoor': 'Stops at a door',
  // Architrave on the plan page (17 Oct 2026).
  'customer.plan.chip5': 'How much architrave do I need?',
  'customer.plan.architraveSummary': '{lm} LM · {openings} openings · {lengths} lengths of {stock} mm',
  'customer.plan.cutPlanArchitrave': 'Architrave cut plan',
  'customer.plan.legendArchitrave': 'Each door and opening: two legs of {height} mm and a head, mitred at the top corners',
  // The laying view (29 Oct 2026): the room being laid at true tile scale; the customer tries variants and keeps one with EmmA.
  'customer.plan.laying': 'Laying view',
  'customer.plan.layoutsMode': 'EmmA\'s layouts',
  'customer.plan.fitRoom': 'Whole room',
  'customer.plan.trueScale': '{room} at true scale: {tile} mm tiles on your measured outline',
  'customer.plan.tryIt': 'Try it',
  'customer.plan.border': 'Border',
  'customer.plan.borderYes': 'With a border',
  'customer.plan.borderNo': 'No border',
  'customer.plan.borderWidth': 'Border width {mm} mm',
  // Cate's rules, 1 Nov 2026: one border width by the millimetre, black feature strips, nibs passed straight.
  'customer.plan.borderWidthMm': 'Border width in millimetres',
  'customer.plan.borderWidthNote': 'One width all round, so every corner mitre has two equal sides. Dial it by the millimetre to balance the cuts.',
  'customer.plan.stripToggle': 'Black feature strip along the {run} m run: fills the {step} mm step so the border runs straight',
  'customer.plan.stripCount': 'Black feature strip: {lm} LM, {mm} mm wide',
  'customer.plan.nibNote': 'A {mm} mm wall nib is no deeper than the border, so the border passes it straight and nothing wraps it.',
  'customer.plan.legendStrip': 'Black feature strip',
  'customer.plan.borderColour': 'Border colour',
  'customer.plan.colourOrder': 'First tile',
  'customer.plan.pattern': 'Pattern',
  'customer.plan.patternDiagonal': 'Diagonal',
  'customer.plan.patternStraight': 'Straight',
  'customer.plan.datum': 'Start from',
  'customer.plan.datumCentre': 'Centre of the room',
  'customer.plan.datumCorner': 'A corner',
  'customer.plan.edgeFinish': 'Along the walls',
  'customer.plan.scotia': 'Scotia',
  'customer.plan.skirting': 'Skirting',
  'customer.plan.legendGoldLine': 'Gold line (bevelled edge)',
  'customer.plan.legendGrout': 'Joint',
  'customer.plan.legendTrim': 'Transition trim at open edges and doorways',
  'customer.plan.counts': 'What this layout takes',
  'customer.plan.areaLine': '{room}: {area} m² of floor',
  'customer.plan.countLine': '{colour}: {full} whole, {cut} cut',
  'customer.plan.borderCount': 'Border: {pieces} strips of {width} mm, {lm} LM, about {tiles} tiles',
  'customer.plan.trimCount': 'Transition trim: {lm} LM',
  'customer.plan.skirtingCount': '{kind}: {lm} LM along the walls',
  'customer.plan.countsNote': 'Counts are from your measured outline, with no wastage added. EmmA confirms the order quantities when you keep a layout.',
  'customer.plan.keep': 'Keep this and ask EmmA',
  'customer.plan.keepMessage': 'Please keep this layout for the {room}: {pattern}, {order} first, {border}, starting from the {datum}. How many tiles of each colour will we need, and does it meet the laying rules? Set-out: {key}',
  'customer.plan.borderOn': 'a {width} mm border in {colour}',
  'customer.plan.borderOff': 'no border',
  'customer.plan.olderLayouts': '{n} layouts drawn on an earlier version of the plan are not shown.',
  // EmmA's laying run on the plan page (29 Oct 2026, PR 4): the page follows her passes.
  'customer.plan.run.title': 'EmmA\'s laying run',
  'customer.plan.run.started': 'EmmA is laying it out…',
  'customer.plan.run.pass': 'Pass {pass} of {of}',
  'customer.plan.run.drawn': '{total} tiles: {full} whole, {cut} cut · smallest cut {mm} mm',
  'customer.plan.run.ruleOk': 'This pass meets the laying rules.',
  'customer.plan.run.ruleFail': 'Not yet: {list}. EmmA adjusts and goes again.',
  'customer.plan.run.passed': 'Done: this layout meets the laying rules.',
  'customer.plan.run.budget': 'EmmA stopped after {pass} passes and kept the best layout.',
  'customer.plan.run.failed': 'EmmA could not finish this run: {error}',
  'customer.plan.run.see': 'See EmmA\'s layout on the plan',
  'customer.plan.run.busy': 'EmmA is already laying this room out; that run is shown.',
  'customer.plan.run.unavailable': 'EmmA cannot start a run just now. Please try again shortly.',
  'customer.plan.run.noPlan': 'Your floor plan is not saved yet, so EmmA cannot lay it out.',
  'customer.plan.run.picture': 'Ask EmmA for a picture',
  'customer.plan.run.pictureMessage': 'Please show me a picture of the {room} floor laid out like this, with my tiles.',
  'customer.page.drawings': 'Drawings',
  'customer.page.drawingsNote': 'Supplementary to the Proposal: the measured areas above are what is priced.',
  'customer.page.skirtingTitle': 'Skirting for your Job',
  'customer.page.skirtingSupplyInstall': 'Artemis supplies and installs',
  'customer.page.skirtingInstallOnly': 'You supply, Artemis installs',
  'customer.page.confirmed': 'Confirmed:',
  'customer.page.confirmedWith': 'Confirmed with {name}.',
  'customer.page.profileToConfirm': 'Profile and height to confirm before ordering.',
  'customer.page.buyingGuide': 'Optional buying guide',
  'customer.page.guideMeasured': 'measured',
  'customer.page.guideExtra': '({pct}% extra)',
  'customer.page.guideTotal': '{lm} LM total recommended',
  'customer.page.guideNoLabour': 'This does not increase installation labour.',
  'customer.page.guideMissing': 'The approved skirting measurement is still required before a purchase guide can be shown. Artemis will not guess it from room perimeter.',
  'customer.page.happy': 'Happy with it?',
  'customer.page.happyNote': 'Confirming tells us to book the work in. Nothing is charged here, and we will contact you to agree the date.',
  'customer.page.chooseFirstOne': 'Choose on the highlighted option above first — it needs your answer, including "not this time".',
  'customer.page.chooseFirstMany': 'Choose on the highlighted options above first — each one needs your answer, including "not this time".',
  'customer.page.thanksAt': ' at {amount} + GST',
  'customer.page.thanks': 'Thank you — your Proposal is confirmed{amount}. We will be in touch to agree the date.',
  'customer.page.fullName': 'Your full name',
  'customer.page.confirm': 'Confirm this Proposal',
  // Per-room choice on an open-book Proposal (15 Oct 2026). Product names, colours and figures are data, never translated.
  'customer.choose.aria': 'Choose your floor, room by room',
  'customer.choose.floorArea': 'Floor being laid',
  'customer.choose.roomsCount': '{count} rooms',
  'customer.choose.current': 'Current choice',
  'customer.choose.customMix': 'Your own mix',
  'customer.choose.finishing': 'Finishing',
  'customer.choose.skirtingOn': '{size}mm skirting',
  'customer.choose.skirtingOff': 'No skirting',
  'customer.choose.archOn': 'Door architraves',
  'customer.choose.archOff': 'No architraves',
  'customer.choose.disposalArtemis': 'Artemis disposal',
  'customer.choose.disposalSkip': "Builder's skip",
  'customer.choose.presetsTitle': 'Two ways to go',
  'customer.choose.presetChosen': 'Chosen',
  'customer.choose.presetChoose': 'Choose this',
  'customer.choose.planTitle': 'Your plan — tap a room for its area and cost',
  'customer.choose.tapHint': 'Tap a room to see its area, the floor going in and its own cost.',
  'customer.choose.notIn': 'Not in this proposal',
  'customer.choose.typeCarpet': 'Carpet',
  'customer.choose.typeHardFloor': 'Laminate',
  'customer.choose.atCost': 'at cost',
  'customer.choose.rollRun': '{m} m roll run',
  'customer.choose.chosen': 'Chosen · whole job',
  'customer.choose.useAll': 'Use for the whole job',
  'customer.choose.changeBoard': 'Choose another laminate',
  'customer.choose.jobCost': 'whole job · Artemis cost',
  'customer.choose.mainPlank': '{product} · board cost per m²',
  'customer.choose.roomCost': 'this room only · Artemis cost',
  'customer.choose.closeRange': 'Close the range',
  'customer.choose.rangeTitle': 'The laminate range — one board for the whole job, at Artemis cost',
  'customer.choose.recommends': 'Artemis recommends',
  'customer.choose.orientation': 'Lay direction',
  'customer.choose.orientationHouse': 'Lay direction — one run through the whole house',
  'customer.choose.alongX': 'Across',
  'customer.choose.alongY': 'Along',
  'customer.choose.leastWaste': 'Least waste',
  'customer.choose.installerDecides': 'Installer decides',
  'customer.choose.chip1': 'How do the boards differ?',
  'customer.choose.chip2': 'How is the per-m² cost built?',
  'customer.choose.chip3': 'Why is carpet priced by the roll?',
  'customer.choose.chip4': 'Which colour goes with my kitchen and doors?',
  'customer.choose.emmaDesignRole': 'Artemis Flooring · boards, lay direction, colour',
  'customer.choose.emmaDesignIntro': 'Tell EmmA what is fixed in the house — cabinetry, benchtop, paint, doors, tiles — and the look you are after. She will say which board and colour sit well, which way to run it, and apply the choice here when you ask.',
  'customer.choose.noRoomTitle': 'Tap a room on the plan',
  'customer.choose.noRoomText': 'Each room shows its area, the floor going in and what it costs there.',
  'customer.choose.colourTitle': 'Colour',
  'customer.choose.colourNote': 'One colour per product for the whole job. Colours on a screen are a guide; we bring samples before the fit.',
  'customer.choose.skirtingTitle': 'Skirting — supply & install',
  'customer.choose.skirtingRun': '{lm} LM measured on your plan, door openings taken out',
  'customer.choose.skirtingExcluded': '; {rooms} left out',
  'customer.choose.skirtingIntro': 'Fitted after the floor goes down, tight to it. Corners mitred; straight runs joined square; open ends finished with a mitred return. Top edge caulked white, paintable.',
  'customer.choose.none': 'Not needed',
  'customer.choose.skirtingOpt': '{size}mm · {rate}/LM at cost',
  'customer.choose.profileTitle': 'Profile',
  'customer.choose.archTitle': 'Door architraves — both sides',
  'customer.choose.archIntro': 'The same board as the skirting, up both sides and across the head of each door, mitred at 45°. New build: nothing to remove.',
  'customer.choose.archRule': '{doors} doors take architraves ({size}mm). Open doorways, the entrance door and the wide sliders do not.',
  'customer.choose.archYes': 'Yes · {doors} doors',
  'customer.choose.disposalTitle': 'Offcut disposal',
  'customer.choose.disposalIntro': "Using the builder's skip on site costs nothing. Choose Artemis disposal only if no skip is available.",
  'customer.choose.disposalSkipRow': "Builder's skip on site",
  'customer.choose.disposalArtemisRow': 'Artemis takes the offcuts · {rate}/m²',
  'customer.choose.changesTitle': 'Changes from the recommendation',
  'customer.choose.reset': 'Back to the recommendation',
  'customer.choose.noChanges': 'Every room is on {product}.',
  'customer.carpet.measuredCoverage': 'measured floor coverage',
  'pricing.line.areaUnknown': 'Area unknown for {rooms} — record the dimensions in the site assessment to price this line.',
  'pricing.line.rollLength': 'Roll length ordered · LM',
  'pricing.line.rollLengthHint': 'Whole LM ordered. Include 0.10 m once in each physical cut before rounding the total up.',
  'pricing.line.rollWidth': 'Full roll width · m',
  'pricing.line.purchasedArea': 'Customer quantity: {whole} whole LM × {width} m = {area} m² purchased',
  'pricing.line.floorArea': 'Floor area · m²',
  'pricing.line.area': 'Area · m²',
  'pricing.line.areaHintCarpet': 'Underlay and glue are priced on this area',
  'pricing.line.areaHint': 'Quantity and area are the same for this floor',
  'pricing.line.layMethod': 'Lay method',
  'pricing.line.fromCatalogue': 'From the catalogue · {rule}',
  'pricing.line.notDetermined': 'Not determined',
  'pricing.line.thickerUnderlay': 'Thicker underlay upgrade',
  'pricing.line.role': 'This line is',
  'pricing.line.recommended': 'Recommended',
  'pricing.line.option': 'Customer may choose it',
  'pricing.line.group': 'Sub-category',
  'pricing.line.noGroup': 'No sub-category',
  'pricing.line.groupHint': 'One pick per sub-category — everything else in the same group is an alternative for the customer.',
  'pricing.margin.title': 'The rate on what the customer may choose',
  'pricing.margin.note': 'Applies to the options list only. Your recommended lines keep the catalogue price.',
  'pricing.margin.kindGm': 'GM % of sell',
  'pricing.margin.kindMarkup': 'Markup % on cost',
  'pricing.margin.both': 'GM {gm} of sell = markup {markup} on cost',
  'pricing.margin.none': 'No rate set — options are listed at the catalogue price.',
  'pricing.margin.unset': 'Not set for this customer',
  'pricing.margin.placeholder': 'e.g. 40',
  'pricing.margin.needed': '{count} alternative(s) are waiting for your GM% — set it for this customer and they are listed at that rate.',
  'priced.options.title': 'What the customer may choose',
  'priced.options.empty': 'No options yet. Mark a line as "Customer may choose it" and it appears here at your rate.',
  'priced.options.listedAt': 'Listed at {rate}',
  'priced.options.notInTotal': 'Alternatives are not added to the total — the customer picks one and the quote is repriced.',
  'priced.options.choose': 'Make this the pick',
  'priced.options.chosen': 'Pinned at {rate} — this is the recommended line now, and the price it was listed at is the price it keeps.',
  'priced.options.swapNote': 'Choosing one returns the other in its group to the alternatives at your rate.',
  'priced.col.rate': 'Rate',
  'priced.gm.column': 'GM%',
  'priced.gm.title': 'Gross margin as a share of the sell price, with the labour paid to an external installer. The second figure is what Artemis crew would add.',
  'priced.gm.headline': 'GM {gm} with an external installer',
  'priced.gm.bonus': 'Artemis crew: GM {gm} — a bonus of {points} points, and crew is limited.',
  'priced.gm.note': 'The external figure is the one that counts. Adjust any price above and both move.',
  'priced.assistedPrice': 'Your price per unit',
  'priced.assistedPriceHint': 'The engine priced this line. Type your own price per unit and it becomes the price, with the GM% updating both ways.',
  'priced.assistedTotal': 'line {amount}',
  'priced.rollWidthNeeded': 'Roll width needed',
  'pricing.selection.add': 'Add a product',
  'pricing.selection.addHint': 'Each product you add is its own line — its own colour, its own rooms, its own price. A job with carpet in the bedrooms and hybrid in the living areas is two lines.',
  'pricing.line.number': 'Product {n}',
  'canvas.floorPlanSummary': '{rooms} rooms · {area}',
  'canvas.recordedReadings': '{count} locked site readings · metres',
  'canvas.carpetLayout': 'Carpet cut proposal',
  'canvas.carpetOrder': '{cuts} cuts · {metres} m including fitting allowances → {whole} whole LM × {width} m = {area} m² purchased',
  'canvas.carpetReviewNeeded': 'Carpet rooms found. Save a physical cut review on the work canvas to attach a layout; no order quantity is inferred from room area.',
  'canvas.importCarpetOnly': 'Use saved carpet cut review',
  'canvas.importCarpetOnlyHelp': 'Keeps the assessed rooms and site details. The carpet order and price recalculate from these saved cuts after you save the draft.',
  'canvas.carpetOnlyImported': 'Saved carpet cuts added to this draft. Review the order and price, then save the draft.',
  'canvas.carpetCandidateImport': 'Add 29 LM candidate to private draft',
  'canvas.carpetCandidateHelp': 'Four shaped cuts from canvas save 13. Site checks remain open; adding it does not release a customer Proposal or place an order.',
  'canvas.carpetCandidateImported': '29 LM candidate added to this private draft. Save draft to keep it. Site checks must be resolved before customer release.',
  'canvas.carpetCandidateStatus': 'Provisional layout · site checks pending',
  'canvas.robeIncluded': 'includes {area} of wardrobe floor',
  'canvas.fixtureExcluded': '{area} m² fixed fixture excluded · flooring stops here',
  'canvas.robe': 'wardrobe',
  'canvas.robeLabel': 'robe {area}',
  'canvas.floorPlan': '{name} · {rooms} rooms · {area} m²',
  'canvas.floorPlanAlt': '{name} as drawn on the canvas, {rooms} rooms',
  'work.seed.doorTrim': 'Door trimming — {count} doors (from the drawing)',
  'work.standalone': 'Priced on its own',
  'work.standaloneHint': 'Tick it inside a product if it comes with that product instead.',
  'work.stairNote': 'Priced per step from {product}. Charged on actual when you approve it during install.',
  'work.carpetStair.standard': 'Standard carpet stair installation',
  'work.carpetStair.standardNote': 'Carpet falls under the nosing without wrapping or fixing around it. Carpet material is included in the purchased roll quantity.',
  'work.carpetStair.lips': 'Carpet lips installation',
  'work.carpetStair.lipsNote': 'Carpet wraps around and is fixed around the nosing. Carpet material is included in the purchased roll quantity.',
  'work.carpetStair.step': 'step',
  'work.carpetStair.materialIncluded': 'Installation charge only; stair carpet is already counted in purchased material.',
  'assessment.laserLocked': 'Bluetooth laser readings are locked here. Correct a site reading in Canvas, save a new revision, then import it into this draft.',
  'carpet.defaults.title': 'Standard carpet package',
  'carpet.defaults.note': 'Ticked items are included with the carpet product. Quantities come from the measured plan and carpet order; change only what this job needs.',
  'carpet.defaults.underlay': '8mm carpet underlay · m² coverage included',
  'carpet.defaults.orderPending': 'purchased area pending',
  'carpet.defaults.smoothedge': 'New or replacement smoothedge included',
  'carpet.defaults.trims': 'Carpet trims included',
  'carpet.defaults.removal': 'Remove and dispose existing carpet included',
  'carpet.defaults.stairs': 'Stair installation · {steps} steps, charged separately',
  'carpet.defaults.standard': 'Standard under-nose · $25/step',
  'carpet.defaults.lips': 'Wrapped carpet lips · $35/step',
  'carpet.defaults.toMeasure': 'length to measure',
  'work.planksNote': 'The extra flooring a stair run consumes — included in the stair work, not charged again.',
  'work.title': 'The work — everything that is not a product',
  'work.quickPickTitle': 'Common services',
  'work.quickPickNote': 'One click adds the registered service with its measured quantity. Adjust the line only when this job differs.',
  'work.manageServices': 'Manage service library',
  'work.quickPickRate': '${rate} / {unit} · EX GST',
  'work.quickPickRateUnset': 'Rate to confirm / {unit}',
  'work.blurb': 'Removal, disposal, skirting, stairs and anything you add. Seeded from what the canvas already measured; the rates come from the Artemis service costs.',
  'work.seed.stairs': 'Stairs — {count} steps (from the drawing)',
  'work.seed.remove': 'Remove & dispose (floor area)',
  'work.remove.freeCarpet': 'Free of charge — existing carpet',
  'work.remove.freeCarpetNote': 'Available only where the assessed existing floor is carpet.',
  'work.remove.charged': 'Remove & dispose — charged per m²',
  'work.remove.free': 'Free of charge · $0.00/m²',
  'accessory.carpet.smoothedge': 'Smoothedge',
  'accessory.carpet.trims': 'Carpet transition trims',
  'accessory.carpet.trimRateNeeded': 'Enter the measured length, then choose whether this trim is included with flooring or charged separately.',
  'accessory.trim.pricing': 'Pricing',
  'accessory.trim.included': 'Included with flooring',
  'accessory.trim.separate': 'Charged separately',
  'work.seed.removeOnly': 'Remove only — customer provides the skip',
  'work.seed.skirting': 'Skirting — {run} LM (drawn run)',
  'work.seed.accessories': 'Seed the accessories the drawing implies',
  'work.none': 'No work added yet. Start from the drawing, or add a custom service.',
  'work.addCustom': 'Add a custom service',
  'work.customName': 'Custom service',
  'work.name': 'Description',
  'job.depositReceived': 'Deposit received',
  'job.depositAmount': 'Amount · AUD',
  'job.depositPercent': '% of deal value',
  'job.depositUnrecorded': 'Amount not recorded',
  'job.depositDate': 'Date not recorded',
  'job.depositHelp': 'Enter dollars or a percentage of the recorded deal value. A percentage with no amount is a legacy setting, not proof of payment.',
  'job.depositRecorded': 'Deposit recorded {date} · {amount}',
  'commercial.mergeReview': 'Review the competing edits',
  'commercial.mergeHelp': 'Another saved revision changed the same details. Your entries are retained. Unrelated changes from both copies will be kept.',
  'commercial.mergeMine': 'Your entry',
  'commercial.mergeLatest': 'Latest saved entry',
  'commercial.mergeKeepMine': 'Keep my values and save combined changes',
  'commercial.mergeReturn': 'Return to my unsaved draft',
  'commercial.newOffer': 'New blank offer',
  'skirting.fromQuote': 'Skirting is included in the saved quote.',
  'skirting.selectionSaved': 'Selection saved. No further confirmation needed unless it changes.',
  'skirting.selectionOptional': 'Open details only to choose or change the profile or supply arrangements.',
  'skirting.editDetails': 'Choose / change skirting',
  'skirting.sourceDetails': 'Source details',
  'proposal.accept.priceFirst': 'Enter the agreed scope and price before recording acceptance.',
  'proposal.accept.directRule': 'Record the customer’s go-ahead for working version {version}. This creates the Job and sends no message or private link.',
  // Accepting a room-choice or open-book Proposal as released (23 Oct 2026, Minto)
  'proposal.accept.releasedCopy': 'This Proposal is accepted as released: v{version}, the copy the customer saw, priced from their own saved choice.',
  'proposal.accept.laterEdits': 'Edits saved after v{version} are not part of this acceptance. Release them first if the customer agreed to them.',
  'proposal.accept.customerChoice': 'The customer’s saved choice prices it at {amount} + GST: {count} rooms differ from our recommendation of {recommended}.',
  'proposal.release.customerChoice': 'The customer’s saved choice prices their page at {amount} + GST, not the {recommended} recommended: {count} rooms differ ({rooms}). Their page shows their choice.',
  'commercial.finalTotal': 'Final total · EX GST',
  'commercial.roundingDiscount': 'Rounding discount',
  'commercial.finalTotalInvalid': 'The final EX GST total must be between zero and the unadjusted scope total.',
  'pricing.rail.floorScope': 'Floor selections only. Stand-alone services and accessories are excluded here; use the complete Proposal cost review for whole-job costs and gross margin. An excluded cost is not a missing or conflicting cost record.',
  'pricing.rail.includedAccessory': 'An accessory included with a floor adds cost to that floor without adding price, lowering its gross margin.',
  'commercial.finalTotalUnavailable': 'Resolve missing or ranged prices before setting a final total.',
  'commercial.roundingTooLarge': 'The scope total is now below the rounding discount. Clear or change the discount.',
  'work.skirtingRooms': 'Rooms receiving skirting',
  'work.skirtingRoomHelp': 'Untick rooms handled by the customer or another trade. This changes skirting only. You can also type the quoted LM.',
  'work.quantity': 'Qty',
  'work.unit': 'UOM',
  'work.rate': 'Rate per',
  'work.rateUnset': 'not set',
  'work.optional': 'Optional — the customer chooses',
  'work.variation': 'Only if used — approved on site',
  'share.step.build': '1 · Build the customer copy',
  'share.step.preview': '2 · Preview — the built Proposal, v{version} (nothing sent)',
  'share.step.message': '3 · The message, and how it goes',
  'share.askAgain': 'Ask EmmA to write it again',
  'share.previewNote': 'This is the actual Proposal the customer opens — the released document itself, not a summary of it. Building and previewing send nothing.',
  'share.previewTitle': 'The built Proposal, exactly as the customer sees it',
  'share.emmaWriting': 'EmmA is reading the Proposal and writing the message…',
  'share.emmaRead': 'EmmA read {rooms} rooms, {options} options and what is included ({included}) and wrote this.',
  'share.none': 'nothing yet',
  'share.channel.email': 'Email',
  'share.channel.sms': 'Text',
  'share.subject': 'Subject',
  'share.body': 'Message',
  'share.bodySms': 'Message · {count} characters',
  'share.smsSubject': 'Your Artemis Flooring proposal',
  'share.needEmail': 'This customer has no email address yet. Add one — it is saved to the customer and used for this message.',
  'share.needPhone': 'This customer has no mobile number yet. Add one — it is saved to the customer and used for this message.',
  'share.saveToContact': 'Save to the customer',
  'share.contactSaved': 'Saved to the customer.',
  'share.savedBothUses': 'Saved on the customer record and used as the recipient here.',
  'share.toEmail': 'Going to {to} by email.',
  'share.toPhone': 'Going to {to} by text.',
  'case.context': 'Context and decision details',
  'case.message': 'Message to send',
  'case.emmaAsks': 'EmmA asks',
  'case.yourAction': 'Your action',
  'case.awaiting': 'Awaiting approval — not sent.',
  'case.checkStatus': 'Decision recorded. Check the delivery result above or in Communications.',
  'case.missingRecipient': 'Recipient is missing. Request changes before approval.',
  'delivery.submitted': 'Sent to the provider — recipient delivery is not confirmed.',
  'delivery.held': 'Approved — held by SMS quiet hours, sends {time} Sydney.',
  'delivery.queued': 'Approved and queued — not sent yet.',
  'delivery.sending': 'Sending — awaiting provider confirmation.',
  'delivery.failed': 'Sending failed.',
  'delivery.uncertain': 'Sending outcome is uncertain. Check before resending.',
  'delivery.declined': 'Declined — not sent.',
  'delivery.revision': 'Changes requested — not sent.',
  'delivery.checking': 'Checking the saved sending result…',
  'delivery.unavailable': 'Could not verify sending. Check Communications before retrying.',
  'delivery.open': 'Open message and status',
  'case.approveSms': 'Approve: send this SMS',
  'case.approveEmail': 'Approve: send this email',
  'case.approveMessage': 'Approve: send this message',
  'share.approveEmail': 'Review email for sending',
  'share.approveText': 'Review SMS for sending',
  'share.approveNote': 'Review the recipient and exact message in the approval card. Sending starts only after you approve that card.',
  'share.queuedEmail': 'Email queued for {to}.',
  'share.queuedText': 'Text queued for {to}.',
  'share.sendFailed': 'Could not queue the message',
  'share.draftNotSaved': 'The draft was not saved, so nothing was queued.',
  'share.sequence.first': 'First message to this customer about this Proposal — EmmA introduces it.',
  'share.sequence.revised': 'Revised Proposal — {count} earlier message(s), the last {days} day(s) ago. EmmA writes to what changed, not a new introduction.',
  'share.sequence.resend': 'Same version as the last message ({count} sent, the last {days} day(s) ago) — EmmA writes a follow-up, not a new offer.',
  'share.changes': 'Changed since the last message: {changes}',
  'share.reply': 'Customer replied: {summary}',
  'share.instruction': 'Tell EmmA what to change',
  'share.instructionHint': 'For example: mention we swapped the skirting to 92 mm and dropped the removal charge; keep it to three lines.',
  'share.rewrite': 'Rewrite',
  'share.rewriting': 'EmmA is rewriting…',
  'share.rewritten': 'EmmA rewrote the message. Review it before sending.',
  'share.finalMessage': 'The message that will be sent (released version {version})',
  'share.noReleasedAmount': 'The released copy has no total. Release the Proposal again before sending.',
  'share.saveRecipientFirst': 'Save it to the customer first — only a saved address or number can be sent to.',
  'share.releaseFirst': 'You have changes that are not released. The customer’s link still shows the previous version — release the latest changes first, then send.',
  'work.variationRate': 'If used — {rate} per {unit}, approved on site',
  'work.quantityAuto': 'Measured from the drawing — change it on the plan, not here',
  'work.ifNeeded': 'if needed',
  'work.subOptions': '{count} sub-options offered',
  'work.optionUnpriced': 'no rate yet',
  'work.includedHeight': 'Height included in this quote',
  'work.includedHeightHint': 'Other heights show only the price difference.',
  'work.height.standard': '66–67 mm',
  'work.height.medium': '92 mm',
  'work.height.tall': '138–140 mm',
  'work.fromCanvas': 'quantity from the drawing',
  'work.fromConfig': 'from the service costs',
  'work.custom': 'your own line',
  'work.unpriced': 'no approved rate — this line cannot be quoted yet',
  'work.accessories': 'Accessories',
  'work.accessoriesBlurb': 'Switch on what you are offering, per site or per floor. One bound to a product is shown to the customer as included in its price.',
  'work.scope': 'Applies to',
  'work.wholeSite': 'Whole site',
  'work.colour': 'Colour / option',
  'work.boundTo': 'Included with product',
  'work.boundToHint': 'product id — leave blank to price it',
  'work.boundNote': 'The customer sees this as an indent line: included in the price, no figure.',
  'work.accessory.nosing': 'Stair nosing',
  'work.accessory.planks': 'Extra planks for the stairs',
  'work.accessory.withStairs': 'Comes with the stair work — included in its price, not charged separately.',
  'work.accessory.scotia': 'Scotia — from its own confirmed traced run',
  'work.accessory.trims': 'Trims — already drawn on the canvas',
  'work.accessory.underlay': 'Underlay — whole site',
  'work.accessory.moisture': 'Moisture barrier — whole site',
  'work.addAccessory': 'Add an accessory',
  'work.chooseFamily': 'Choose a family…',
  'work.included': 'included',
  // The switchboard under a product — what that floor comes with, and the type of each. Jacky, 19 Sep,
  // on O-00000004: it "wont allow adding trims or able selecting type of underlay or moisture barrier
  // under the product switch like previous platform".
  'accessory.title': 'What this floor comes with',
  // Drawn trims on the Proposal (16 Oct 2026), included in the floor price.
  'accessory.drawnTrims': 'Drawn on the plan: {list}',
  'accessory.addDrawnTrims': 'Add the drawn trims (included in the floor price)',
  'accessory.hint': 'Tick a family and choose its type. Anything ticked here is shown to the customer as included in that product’s price.',
  'accessory.chooseType': 'Choose the type…',
  'accessory.qty': 'Qty',
  'accessory.carried': 'cost {cost} per {unit}, carried in the product’s price',
  'accessory.clear': 'remove',
  'accessory.addTrim': 'Add trim',
  'accessory.addUnderlay': 'Add another underlay',
  'accessory.assessmentNeed': 'Site assessment: {requirement}',
  'accessory.measuredUnit': 'Measured in LM; order pieces are calculated after save.',
  'accessory.stockPieceCost': 'supplier cost {cost} per {length} m stock piece',
  'assessment.field.underlay': 'Underlay requirement',
  'material.title': 'Scotia and trim purchasing',
  'material.saveToRefresh': 'Save the Proposal to refresh this staff-only calculation.',
  'material.acceptedCarry': 'This saved calculation is frozen on release and carried to the accepted Job and purchase order.',
  'material.calculation': '{measured} LM measured + {safety} LM safety, in {piece} m stock pieces = {pieces} pieces.',
  'material.provisionalCalculation': '{measured} LM traced so far (provisional). Purchasing pieces are unresolved until measurements and cutting rules are confirmed.',
  'material.source': 'Supplier source: {source} · effective {date}',
  'material.unresolved': 'Not orderable yet: one or more measurements, supplier facts or cutting rules still need confirmation.',
  'accessory.kind.underlay': 'Underlay',
  'accessory.kind.moisture_barrier': 'Moisture barrier',
  'accessory.kind.scotia': 'Scotia / edging',
  'accessory.kind.trims': 'Trims',
  'accessory.kind.adhesive': 'Adhesive',
  'accessory.kind.levelling': 'Levelling',
  'accessory.kind.stair': 'Stair fittings',
  'work.scotiaNote': 'The canvas has a confirmed Scotia run, measured separately from skirting.',
  'work.trimsNote': 'The trims were already drawn on the canvas; the run comes from there.',
  'canvas.title': 'The canvas drawing',
  'canvas.noJob': 'This proposal is not linked to a job yet, so there is no drawing to import — the canvas drawing lives on the job.',
  'canvas.noJobHint': 'Either record the rooms by hand below, or open the job and measure it in the canvas — then this panel imports every room, its drawn area and its floor in one click.',
  'canvas.noDrawingHint': 'The canvas has not been used for this job yet. Open the job to capture a drawing, or record the rooms by hand below.',
  'canvas.openJob': 'Open the job',
  'canvas.openSiteCanvas': 'Open Site Canvas',
  'carpetRun.open': 'Carpet run',
  'canvas.fromJob': 'From the job\'s measured drawing',
  'canvas.fromOpportunity': 'From the plan traced on {ref}',
  'canvas.plansUntraced': '{ref} holds {count} plan(s) with no rooms traced yet ({floors}). Trace them in the canvas on the opportunity and this imports every room, its drawn area and its floor.',
  'canvas.noPlan': 'No plan is attached to this work and no drawing exists yet. Attach the plan on the opportunity, or record the rooms by hand below.',
  'canvas.saveFirst': 'Save the draft once, then the rooms and the drawing can be brought in.',
  'canvas.assetsNote': 'The drawing itself — shown to the customer beside the offer. It never changes a price.',
  'canvas.attach': 'Show on the portal',
  'canvas.attached': 'On the portal',
  'canvas.attachedCount': '{count} drawing(s) on the portal.',
  'canvas.captionFloor': 'Drawn plan · {floor}',
  'canvas.ready': 'Ready to import',
  'canvas.sharedRevision': 'Shared assessment revision',
  'canvas.bindOnly': 'Use this revision — keep Proposal details',
  'canvas.bound': 'Revision selected',
  'canvas.bindHelp': 'Link this saved assessment as the reference without replacing rooms, measurements, prices or Proposal wording. Importing rooms is a separate action below.',
  'canvas.bindingSelected': 'Assessment revision selected. Proposal details are unchanged. Save draft to retain this link.',
  'canvas.sharedRevisionOption': '{owner} · save {number} · {date}',
  'canvas.sharedRevisionSource': 'Shared work revision · {owner} save {number}',
  'canvas.legacyFallback': 'Legacy read-only fallback. Save this assessment into the shared work record before issuing a new Proposal.',
  'pricing.line.noRoomsCta': 'No rooms yet, so there is no area to price. Import the drawing or record the rooms.',
  'pricing.line.goToAssessment': 'Go to the site assessment',
  'canvas.reading': 'Reading the drawing…',
  'canvas.none': 'No drawing has been recorded for {job} yet.',
  'canvas.summary': 'Revision {revision} · {rooms} rooms · {area} m² · {laser} measured on site',
  'canvas.wholeSite': 'Whole site',
  'canvas.planOnly': '{count} of these come from a plan, not a site measure — the carpet estimate stays an estimate until each room is measured.',
  'canvas.modeReplace': 'Replace the assessment',
  'canvas.modeAppend': 'Add to the assessment',
  'canvas.import': 'Import {count} rooms from the drawing',
  'canvas.importReplace': 'Replace the {count} rooms with the drawing',
  'canvas.replaceWarning': 'This replaces the {count} rooms already in this assessment. The drawing becomes the record.',
  'pricing.line.shapeConfirmed': 'Dimensioned shape confirmed',
  'pricing.line.accessories': 'Accessories · cost',
  'pricing.line.sellPer': 'Sell per {unit}',
  'pricing.line.estimateOnly': 'The site assessment has not recorded a dimensioned shape for every room, so this carpet line stays an estimate.',

  // ── 2.2 priced options ────────────────────────────────────────────────────
  'priced.title': 'Priced options',
  'priced.empty': 'Select a product and the engine prices it here. Nothing is priced until something is chosen.',
  'priced.staffTitle': 'Priced options · staff view',
  'priced.staffNote': 'Figures produced by the pricing engine — the sales budget and internal costs never reach the customer copy',
  'priced.updating': 'Updating the totals… your fields stay editable',
  'priced.pricing': 'Pricing…',
  'priced.failed': 'This selection could not be priced: {message}',
  'priced.col.line': 'Line',
  'priced.col.sell': 'Sell',
  'priced.col.yourPrice': 'Your price / unit',
  'priced.col.notes': 'Notes',
  'priced.codStaffOnly': 'COD basis · staff only',
  'priced.notYetDecided': 'Not yet decided',
  'priced.zeroDropped': '$0 — dropped from the customer copy',
  'priced.subtotalFrom': 'Subtotal (from)',
  'priced.incomplete': 'Incomplete',
  'priced.internalTitle': 'Internal — never customer-facing',
  'priced.profit.title': 'Estimated profitability',
  'priced.profit.staffOnly': 'Staff only · never shown to the customer',
  'priced.profit.externalProfit': 'Estimated profit · external installer',
  'priced.profit.externalMargin': 'Estimated margin · external installer',
  'priced.profit.crewProfit': 'Estimated profit · Artemis crew',
  'priced.profit.crewMargin': 'Estimated margin · Artemis crew',
  'priced.profit.complete': 'Estimate uses the pricing engine’s material cost and labour for every priced line. Selling figures exclude GST.',
  'priced.profit.partial': 'Partial estimate from {priced} of {total} lines with known sell, material and labour. Unresolved lines are excluded.',
  'priced.profit.waiting': 'Profitability appears as soon as one line has a selling price, material cost and labour estimate.',
  'priced.externalLabour': 'External labour',
  'priced.contribution': 'Contribution',
  'priced.decidedBlurb': 'The engine returns no material or contribution total while any line is undecided. Over the lines that are decided:',
  'priced.decidedSell': 'Sell · {count} line',
  'priced.decidedSellPlural': 'Sell · {count} lines',
  'priced.excluded': 'Excluded from the figures above:',
  'priced.excludedReason': '{label} — {reason}',
  'priced.notPriced': 'not priced',
  'priced.decidedNote': "This is the decided-lines view you asked for, not the engine's own total. The engine's total stays unavailable until every line is decided — which is what the customer copy enforces.",
  'priced.customerTitle': 'Customer copy · priced options',
  'priced.customerExact': 'Exactly what the released link shows',
  'priced.customerNotReleasable': 'Not releasable yet',
  'priced.blockedTitle': 'This offer cannot be released while a line has no price:',
  'priced.blockedNote': 'Nothing unpriced is ever shown to a customer. Resolve the line above and the customer copy appears.',
  'priced.zeroNote': 'Not shown to the customer because it is not charged: {lines}.',
  'priced.notice.undecidedColour': 'One or more lines depend on a colour that is not chosen yet. The final price is confirmed when you choose.',
  // The waste sentence a customer reads on an area line, by lay — `wasteNoticeId()` in
  // `@artemis-group/aiw-contracts` returns these two ids, and `chargeableArea` returns this English as the
  // line's note. `{percent}` is the config's own rate, so changing 7% to 6% needs no new translation.
  'priced.waste.straight': 'Includes {percent}% for cutting and waste; the final charge is by actual measurement.',
  'priced.waste.pattern': 'Includes {percent}% for cutting and waste on a patterned lay; the final charge is by actual measurement.',

  // ── EmmA in the header (the strip and its drawer) ────────────────────────
  'emma.history': 'Conversations',
  'emma.historyEmpty': 'Nothing said yet.',
  'emma.newThread': 'Start a new conversation',
  'emma.untitled': '(untitled)',
  'emma.branchCarried': 'New conversation — she carried over what was said in “{title}”.',
  'emma.branchAsk': 'Start a new conversation here? What you were just doing stays in “{title}”, and she carries it across.',
  'emma.branchStart': 'Start new',
  'emma.branchCancel': 'Not now',
  'emma.branchMark': 'branched',
  'emma.historyFailed': 'This conversation did not load — nothing was lost, it is still on her side.',
  'emma.historyNoSession': 'Your sign-in has expired, so this conversation did not load. Nothing was lost.',
  'emma.historyRetry': 'Try again',
  'emma.emptyWhich': 'Open on conversation {id} — her store returned {count} turns for it.',
  'emma.turns': '{count} turns',
  'emma.stripWaiting': '{count} waiting on you — open her',
  'emma.stripIdle': 'nothing waiting on you',
  'emma.open': 'open',
  'emma.close': 'close',
  'emma.tabChat': 'Talk to EmmA',
  'emma.tabWaiting': 'Waiting on you',
  'emma.desk': 'Full desk',
  'emma.empty': 'Ask her anything — this page travels with the question, so “this job” means the job on screen.',
  'emma.placeholder': 'Tell her what to do, or ask what she knows…',
  'emma.send': 'Send',
  'emma.working': 'Working…',
  'emma.thinking': 'working…',
  'emma.howIDidIt': 'How I got there',
  'emma.showDetails': 'Show details',
  'emma.noWords': 'Done.',
  'emma.unreachable': 'EmmA could not be reached. Nothing was changed; try again.',
  'emma.stopped': 'EmmA stopped before she answered — nothing was changed. {reason}',
  'emma.signIn': 'Your session has expired. Sign in again and repeat it — nothing was changed.',
  'emma.openProposal': 'Open what she prepared',
  'emma.nothingWaiting': 'Nothing is waiting on you.',
  'emma.reviewDraft': 'Review the wording before it goes →',
  'emma.queueNote': 'These are already-open items, not new work.',
  // ER-08: files handed to her in chat. Server refusals arrive as sentences and are shown as they came;
  // these are the drawer's own words — the picker, the tray, the chips, and the refusals decided here.
  'emma.attach': '＋ File',
  'emma.attachTitle': 'Attach a PDF or photo to this conversation — she reads it on every turn from now on',
  'emma.attachKeeping': 'Keeping {name}…',
  'emma.attachPending': 'Goes with your next message',
  'emma.attachOpen': 'Open {name}',
  'emma.attachRemove': 'Don’t mark {name} on this message',
  'emma.attachNote': '{kind} · {size}',
  'emma.attachKind.pdf': 'PDF',
  'emma.attachKind.photo': 'Photo',
  'emma.attachKind.text': 'Text',
  'emma.attachUnknownType': 'this type',
  'emma.attachRefuse.type': 'I can take PDFs, photos (JPEG, PNG, WebP) and plain text files, not {mime}.',
  'emma.attachRefuse.heic': 'I can take PDFs, photos (JPEG, PNG, WebP) and plain text files, not {mime}. Export it as JPEG or PNG first.',
  'emma.attachRefuse.empty': 'The file is empty.',
  'emma.attachRefuse.big': 'That file is {mb} MB; EmmA reads files up to {max} MB.',
  'emma.attachRefuse.many': 'This conversation already holds {max} files, the most she keeps on one. Start a new conversation for more.',
  'emma.attachOpenFailed': 'That file could not be opened — {reason}',
  'emma.placeholderFile': 'Ask her about the file…',

  // ── the job whiteboard ───────────────────────────────────────────────────
  'job.openInMaps': 'Open in Maps',
  'job.reference': 'Job reference',
  "closedJob.title": "Bring this job back",
  "closedJob.lede": "This job is {stage}. Choose how it comes back; every choice keeps its quotes, notes and history.",
  "closedJob.liveOnly": "These choices are available in the live workspace.",
  "closedJob.loading": "Checking the job’s stage history…",
  "closedJob.unavailable": "The return options could not be loaded. Refresh before changing this job.",
  "closedJob.retry": "Retry",
  "closedJob.restore.title": "Return to previous stage",
  "closedJob.restore.titleTo": "Return to previous stage ({stage})",
  "closedJob.restore.line": "Undo the stall: back to the stage its own history shows it held. No customer re-acceptance; installations stay as they are.",
  "closedJob.restore.action": "Return to previous stage",
  "closedJob.restore.done": "Returned to {stage}",
  "closedJob.opportunity.title": "Return to opportunity",
  "closedJob.opportunity.line": "Put it back in the Pipeline as an open enquiry, for legacy jobs whose stage history does not match.",
  "closedJob.opportunity.lineTo": "Put it back in the Pipeline as an open enquiry ({stage}), for legacy jobs whose stage history does not match.",
  "closedJob.opportunity.action": "Return to opportunity",
  "closedJob.opportunity.done": "Returned to the Pipeline",
  "closedJob.reopen.title": "Reopen with customer acceptance",
  "closedJob.reopen.line": "The customer has accepted the saved quote again: restart the same work at Pending Schedule.",
  "closedJob.reason": "Why (at least 10 characters)",
  "closedJob.reasonHint": "What happened, e.g. stalled by mistake",
  "closedJob.cancel": "Cancel",
  "closedJob.why.not_closed": "Only a Stalled or Lost job can use this.",
  "closedJob.why.no_history": "No trustworthy earlier stage: no recorded stage change put this job where it is. Use Return to opportunity or Reopen.",
  "closedJob.why.unknown_stage": "No trustworthy earlier stage: the recorded one is retired or unknown. Use Return to opportunity or Reopen.",
  "closedJob.why.paid_date": "A paid date is recorded, so only a return to Fully Paid fits. Check the payment records first.",
  "closedJob.why.close_date": "A close (invoiced) date is recorded, so only a return to Invoiced or Fully Paid fits. Check the financial history first.",
  "closedJob.why.aiw_origin": "This job was created by an accepted offer in AIW, and that acceptance cannot be safely unwound. Use Return to previous stage or Reopen.",
  "closedJob.why.aiw_acceptance": "This job carries an AIW acceptance or Pipeline record. Use Return to previous stage or Reopen.",
  "closedJob.why.installation": "This job has installation records, so it would not appear in the Pipeline. Use Return to previous stage or Reopen.",
  "closedJob.why.money": "Money is recorded against this job (deposit, paid or close date, an open invoice or a cash ledger), so it cannot become an opportunity.",
  'workTabs.label': 'Work sections',
  'workTabs.overview': 'Overview',
  'workTabs.canvas': 'Site Canvas',
  'workTabs.opening': 'Opening Site Canvas…',
  'workTabs.proposal': 'Proposal',
  'workTabs.installation': 'Installation',
  'workTabs.documents': 'Documents',
  'quote.open': 'Open quote',
  'schedule.date': 'Day to book',
  'schedule.crew': 'Crew',
  'schedule.assignee': 'Crew or staff member',
  'schedule.crewChoose': 'Choose a crew…',
  'schedule.crewUnknown': 'Not a name on the installer book — it will be recorded as typed.',
  'schedule.external': 'subcontractor',
  'schedule.notes': 'Anything the crew should know',
  'schedule.book': 'Book the crew-day',
  'schedule.booking': 'Booking…',
  'schedule.booked': 'Booked {team} on {date}. The gate and the next date are derived from the booked days, and the job\'s stage follows.',
  'schedule.days': 'Crew-days',
  'schedule.duration': '{days} day card(s) · {count} crew assignment(s)',
  'schedule.addDay': '+ Add a day',
  'schedule.addSameDay': '+ Add crew',
  'schedule.addNextDay': '+ Add next day',
  'schedule.currentProposal': 'Current saved Proposal · v{version}',
  'schedule.quoteTasks': 'Work from the current saved Proposal / quote',
  'schedule.chooseTask': 'Select at least one quoted work item for this job day. Every crew attending that day shares this scope.',
  'schedule.combinationInvalid': 'Every row needs a valid Sydney date, an assignee and quoted work. The same crew + day combination can appear only once.',
  'schedule.dayWork': 'Work planned that day',
  'schedule.dayWorkHint': 'e.g. preparation, installation, finishing',
  'schedule.datesInvalid': 'Every crew-day needs a different, valid date.',
  'schedule.savePlan': 'Save installation plan',
  'schedule.saving': 'Saving plan…',
  'schedule.saved': 'Saved {count} crew + day combination(s). The calendar and job now read this exact plan.',
  'schedule.inactive': 'Scheduling is available only while this Job is Pending Schedule or Installation Scheduled.',
  'schedule.multipleInstalls': 'This job has more than one installation record. Choose and reconcile the installation before changing its crew-days.',
  'schedule.pending': 'Sent for approval — nothing is booked until it is released.',
  'schedule.refused': 'Not booked: {reason}',
  'schedule.failed': 'Not booked: {message}',
  'schedule.replacedWork': 'This saved work is no longer in the accepted quote. Untick it and select the agreed replacement above before saving.',
  'schedule.quoteLocksConflict': 'The Job and installation reference different accepted quotes. Confirm the agreed scope before scheduling.',
  'schedule.quoteUnavailable': 'The accepted quote is unavailable. Restore its reference before scheduling.',
  'schedule.quoteAmbiguous': 'More than one primary quote is recorded. Choose the accepted quote before scheduling.',
  'schedule.installAmbiguous': 'More than one installation is recorded. Choose the installation before scheduling.',
  'schedule.quoteRequired': 'Choose a primary quote before scheduling its work.',
  'schedule.portal': 'The crew reads this in the installer portal',
  'schedule.destinations': 'Installer crews use the portal; staff use the Artemis mobile app',
  'schedule.derived': 'The gate, the next date and the job stage are derived, never typed.',
  'job.installBelow': 'Install days below',
  'job.completionPhotos': 'View completion photos',
  'job.completionReview': 'Installer reports',
  'job.completionReviewError': 'Installer reports could not be loaded.',
  'job.completionReviewEmpty': 'No installer report has been submitted for this job.',
  'job.dayReport': 'Day completion report',
  'job.scopeCompleted': 'Assigned scope completed',
  'job.setOutReport': 'Set-out check',
  'job.issueReport': 'Site issue',
  'job.progressReport': 'Progress report',
  'job.wholeWorkOrderReported': 'All scheduled days reported',
  'job.officeConfirmTitle': 'Office installation confirmation',
  'job.officeConfirmHelp': 'Confirm the physical work from your own evidence. Installer reports and payment are recorded separately.',
  'job.officeNotes': 'What did the office verify?',
  'job.officeUpload': 'Add completion photos',
  'job.officeUploading': 'Uploading photos…',
  'job.officeConfirmAction': 'Confirm installation complete',
  'job.officeConfirmed': 'Office confirmed installation complete',
  'job.officeConfirmError': 'Office confirmation was not saved.',
  'job.officeReadError': 'Office confirmation could not be loaded.',
  // Batch upload (1 Nov 2026): one refused file no longer stops the rest; the message names what did not land.
  'job.officePhotoPartial': 'Saved {saved} of {total} photos. Not saved: {names}. Add those again; the others are already on the Job.',
  'job.officePhotoReadError': 'Office photos could not be loaded.',
  'job.installerUploadedPhotos': 'Installer uploaded photos',
  'job.officeUploadedPhotos': 'Office uploaded photos',
  'job.installerPhotoReadError': 'Installer photos could not be loaded.',
  'job.noInstallerPhotos': 'No installer photos uploaded for this Job.',
  'job.photoCount': '{count} photos',
  'job.documentActions': 'Prepare customer documents',
  'job.documentsTitle': 'Job documents',
  'job.documentsDraftHint': 'Saved drafts stay private until approved and sent.',
  'job.documentsLoading': 'Loading saved drafts…',
  'job.documentsLoadFailed': 'Saved drafts could not be loaded.',
  'job.documentsDraft': 'draft',
  'job.documentsOpen': 'Open PDF',
  'job.documentsEmpty': 'No completion or warranty draft saved for this Job yet.',
  'job.documentsOpenFailed': 'This saved PDF could not be opened. Review the draft in Communications.',
  'job.showPhotoCopies': 'Show {count} office copies also in installer photos',
  'job.hidePhotoCopies': 'Hide {count} repeated office copies',
  'job.documentNeedsCompletion': 'The completion report needs a saved installer day report or an office installation confirmation. The warranty statement does not.',
  'job.documentNeedsCustomer': 'Link the customer contact before preparing a document draft.',
  'job.documentReviewHint': 'Preview the PDF, then save it as a communication draft. Sending still needs human review.',
  'job.documentProduct': 'Supplied product',
  'job.documentCompletionDate': 'Completion date',
  'job.documentDateFromOffice': 'Prefilled from the office confirmation. Change it if the work finished on another day.',
  'job.documentDateFromInstaller': 'Prefilled from the installer’s day report. Change it if the work finished on another day.',
  'job.documentDateFromSchedule': 'Prefilled from the last scheduled crew day. Change it if the work finished on another day.',
  'job.documentDateUnknown': 'Enter the day the installation finished.',
  'job.documentSuppliedProduct': 'Supplied product',
  'job.documentProductUnknown': 'No supplied product is recorded on this Job’s Proposal or quote. Enter it below.',
  'job.documentWarrantyOnFile': 'Manufacturer warranty on file',
  'job.documentWarrantyMissing': 'No manufacturer warranty PDF is on file for {product}. The statement refers to the manufacturer’s published terms; add the PDF to the product in the Catalogue to attach it.',
  'job.documentWarrantyUnavailable': 'The supplied product and its warranty document could not be read for this Job.',
  'job.strataTitle': 'Strata submission pack',
  'job.strataHint': 'Built from this Job’s accepted sale, saved drawing, the supplier documents on file for the product and the company certificates. Kept under Prepared documents; nothing is sent from here.',
  'job.strataUnavailable': 'The strata pack could not be read for this Job.',
  'job.strataUnderlay': 'underlay',
  'job.strataMissing': 'Not on file:',
  'job.strataPrevious': '{count} earlier revision(s) kept; latest is rev {revision}.',
  'job.strataPrepare': 'Prepare strata pack (rev {revision})',
  'job.strataOpenDocuments': 'Open prepared documents',
  'job.strataKept': 'Strata pack rev {revision} ({pages} pages) prepared and kept under this Job. Review every page before it goes to the strata manager.',
  'job.strataNotKept': 'The pack was prepared but could not be kept under the Job: {reason}. Save the preview if you need it.',
  'job.strataFailed': 'The strata pack could not be prepared.',
  'job.documentManufacturerLimit': 'The manufacturer documents on file exceed the email attachment limit (four PDFs, 10 MB in total with the statement).',
  'job.documentAttachments': 'Documents in this draft',
  'job.prepareCompletion': 'Prepare completion report',
  'job.prepareWarranty': 'Prepare warranty statement',
  'job.previewDocument': 'Preview PDF',
  'job.saveDocumentDraft': 'Save communication draft',
  'job.documentPrepared': 'PDF ready for review.',
  'job.documentPending': 'Document draft is awaiting review.',
  'job.documentFailed': 'Document could not be prepared.',
  'job.documentNeedsAddress': 'Save the site address before preparing customer documents.',
  'job.documentPhotosUnavailable': 'Some saved completion photos are unavailable. Review the archive before preparing the report.',
  'job.documentNeedsProduct': 'Confirm the supplied product before preparing the warranty statement.',
  'job.documentDraftSaved': 'Draft saved; review it before sending.',
  'job.openCommunications': 'Open Communications',
  'job.deliverables': 'Job completion documents',
  'job.deliverablesEmpty': 'No completion report or warranty statement has been issued for this job.',
  'job.deliverablesError': 'Saved completion documents could not be loaded.',
  'job.completionReport': 'Completion report',
  'job.warrantyStatement': 'Warranty statement',
  'wb.title': 'Whiteboard',
  'wb.loading': 'reading…',
  'wb.version': 'version {version} · {count} entries',
  'wb.history': 'History ({count})',
  'wb.hideHistory': 'Hide history',
  'wb.historyNote': 'Every write keeps the whole previous document — these versions can be restored:',
  'wb.empty': 'Nothing on this whiteboard yet. It takes anything — a note, a table, a drawing, a photo, a PDF — in whatever shape the job needs.',
  'wb.unavailable': 'The whiteboard could not be read: {message}',
  'wb.untitled': '(untitled)',
  'wb.unknownKindHint': 'This build does not draw this kind yet — it is stored and shown raw rather than hidden.',
  'wb.rawShape': 'Show the raw shape',
  'wb.superseded': 'Removed: {reason}',
  'wb.assets': '{count} attachment(s)',
  'wb.addTitle': 'Write on it',
  'wb.addNote': 'Tell EmmA in the strip and she writes here too — this box is for when you would rather type it yourself.',
  'wb.kind': 'Kind',
  'wb.kind.note': 'Note',
  'wb.kind.table': 'Table',
  'wb.kind.checklist': 'Checklist',
  'wb.kind.drawing': 'Drawing',
  'wb.kind.photo': 'Photo',
  'wb.kind.pdf': 'PDF',
  'wb.titlePlaceholder': 'What is this about?',
  'wb.bodyPlaceholder': 'Anything — measurements, what the customer asked for, what the site is like…',
  'wb.save': 'Add to the whiteboard',
  'wb.saveNote': 'Saved to the job, not sent anywhere.',
  'wb.saved': 'Added. The previous version is kept.',
  'wb.pending': 'Waiting for approval in EmmA Desk.',
  'wb.notConfirmed': 'The outcome could not be confirmed — retry the same entry safely.',

  // ── EmmA Desk: the conversation, from her store ──────────────────────────
  'desk.conversation': 'Conversation',
  'desk.session': 'session {id}',
  'desk.noSession': 'no conversation yet — ask her something from the strip and it will appear here',
  'desk.refresh': 'Refresh',
  'desk.startFresh': 'Start a fresh conversation',
  'desk.loadingHistory': 'Reading the conversation…',
  'desk.emptyHistory': 'Nothing said yet. Open her from the top strip, on any page.',

  // ── Pipeline and the opportunity page ────────────────────────────────────
  'pipe.title': 'Pipeline',
  'pipe.lede': 'Work we are pursuing. Customer acceptance carries the same history into a Job.',
  'pipe.search': 'Search Pipeline',
  'pipe.searchPlaceholder': 'Customer or suburb',
  'pipe.leadInbox': 'Lead inbox',
  'pipe.acceptedJobs': 'Accepted Jobs',
  'pipe.loading': 'Loading Pipeline…',
  'pipe.totals': '{count} items · {value} recorded value. This is not received revenue.',
  'pipe.siteTbc': 'Site to confirm',
  'pipe.notPriced': 'Not priced',
  'pipe.openOpportunity': 'Open opportunity',
  'pipe.openWork': 'Open work',
  'pipe.prepareProposal': 'Prepare Proposal',
  'pipe.prepareAnother': 'Prepare another Proposal',
  'pipe.none': 'No work in this view.',
  'pipe.phase.allActive': 'All active',
  'pipe.phase.enquiry': 'Enquiry',
  'pipe.phase.preparing': 'Preparing quote',
  'pipe.phase.quoteSent': 'Quote sent',
  'pipe.phase.accepted': 'Accepted — create Job',
  'pipe.phase.stalled': 'Stalled',
  'pipe.stalledNote': 'Stalled work is parked: the customer paused and may come back. It is not counted in All active. Use Back to Pipeline to bring it back.',
  // Move to Stalled / Back to Pipeline on a Pipeline card (12 Oct 2026)
  'pipe.moveToStalled': 'Move to Stalled',
  'pipe.stallReason': 'Why is it stalled?',
  'pipe.stallPlaceholder': 'Customer paused until after the holidays',
  'pipe.stallDone': 'Moved to Stalled',
  'pipe.backToPipeline': 'Back to Pipeline',
  'pipe.backReason': 'Why is it back?',
  'pipe.backDefault': 'Customer is back in touch',
  'pipe.backDone': 'Back in the Pipeline',
  'pipe.reasonHint': 'One line, at least 3 characters.',
  'pipe.cancel': 'Cancel',
  'lead.disposition.stalled': 'Stalled',
  'lead.disposition.stalledHint': 'Parked, not lost — the customer paused and may come back. It stays in Pipeline under Stalled.',
  'lead.disposition.wentQuietHint': 'Went quiet means dead — it leaves the Pipeline. Use Stalled if the customer may come back.',
  'lead.noAnswer.button': 'No answer — text',
  'lead.noAnswer.busy': 'Asking EmmA…',
  'lead.noAnswer.hint': 'Records the call attempt and has EmmA draft a text with your free times and the booking link.',
  'lead.noAnswer.drafted': 'EmmA drafted this text. It is waiting in Approvals / Communications for you to send.',
  'lead.noAnswer.failed': 'EmmA could not draft the text: {reason}',
  'lead.noAnswer.unreachable': 'EmmA could not be reached. Try again.',
  'opp.title': 'Opportunity',
  'opp.kicker': 'Opportunity · {ref}',
  'opp.untitled': 'Customer opportunity',
  'opp.preJob': 'Pre-job work',
  'opp.siteTbc': 'Site to confirm',
  'opp.unavailable': 'Opportunity unavailable',
  'opp.notInWorkspace': 'This reference is not available in this workspace.',
  'opp.loading': 'Loading…',
  'opp.customerSite': 'Customer & site',
  'opp.editDetails': 'Edit customer & site',
  'opp.saveDetails': 'Save details',
  'opp.cancelEdit': 'Cancel',
  'opp.detailsSaved': 'Customer and site details saved.',
  'opp.loadingDetails': 'Loading customer and site details…',
  'opp.detailsFailed': 'Customer and site details could not be loaded. Refresh before editing.',
  'opp.contactFailed': 'The linked customer could not be loaded. Refresh before editing.',
  'opp.contact': 'Contact',
  'opp.phone': 'Phone',
  'opp.email': 'Email',
  'opp.site': 'Site',
  'opp.siteAddress': 'Work-site address',
  'opp.siteAddressPlaceholder': 'Unit, street number and street name',
  'opp.suburb': 'Suburb',
  'opp.postcode': 'Postcode',
  'opp.phoneInvalid': 'A nonblank phone number must contain at least one digit.',
  'opp.siteOwnership': 'The work-site address belongs to this opportunity. It does not replace the customer’s home or billing address.',
  'opp.notRecorded': 'not recorded',
  'opp.brief': 'Customer brief',
  'opp.noBrief': 'No brief supplied.',
  'opp.acceptedJob': 'Accepted Job',
  'opp.register': 'Register what you know',
  'opp.registerNote': 'Before a quote exists, the record of this opportunity is what you saw and said — a site visit, a call, what the customer wants. It is saved against the customer, and it is what the next person reads.',
  'opp.channel': 'How',
  'opp.direction': 'Direction',
  'opp.dirOut': 'We contacted them',
  'opp.dirIn': 'They contacted us',
  'opp.whatHappened': 'What happened',
  'opp.whatPlaceholder': 'For example: measured living, three bedrooms and the hallway; existing carpet direct-stick; customer wants it done before the 30th.',
  'opp.recordIt': 'Record it',
  'opp.nextStep': 'Next step',
  'opp.nextWhat': 'What needs doing',
  'opp.nextPlaceholder': 'For example: send the quote for the stairs',
  'opp.nextWhen': 'When',
  'opp.scheduleIt': 'Schedule it',
  'opp.recorded': 'Recorded so far',
  'opp.loadingTouches': 'Reading the record…',
  'opp.touchesFailed': 'The record could not be read.',
  'opp.nothingRecorded': 'Nothing recorded yet — the first note starts it.',
  'opp.channel.site_visit': 'Site visit',
  'opp.channel.phone': 'Phone',
  'opp.channel.email': 'Email',
  'opp.channel.sms': 'SMS',
  'opp.channel.wechat': 'WeChat',
  'opp.channel.other': 'Other',
  'opp.touchSaved': 'Recorded on the customer. Nothing was sent.',
  'opp.nextSaved': 'Next step scheduled.',
  'opp.needsApproval': 'Waiting for your approval in EmmA Desk.',
  'opp.notConfirmed': 'The outcome could not be confirmed — retry the same details safely.',
  'opp.plan': 'Plan for iPad tracing',
  'opp.planOnJob': 'This opportunity is now linked to a Job. Continue drawing from the Job; the original opportunity files are retained.',
  'opp.planNote': 'Reference {ref} stays with this plan, tracing and Proposal.',
  'opp.planUploadNote': 'Upload the original PNG, JPG or PDF. Dimensions and tracing are recorded on iPad; customer plan dimensions remain unverified.',
  'opp.planReserved': 'Reserved original: {name}. Retry this same file if the upload was interrupted.',
  'opp.plansFailed': 'Plans could not be loaded.',
  'opp.groundFloor': 'Ground floor',
  'opp.firstFloor': 'First floor',
  // Plans held outside the gf/1f slots (merged Opportunities, raw plans).
  'opp.otherPlans': 'Other plans',
  'opp.otherPlan': 'Plan file: {name}',
  'opp.openProposal': 'Open Proposal',
  'opp.prepareProposal': 'Prepare Proposal',
  'opp.proposalQuiet': 'The Proposal is the priced document. What you record above is the record behind it.',
  'opp.nextAction': 'Next action',
  'opp.nextJob': 'Open the accepted job',
  'opp.nextJobNote': 'The opportunity is accepted. Continue with scheduling, materials and delivery on the job.',
  'opp.nextProposalNote': 'Start with the priced Proposal, or book a measure below. Secondary recordkeeping stays out of the way until you need it.',
  'opp.contactAndSite': 'Contact & site details',
  'opp.activityAndFollowUp': 'Activity & follow-up',
  'opp.plansAndFiles': 'Plans, proposal history & files',
  'opp.retry': 'Retry',
  'opp.uploading': 'Uploading and verifying…',

  // ── Artemis Warehouse Inventory ──────────────────────────────────────────
  'inv.kicker': 'Stock',
  'inv.title': 'Artemis Warehouse Inventory',
  'inv.lede': 'What is on the shelf, what is committed to a job, what is on order, and what is honestly available.',
  'inv.meta': 'SKU · product · location · category · UOM · on hand · committed · ordered · available',
  'inv.search': 'Search SKU, product or description…',
  'inv.allLocations': 'All locations',
  'inv.shortOnly': 'Short only',
  'inv.shortTitle': '{count} line(s) cannot cover what is committed',
  'inv.by': 'by {qty}',
  'inv.col.sku': 'Artemis SKU',
  'inv.col.supplierSku': 'Supplier SKU',
  'inv.col.product': 'Product',
  'inv.col.location': 'Location',
  'inv.col.category': 'Category',
  'inv.col.uom': 'UOM',
  'inv.col.onHand': 'On hand',
  'inv.col.committed': 'Committed',
  'inv.committedHint': 'Committed to a job — click the number for the jobs',
  'inv.col.ordered': 'Ordered',
  'inv.col.available': 'Available',
  'inv.availableHint': 'On hand minus committed. Negative means material is committed that is not on the shelf.',
  'inv.col.job': 'Job',
  'inv.col.qty': 'Qty',
  'inv.col.when': 'When',
  'inv.col.kind': 'Movement',
  'inv.col.ref': 'Reference',
  'inv.none': 'No stock is recorded yet — receive stock to start the ledger.',
  'inv.footnote': 'Available = on hand − committed. Every number here is derived from the movement ledger, so a correction is a movement, not an edit.',
  'inv.intel': 'Where this number comes from',
  'inv.commitments': 'Committed to',
  'inv.movements': 'Movements',
  'inv.noCommitments': 'Nothing committed.',
  'inv.noMovements': 'No movements recorded.',
  'inv.rackTitle': 'Carpet racks',
  'inv.rackMeta': 'A and B · 3 levels × 3 positions',
  'inv.levelPos': 'level {level} · pos {position}',
  'inv.rackFootnote': 'General Storage and the Trims Rack are separate locations. Click a slot to filter the list to it.',
  'inv.noSupplierSku': 'No supplier SKU recorded for this item',
  'inv.unavailable': 'Inventory could not be read: {message}',
  'inv.loading': 'Reading movements…',

  // ── the installer desk ───────────────────────────────────────────────────
  'crew.add': 'Add a crew',
  'crew.addTitle': 'Add a crew to the roster',
  'crew.edit': 'Edit this crew',
  'crew.editTitle': 'Edit {name}',
  'crew.name': 'Crew name',
  'crew.kind': 'Crew type',
  'crew.internal': 'Artemis crew',
  'crew.external': 'Subcontractor',
  'crew.mobile': 'Mobile (their portal login)',
  'crew.needPhone': 'Enter the mobile number this person will use to sign in.',
  'crew.accountName': 'Login name (optional; defaults to crew name)',
  'crew.accountRole': 'Account role',
  'crew.accountWorker': 'Installer',
  'crew.accountManager': 'Team manager — does not install',
  'crew.phoneNote': 'The mobile is how the crew signs in: the portal texts them a code.',
  'crew.priceBasis': 'Price basis',
  'crew.basisList': 'Our list rates',
  'crew.basisCustom': 'Their own rates',
  'crew.labourMode': 'How we pay them',
  'crew.labour.team_artemis_zero': 'Artemis crew — no labour fee',
  'crew.labour.catalog_lab_cost': 'Catalogue labour cost',
  'crew.labour.declared_rates': 'Their declared rates',
  'crew.abn': 'ABN',
  'crew.bankName': 'Account name',
  'crew.bsb': 'BSB',
  'crew.account': 'Account number',
  'crew.paymentPreference': 'Paid by',
  'crew.pay.unset': '— not recorded',
  'crew.pay.cash': 'Cash',
  'crew.pay.transfer': 'Bank transfer',
  'crew.categories': 'What they lay',
  'crew.categoriesNote': 'Tick what this crew installs. The list is what the catalogue actually sells.',
  'crew.categoriesLoading': 'Reading the catalogue…',
  'crew.languages': 'Language they work in',
  'crew.lang.en': 'English',
  'crew.lang.zh': 'Chinese (中文)',
  'crew.lang.en+zh': 'Both — English and Chinese',
  // The crew's own calendar. The address is the capability; the acts are mint / rotate / revoke.
  'crew.calendar': 'Their calendar',
  'crew.calendarWhy': 'A link they can add to their phone once — it keeps itself up to date with every crew-day we book.',
  'crew.calendarNone': 'No calendar address yet.',
  'crew.calendarIssue': 'Create the calendar link',
  'crew.calendarRotate': 'Replace the link',
  'crew.calendarRotateWhy': 'The old link stops working immediately. Do this if it went to the wrong person.',
  'crew.calendarRevoke': 'Stop the link',
  'crew.calendarRevoked': 'Stopped {date} — the link in their phone no longer works.',
  'crew.calendarIssued': 'Issued',
  'crew.calendarRotated': 'Replaced',
  'crew.calendarExpires': 'Expires',
  'crew.calendarNoExpiry': 'never — it keeps working until you stop it',
  'crew.calendarToken': 'Token',
  'crew.calendarCopy': 'Copy the link',
  'crew.calendarCopied': 'Link copied',
  'crew.calendarGoogle': 'Add to Google Calendar',
  'crew.calendarPaste': 'Or paste this into Apple or Outlook Calendar:',
  'crew.calendarNeverRead': 'No phone has fetched it yet — it is not subscribed until it is opened.',
  'crew.calendarRead': 'Last fetched {date} ({count}×).',
  'crew.calendarBusy': 'Working…',
  'crew.calendarFailed': 'The link could not be changed: {message}',
  'crew.archiveReason': 'Why archive them',
  'crew.archivePlaceholder': 'no longer working with them · replaced by …',
  'crew.archive': 'Archive',
  'crew.saving': 'Saving…',
  'crew.save': 'Save the crew',
  'crew.needName': 'A crew needs a name.',
  'crew.needReason': 'Say why they are being archived.',
  'crew.added': '{name} is on the roster. Add their mobile and they can sign in.',
  'crew.saved': '{name} updated.',
  'crew.archived': '{name} archived — they stop appearing for scheduling, and the days they worked keep their name.',
  'crew.pending': 'Sent for approval — nothing changes until it is released.',
  'crew.refused': 'Not saved: {reason}',
  'crew.failed': 'Not saved: {message}',
  'inst.kicker': 'Installers',
  'inst.title': 'Installers',
  'inst.lede': 'Who we use, what they charge, and what we have actually paid them.',
  'inst.meta': 'name · code · crew · rates · language · jobs paid · paid · payments · last',
  'inst.summary': '{total} on the desk · {subs} subcontractors',
  'inst.col.name': 'Installer',
  'inst.col.crew': 'Crew',
  'inst.col.rates': 'Rate on the party',
  'inst.col.ratesHint': 'Rates that live on the installer themselves rather than in a price list',
  'inst.col.account': 'Portal account',
  'inst.col.language': 'Language',
  'inst.col.unitRate': 'Rate',
  'inst.col.fee': 'Fee',
  'inst.col.basis': 'Basis',
  'inst.payable': 'Payable {amount} ex GST',
  'inst.feeComplete': 'fee complete',
  'inst.editFeesOnJob': 'Edit the fee lines on the Job card',
  'inst.feeConfirmed': 'Confirmed {date} · {total} · text {state}',
  'inst.feeConfirmedChanged': 'lines changed since',
  'inst.feeNotConfirmed': 'Not yet confirmed to the crew',
  'inst.col.profile': 'Profile',
  'inst.col.dayRate': 'Min day (1 crew)',
  'inst.dayRateHint': 'The minimum daily rate condition — what a short day still costs',
  'inst.col.phone': 'Phone',
  'inst.col.abn': 'ABN',
  'inst.col.bank': 'Bank',
  'inst.col.priceBasis': 'Price basis',
  'inst.col.paidBy': 'Paid by',
  'inst.col.categories': 'Categories',
  'inst.col.category': 'Category',
  'inst.col.version': 'Ver',
  'inst.col.effective': 'Effective',
  'inst.complete': 'complete',
  'inst.gaps': '{count} to capture',
  'inst.notRecorded': 'not recorded',
  'inst.notAgreed': 'not agreed',
  'inst.list': 'price list',
  'inst.custom': 'custom / project',
  'inst.col.jobs': 'Jobs',
  'inst.col.jobsHint': 'Jobs that owe this crew a fee: scheduled to them, or holding an agreed fee',
  'inst.col.payable': 'Payable',
  'inst.col.payableHint': 'Installer fees across those Jobs, ex GST — recorded, proposed from the rate book, or agreed extra work. Open the crew for the lines.',
  'inst.col.paidHint': 'Payments recorded on the old platform',
  'inst.col.paid': 'Paid',
  'inst.col.last': 'Last',
  'inst.col.date': 'Date',
  'inst.col.job': 'Job',
  'inst.col.amount': 'Amount',
  'inst.col.reference': 'Reference',
  'inst.col.confirmed': 'Confirmed',
  'inst.noRates': 'no list or rule yet',
  'inst.none': 'No installer is on the desk yet.',
  'inst.loading': 'Reading installers…',
  'inst.unavailable': 'Installers could not be read: {message}',
  'inst.gap': 'Only a missing portal mobile needs attention. ABN and bank details are optional office records.',
  'inst.back': 'All installers',
  'inst.ledger': 'Labour ledger',
  'inst.ledgerMeta': 'every scheduled crew-day and every fee paid, linked to its job',
  'inst.ledgerNote': 'Crew-days are work records, including internal Artemis crews. Median external fee per job {median} · {twice} job(s) paid more than once. "Owed" is not recorded, so it is not stated.',
  'inst.allocated': 'allocation',
  'inst.paymentRow': 'payment',
  'inst.crewDay': 'crew-day',
  'inst.dayScheduled': 'scheduled',
  'inst.dayCompleted': 'completed',
  'inst.confirmed': 'confirmed',
  'inst.unconfirmed': 'not confirmed',
  'inst.rateBook': 'Rate card',
  'inst.col.status': 'Status',
  'inst.rateNone': 'No rate card yet.',
  'inst.rateNoneArtemis': 'Artemis crew — we pay no labour fee, so there is no rate card to agree.',
  'inst.rateNoneNote': 'A rate card has to be created and approved before this crew can be paid from one. The catalogue\'s own activity list is not a rate card — it is only where one would be built from, and it is not shown here as though it were agreed.',
  'inst.rateNotApproved': 'not approved',
  'inst.rateNotApprovedNote': 'Submitted, and not approved. Nothing here is agreed until the office approves it.',
  'inst.rateApproved': 'approved',
  'inst.rateApprovedNote': 'Office-approved for job costing. Contractor confirmation remains a separate fact; a later rate change needs a new version.',
  'inst.approvalRecord': 'Office approval: {date} · {by} · {note}',
  'inst.approveExactVersion': 'Approve v{version} rates',
  'inst.approvalHelp': 'Office approval applies only to the rates shown in this version. Unquoted services and day minimums stay unknown.',
  'inst.approvalEvidence': 'Office accepted the listed rates for job costing; unquoted services and day minimums remain unconfirmed.',
  'inst.approvalSaving': 'Approving…',
  'inst.approvalSaved': 'This rate-card version is approved for job costing.',
  'inst.approvalPending': 'Approval is processing.',
  'inst.approvalFailed': 'Could not approve this rate-card version.',
  'inst.rateBookMeta': 'registered rates for this crew, with submitted and approved versions visible',
  'inst.jobPricing': 'Rates against Job work',
  'inst.jobPricingMeta': 'accepted work assigned to this crew, compared with their current registered rate card',
  'inst.jobPricingNone': 'No Artemis Job work is assigned to this crew yet.',
  'inst.jobPricingNote': 'Matched subtotal is labour only, excluding GST. One accepted scope is priced once even when the crew attends on several days. Ambiguous or missing rates remain unresolved.',
  'inst.scopeGapTitle': 'Proposal items outside the accepted Job scope',
  'inst.scopeGapHelp': 'These items appear in a published Proposal but not in the accepted Job quote or assigned crew-day. A material quantity is not an installation labour quantity. Check the customer agreement and measure the work before assigning or costing it.',
  'inst.openProposal': 'Open Proposal',
  'inst.openJobDays': 'Open Job days',
  'inst.col.work': 'Job work',
  'inst.col.qty': 'Qty',
  'inst.col.registeredRate': 'Registered rate',
  'inst.col.expected': 'Expected labour',
  'inst.matchedSubtotal': 'Matched subtotal {amount}',
  'inst.unresolvedCount': '{count} unresolved',
  'inst.rateMatched': 'matched',
  'inst.quantityMissing': 'quantity needs review',
  'inst.unitMismatch': 'unit needs review',
  'inst.rateAmbiguous': 'choose rate',
  'inst.rateMissing': 'rate missing',
  'inst.rateUnmapped': 'work not mapped',
  'inst.rateIncluded': 'included in floor rate',
  'inst.rateCandidate': '{activity} · {rate}/{uom} · {status}',
  'inst.rateLines': '{count} registered lines',
  'inst.pdfRegistered': 'PDF registered',
  'inst.formRegistered': 'form submission',
  'inst.sourceDocument': 'Source document',
  'inst.sourceFingerprint': 'SHA-256 {hash}',
  'inst.originalUnavailable': 'The rates and fingerprint are registered; no retrievable PDF copy is stored.',
  'inst.rateTerms': 'Conditions',
  'inst.ratePerUnit': '{rate} / {uom}',
  'inst.assistedOpen': 'Enter rates for this crew',
  'inst.assistedTitle': 'Artemis-assisted service pricing',
  'inst.assistedHelp': 'Office staff or the company manager can enter this table. Blank services have no quoted rate yet; this creates a new submitted version, never an approved fee.',
  'inst.assistedScope': 'Services this crew can install',
  'inst.assistedService': 'Service',
  'inst.assistedUnit': 'Unit',
  'inst.assistedRate': 'Rate ex GST',
  'inst.assistedOneCrew': 'Minimum day rate — 1 crew',
  'inst.assistedTwoCrew': 'Minimum day rate — 2 crew',
  'inst.assistedEffective': 'Effective from',
  'inst.assistedMinimumHelp': 'Leave both day-minimum fields blank if they have not been quoted. Never enter zero as a substitute.',
  'inst.assistedSource': 'Who provided these rates and what remains to confirm',
  'inst.assistedGst': 'I confirm the entered rates are ex GST',
  'inst.assistedSubmit': 'Save submitted rate version',
  'inst.assistedSaving': 'Saving…',
  'inst.assistedSaved': 'Submitted rate version saved. It is not yet approved.',
  'inst.assistedPending': 'Rate submission is pending review.',
  'inst.assistedFailed': 'Could not submit rates.',
  'inst.assistedNotApproved': 'This is office evidence, not an approved payable agreement. Confirm unresolved scope and terms before using it for a Job.',
  'inst.profile': 'Profile',
  'inst.missingTitle': 'Portal mobile missing',
  'inst.missingNote': 'Add a mobile so this crew can sign in to the installer portal.',

  // ── purchase orders (card AIW-PO) ─────────────────────────────────────────
  'po.title': 'Purchase orders',
  'po.lede': 'What has been ordered, what has come back, and what still has to land before a crew arrives.',
  'po.find': 'Find a PO',
  'po.findPlaceholder': 'PO, job, supplier or suburb',
  'po.showClosed': 'Show received and cancelled',
  'po.count': '{shown} of {total} shown',
  'po.col.po': 'PO',
  'po.col.job': 'Job',
  'po.col.supplier': 'Supplier',
  'po.col.status': 'Status',
  'po.col.sent': 'Sent',
  'po.col.delivered': 'Delivered',
  'po.col.lines': 'Lines',
  'po.col.total': 'Total ex GST',
  'po.none': 'No purchase order matches that.',
  'po.unavailable': 'Purchase orders could not be read: {message}',
  'po.loading': 'Reading purchase orders…',
  'po.source.platform': 'platform',
  'po.source.crm': 'CRM',
  'po.notRecorded': 'not recorded',
  'po.mustLand': 'must land',
  'po.mustLandNote': 'sent {sent}, nothing acknowledged, installs {install}',
  'po.detail': 'Purchase order',
  'po.detail.supplier': 'Supplier',
  'po.detail.deliverTo': 'Deliver to',
  'po.detail.sent': 'Sent',
  'po.detail.acknowledged': 'Acknowledged',
  'po.detail.promised': 'Promised',
  'po.detail.delivered': 'Delivered',
  'po.detail.installDays': 'Install days',
  'po.detail.email': 'Orders email',
  'po.detail.creditTerms': 'Credit terms',
  'po.detail.legacyNote': 'This is an existing platform order. Supplier evidence and collection records are registered here without replacing the original order.',
  'po.line.item': 'Line',
  'po.line.forJob': 'For job',
  'po.line.toStock': 'Into warehouse stock',
  'po.line.ordered': 'Ordered',
  'po.line.required': 'Take-off required',
  'po.line.unitCost': 'Unit cost',
  'po.line.total': 'Line',
  'po.line.check': 'Ordered vs required',
  'po.line.covers': 'covers take-off {percent}%',
  'po.line.short': 'short {amount}',
  'po.line.over': 'over by {percent}% — check waste',
  'po.line.unknown': 'no take-off figure',
  'po.noAmount': 'no amount recorded',
  'po.verify.title': 'Ordered vs required',
  'po.verify.note': 'Only a shortfall or more than the waste ceiling is flagged. Missing take-off quantities remain unverified.',
  'po.noneYet': 'No purchase order has been raised for this job yet.',
  'job.materials.title': 'Materials due vs schedule',
  'job.materials.none': 'No purchase order is attached to this job yet, so nothing can be verified. Record what the supplier said when it lands.',
  'job.materials.verify': 'Material status must be verified against the purchase order.',
  'job.materials.viewPo': 'Open the purchase order',

  // ── active Job skirting ─────────────────────────────────────────────────
  'skirting.panelTitle': 'Skirting',
  'skirting.panelMeta': 'accepted scope · fulfilment',
  'skirting.loading': 'Loading skirting scope…',
  'skirting.rule': 'Use accepted scope and approved assessment evidence only. A customer purchase allowance never changes labour.',
  'skirting.quoteDetected': 'Accepted quote detected: {quote}. Review whether Artemis supplies the skirting, the $19/$20 rate and the included height below.',
  'skirting.linkUnlock': 'Record the accepted scope first. That unlocks Create customer URL immediately below this form.',
  'skirting.ready': 'Ready — record this scope to activate the standalone customer URL.',
  'skirting.missing.summary': 'Still needed: {items}',
  'skirting.missing.acceptedEvidence': 'accepted quote or Proposal evidence',
  'skirting.missing.net': 'a valid measured net LM',
  'skirting.missing.measureSource': 'measurement source',
  'skirting.missing.measureEvidence': 'measurement evidence',
  'skirting.missing.height': 'height for the selected profile',
  'skirting.missing.confirmedBy': 'who confirmed the profile',
  'skirting.saveFailed': 'The skirting scope was not recorded. Review the message above and try again.',
  'skirting.scope': 'Scope',
  'skirting.scope.supply': 'Artemis supplies and installs',
  'skirting.scope.install': 'Customer supplies, Artemis installs',
  'skirting.source': 'Accepted source',
  'skirting.source.proposal': 'Accepted Proposal',
  'skirting.source.quote': 'Accepted quote',
  'skirting.source.legacy': 'Reviewed migrated Job',
  'skirting.evidence': 'Evidence',
  'skirting.evidence.placeholder': 'Proposal/version or reviewed quote',
  'skirting.net': 'Measured net LM',
  'skirting.net.placeholder': 'Missing until approved',
  'skirting.measureSource': 'Measurement source',
  'skirting.measureSource.placeholder': 'Approved assessment',
  'skirting.measureEvidence.placeholder': 'File + field/version',
  'skirting.profileHeight': 'Profile and height',
  'skirting.profileRule': 'Leave blank for “to confirm before ordering”. Do not fabricate a customer choice.',
  'skirting.height': 'Height',
  'skirting.confirmedBy': 'Confirmed by',
  'skirting.channel.inPerson': 'In person',
  'skirting.channel.phone': 'Phone',
  'skirting.channel.link': 'Customer link',
  'skirting.channel.other': 'Other',
  'skirting.guide': 'Optional buying guide',
  'skirting.guide.math': '{net} LM net + {extra} LM ({pct}%) = {recommended} LM recommended.',
  'skirting.guide.pieces': '{pieces} × {length} m verified stock pieces = {total} LM.',
  'skirting.noMeasure': 'No approved net length: unresolved, never guessed from perimeter.',
  'skirting.correct': 'Record correction',
  'skirting.record': 'Record skirting scope',
  'skirting.revision': 'Revision {version} · recorded by {actor}. Earlier revisions remain in audit history.',
  'skirting.savedCorrection': 'Skirting correction recorded',
  'skirting.savedScope': 'Skirting scope recorded',
  'skirting.discountPct': 'Discount % (own line under this one)',
  'skirting.roundDown': 'Round the quote down to $',
  'skirting.discount.help': 'The line keeps its quantity and rate. A discount shows as its own line beneath it; the round-down goes on the rounding line at the bottom.',
  'skirting.acceptedRate': 'Accepted quote rate',
  'skirting.includedBand': 'Height included in that rate',
  'skirting.band.standard': '66–67 mm · included',
  'skirting.band.medium': '92 mm · included',
  'skirting.band.tall': '138–140 mm · included',
  'skirting.acceptedBasis': 'This is the accepted quote basis. Other heights appear to the customer only as the difference from it.',
  'skirting.guide.title': 'Skirting guide — PDF by email (the default)',
  'skirting.guide.supplyHelp': 'Prints each profile drawn to scale beside its installed photo, marks the height the accepted quote includes and shows every other height as the difference only. The customer replies in words; record the choice above with channel Email.',
  'skirting.guide.installHelp': 'Prints the same drawings as a buying guide with the approved net length and allowance. No Artemis supply price appears.',
  'skirting.quote.applied': 'Added to the accepted quote by itself: {lm} LM at {rate}/LM = {amount} + GST, new quote {quote}. The customer confirmation is the acceptance evidence.',
  'skirting.quote.skipped': 'Recorded, but not added to the quote: {reason}',
  'skirting.noneQuoted': 'No skirting on this Job\'s quote. Door architrave can still be offered below.',
  'skirting.architrave.title': 'Door architrave — a separate document',
  'skirting.architrave.help': 'Prints the price per door (one side or both, by width and thickness, any profile) and every profile drawn close up at the mitred corner. Needs only the Job and its customer email.',
  'skirting.architrave.send': 'Email the door architrave guide (PDF)',
  'skirting.architrave.drafted': 'Door architrave guide drafted',
  'skirting.architrave.record': 'Record a confirmation received by text, phone or in person — it goes onto the accepted quote by itself',
  'skirting.architrave.recordAction': 'Record and add to the quote',
  'skirting.architrave.recorded': 'Door architrave recorded',
  'skirting.architrave.profile': 'Profile',
  'skirting.architrave.size': 'Size',
  'skirting.architrave.applied': 'Added to the accepted quote by itself: {doors} doors at {rate} per door = {amount} + GST, new quote {quote}.',
  'skirting.architrave.answer': 'Customer chose online ({who}): {what} · {total}',
  'skirting.architrave.one': 'one side',
  'skirting.architrave.both': 'both sides',
  'skirting.architrave.doors': 'doors',
  'skirting.architrave.customerSupplies': 'customer supplies the boards',
  'skirting.architrave.artemisSupplies': 'Artemis supplies',
  'skirting.guide.urlToo': 'The email also carries a private URL for choosing online. Sending again makes a new URL; an earlier one keeps working until you revoke it.',
  'skirting.guide.language': 'Email wording',
  'skirting.guide.send': 'Email the skirting guide (PDF)',
  'skirting.guide.drafted': 'Skirting guide drafted',
  'skirting.guide.review': 'Draft saved with the PDF attached. Review and send it from Communications — nothing has been sent yet.',
  'skirting.guide.open': 'Open Communications',
  'skirting.link.alternative': 'Alternative: standalone customer URL',
  'skirting.link.title': 'Standalone customer link',
  'skirting.link.supplyHelp': 'Customer chooses an Artemis package profile and height. The link shows the included height and exact +$4/+5 per LM difference.',
  'skirting.link.installHelp': 'Customer sees an optional buying guide and records whether they will supply new, existing, or not-yet-decided skirting. No supply price is shown.',
  'skirting.link.create': 'Create customer URL',
  'skirting.link.replace': 'Create replacement URL',
  'skirting.link.revoke': 'Revoke URL',
  'skirting.link.copy': 'Copy',
  'skirting.link.copyNow': 'Copy this URL now. Only its secure hash is stored, so the same plaintext URL cannot be recovered later.',
  'skirting.link.status': 'Latest link: {status}',
  'skirting.link.created': 'Customer skirting URL created',
  'skirting.link.revoked': 'Customer skirting URL revoked',

  // ── staff records acceptance received outside the customer portal ───────
  'proposal.accept.title': 'Record acceptance',
  'proposal.accept.loading': 'Loading acceptance…',
  'proposal.accept.unavailable': 'Acceptance record is unavailable.',
  'proposal.accept.releaseFirst': 'Release a priced customer version before recording acceptance.',
  'proposal.accept.rule': 'The customer already gave the go-ahead. Choose how they confirmed, then accept released version {version}.',
  'proposal.accept.recorded': 'Proposal accepted',
  'proposal.accept.jobCreated': 'Job {ref} has been created.',
  'proposal.accept.jobProcessing': 'Job creation is being completed.',
  'proposal.accept.channel': 'Method',
  'proposal.accept.sms': 'SMS',
  'proposal.accept.phone': 'Phone',
  'proposal.accept.inPerson': 'In person',
  'proposal.accept.email': 'Email',
  'proposal.accept.confirm': 'Record acceptance & create Job',
  'proposal.accept.saved': 'Proposal accepted at {amount}. Job {ref} is in Pending Schedule.',

  // ── the words the engine sends a customer, by exact English match ─────────
  'engine.notice.selectColour': 'Final price depends on your selected colour.',
  'engine.notice.promotion': 'Promotional pricing — contact us to confirm availability.',
  "po.office.office": "PO Office",
  "po.office.findJob": "Find a Job",
  "po.office.findJobHint": "Customer, address or Job reference",
  "po.office.onlyMissing": "No PO yet only",
  "po.office.completedJobs": "Include fully paid Jobs",
  "po.office.chooseJob": "Choose a Job",
  "po.office.noPo": "No PO yet",
  "po.office.existingCount": "{count} existing POs",
  "po.office.missingCount": "{count} Jobs have no PO yet",
  "po.office.jobListFailed": "Could not load all Jobs and orders. Refresh before relying on PO status.",
  "po.office.noJobs": "No Jobs match these filters.",
  "po.office.loadingPlan": "Loading the saved material plan…",
  "po.office.noMaterials": "This saved quote has no supplier material groups to order.",
  "po.office.linkedOrders": "Linked purchase orders",
  "po.office.draft": "Prepare from accepted Job",
  "po.office.job": "Job reference",
  "po.office.supplier": "Supplier ID",
  "po.office.required": "Required by",
  "po.office.requiredHint": "{days} working days before installation on {date}",
  "po.office.noInstallDate": "No installation date yet. Choose the date.",
  "po.office.deliveryPickup": "Pickup (we collect)",
  "po.office.deliveryWarehouse": "Deliver to {label}",
  "po.office.deliveryCustom": "Deliver to another address",
  "po.office.customAddress": "Delivery address",
  "po.materials.fromFloorArea": "Quantity from the quoted floor area; the Proposal saved none for this row.",
  "po.office.destination": "Delivery address / pickup location",
  "po.office.prepare": "Read saved material plan",
  "po.materials.hint": "Resolved materials are preselected. Review quantities and handle only the exceptions below.",
  "po.materials.coverage": "Already covered by purchase orders",
  "po.materials.coverageHint": "Check existing orders before adding more. These lines are not preselected again.",
  "po.materials.coveredLines": "{count} covered lines",
  "po.materials.ready": "Ready to order",
  "po.materials.exceptions": "Needs attention · {count}",
  "po.materials.exceptionHard": "Resolve before ordering",
  "po.materials.exceptionReview": "Review",
  "po.exception.unquoted": "Measured work is not on the accepted Proposal; confirm it before ordering.",
  "po.exception.missingVariant": "Choose and confirm the exact supplier product and variant.",
  "po.exception.missingPrice": "A current supplier purchase price is required.",
  "po.exception.missingUnit": "A supplier purchase unit is required.",
  "po.exception.invalidQuantity": "Enter a valid positive order quantity.",
  "po.exception.missingMeasurement": "The required measurement is missing.",
  "po.exception.review": "Review this material before ordering.",
  "po.exception.existingCoverage": "An existing order already covers this material.",
  "po.exception.acceptedSnapshotQuantity": "The accepted material purchase quantity is unresolved.",
  "po.exception.acceptedSnapshotLine": "The accepted material no longer matches one current order line.",
  "po.exception.acceptedSnapshotUnit": "Check the accepted material against the supplier purchase unit.",
  "po.office.deliveryTiming": "When is this order needed?",
  "po.office.timing.required_by": "Required by a date",
  "po.office.timing.asap": "As soon as possible",
  "po.office.timing.hold": "On hold — do not send",
  "po.office.holdSendBlocked": "This order is on hold. Change its timing before sending.",
  "po.materials.source": "Working Proposal v{version} and latest saved scoped assessment. Customer acceptance and pricing stay as recorded.",
  "po.materials.floorStairs": "Flooring {floor} m² + Stairs {stairs} m² = {total} m²",
  "po.materials.floorStairsSteps": "Flooring {floor} m² + Stairs {steps} × {rate} m² ({stairs} m²) = {total} m²",
  "po.materials.required": "Required quantity",
  "po.materials.assessment": "Latest scoped assessment",
  "po.materials.unquoted": "Measured on the plan, not on the Proposal",
  "po.materials.proposal": "Saved Proposal / quote",
  "po.materials.savedQuantity": "Previously saved quantity",
  "po.materials.floorOverage": "Order quantity includes {pct}% extra floor; unused full boxes go back",
  "po.materials.floorSpare": "Order quantity adds {extra} m² spare floor on top of the quoted area and wastage, then whole boxes.",
  "po.materials.supplierItem": "Supplier product and specification",
  "po.materials.choose": "Choose supplier option",
  "po.materials.orderQuantity": "Order quantity",
  "po.materials.stockLength": "{length} m per stock piece",
  "po.materials.cartons": "{count} ctn × {size} {unit} = {total} {unit}",
  "po.materials.freeStock": "{quantity} {unit} free stock; not automatically deducted.",
  "po.materials.unpriced": "Supplier product or purchase price is missing. This requirement is kept in the list.",
  "po.materials.ambiguousFloor": "This accessory covers different flooring products, variants or colours. Confirm its floor scope and supplier item before ordering.",
  "po.materials.incomplete": "Choose a priced supplier item and enter an order quantity.",
  "po.materials.barrier": "Usually installer sourced. Select only if Artemis is ordering this supply.",
  "po.materials.runs": "Show {count} measured runs",
  "po.materials.selected": "{count} selected items · ${total} EX GST",
  "po.materials.orderReason": "Additional selected materials for the Job",
  "po.materials.saveSelected": "Save selected items — {supplier}",
  "po.materials.target": "Save into",
  "po.materials.newOrder": "New additional PO",
  "po.materials.existingDraft": "Existing draft {id} · v{version}",
  "po.materials.openSaved": "Open saved PO",
  "po.materials.refresh": "The materials service is updating. Refresh this page before saving an order.",
  "po.office.create": "Save draft",
  "po.office.additional": "Reason for an additional order",
  "po.office.stock": "Available stock must be allocated before reducing order quantities.",
  "po.office.pickup": "Pickup reference",
  "po.office.provisional": "Provisional — awaiting official email",
  "po.office.conflict": "Sources disagree — review before collection",
  "po.office.register": "Register supplier evidence",
  "po.office.kind": "Evidence type",
  "po.office.value": "Reference / date / detail",
  "po.office.channel": "Source channel",
  "po.office.manual": "Office record",
  "po.office.email": "Email",
  "po.office.wechat": "WeChat",
  "po.office.mailbox": "Mailbox",
  "po.office.message": "Message ID",
  "po.office.attachment": "Attachment filename",
  "po.office.note": "Reason / source detail",
  "po.office.save": "Save evidence",
  "po.office.history": "Evidence history",
  "po.office.referenceNote": "A pickup number does not mean paid, ready or collected.",
  "po.office.pickup_reference": "Pickup reference",
  "po.office.invoice_reference": "Invoice reference",
  "po.office.confirmation": "Supplier confirmation",
  "po.office.promised_date": "Promised date",
  "po.office.ready": "Ready for collection",
  "po.office.collected": "Collected / received",
  "po.office.supersedes": "Correct previous evidence",
  "po.office.new": "New evidence",
  "po.office.copied": "Copied",
  "po.office.copy": "Copy",
  "po.office.saved": "Saved",
  "po.office.pending": "Review is waiting below; it has not executed yet.",
  "po.office.failed": "Could not save: {message}",
  "po.office.busy": "Working…",
  "po.office.reviews": "Order reviews",
  "po.office.edit": "Revise draft",
  "po.office.reason": "Why is this changing?",
  "po.office.quantity": "Quantity",
  "po.office.cost": "Unit cost",
  "po.office.recipient": "Supplier ordering email",
  "po.office.cc": "CC emails, comma separated",
  "po.office.reviewSend": "Send purchase order",
  "po.office.subject": "Email subject",
  "po.office.requiredAsap": "Leave empty to ship as soon as possible.",
  "po.office.emailPreview": "The email that will be sent",
  "po.office.to": "To",
  "po.office.ccShort": "CC",
  "po.office.pdfShow": "Show the purchase-order PDF",
  "po.office.pdfLoading": "Drawing the PDF…",
  "po.office.pdfTitle": "Purchase-order PDF preview",
  "po.office.pdfOpen": "Open the PDF in a new tab",
  "po.office.pdfUnavailable": "The PDF preview is unavailable just now.",
  "po.office.pdfFirst": "Show the PDF before sending",
  "po.office.body": "Message to supplier",
  "po.office.sendNote": "Saving a draft does not send it. Check the exact order and email, then press Send. That press is your approval; nobody is asked again.",
  "po.office.inbox": "Supplier replies to review",
  "po.office.link": "Link reply to order",
  "po.office.selectOrder": "Choose purchase order",
  "po.office.emptyInbox": "No supplier replies need review.",
  "po.office.supply": "Supply",
  "po.office.refreshing": "Refreshing",
  "po.office.mustLandCount": "{count} orders need materials before installation",
  "po.office.yes": "Yes",
  "po.office.packs": "{count} packs",
  "po.office.lines": "{count} lines",
  "po.office.status.draft": "Draft",
  "po.office.status.approved": "Approved",
  "po.office.status.sent": "Sent",
  "po.office.status.acknowledged": "Acknowledged",
  "po.office.status.paid": "Paid",
  "po.office.status.delivered": "Delivered",
  "po.office.status.received": "Received",
  "po.office.status.cancelled": "Cancelled",
  'po.office.no': 'No',
"po.office.cancelDelivery":"Request delivery cancellation",
"po.office.checkDelivery":"Check saved message again",
  'po.office.saveFirst': 'Save changes before reviewing',
  'calendar.busy': 'Busy',
  'calendar.allDay': 'All day',
  'calendar.actionNew': "New appointment",
  'calendar.actionOpen': "Open appointment",
  'calendar.appointmentEditor': "Appointment · Sydney time",
  'calendar.actionTitle': "Short title",
  'calendar.actionLocation': "Location",
  'calendar.actionStart': "Starts · Sydney",
  'calendar.actionEnd': "Ends · Sydney",
  'calendar.actionNotes': "Notes",
  'calendar.actionCheck': "Check saved request",
  'calendar.actionSave': "Save appointment",
  'calendar.actionCancel': "Cancel appointment",
  'calendar.actionClose': "Close",
  'calendar.actionChecking': "Confirming the saved calendar record…",
  'calendar.actionSaved': "Saved and reopened in Jacky’s calendar.",
  'calendar.actionError': "The save could not be verified. Reopen this form to check the same request before trying again.",
  'calendar.subscribeInstall': "Subscribe to installations",
  'calendar.kicker': 'Work',
  'calendar.title': 'Calendar',
  'calendar.lede': 'Installations, site quotations and other appointments · Sydney time',
  'calendar.internal': 'Internal crew',
  'calendar.external': 'External crew',
  'calendar.unassigned': 'Crew unconfirmed',
  'calendar.staff': 'Artemis staff mobile',
  'calendar.mixed': 'Mixed installers',
  'calendar.siteQuotation': 'Site quotation',
  'calendar.otherVisit': 'Other visit / appointment',
  'calendar.previous': 'Previous {mode}',
  'calendar.next': 'Next {mode}',
  'calendar.today': 'Today',
  'calendar.month': 'Month',
  'calendar.week': 'Week + next week',
  'calendar.view': 'Calendar view',
  'calendar.legend': 'Calendar colour legend',
  'calendar.monthGrid': 'Month calendar',
  'calendar.weekGrid': 'Two-week calendar',
  'calendar.showDay': 'Show {date}',
  'calendar.installers': '{count} installers',
  'calendar.installer': '{count} installer',
  'calendar.installationJobs': '{count} installation jobs',
  'calendar.installationsUnavailable': 'Installations unavailable',
  'calendar.loadingInstallations': 'Loading installations…',
  'calendar.liveSalesExcluded': 'Live Jacky calendar excluded in this workspace',
  'calendar.salesAppointments': '{count} Jacky appointments',
  'calendar.loadingWeeks': 'loading remaining weeks…',
  'calendar.checkingChanges': 'checking for changes…',
  'calendar.updateFailed': 'update failed; saved entries retained',
  'calendar.salesLastChecked': 'Jacky calendar last checked {date}',
  'calendar.refresh': 'Refresh calendar',
  'calendar.sourceError': 'One calendar source could not be updated. Saved entries remain visible; changes or appointments may be missing. Refresh to retry.',
  'calendar.invalidDates': '{count} installation dates could not be placed. Review the source work orders.',
  'calendar.more': '+{count} more',
  'calendar.dayDetails': 'Selected day details',
  'calendar.installerDetails': 'Installer details',
  'calendar.crewNotRecorded': 'Crew not recorded',
  'calendar.taskNotRecorded': 'Task not recorded',
  'calendar.completed': 'Completion recorded',
  'calendar.scheduled': 'Scheduled; attendance not confirmed',
  'calendar.openWork': 'Open installation work',
  'calendar.addressNotRecorded': 'Address not recorded',
  'calendar.bookingUnconfirmed': 'Jacky calendar; booking does not confirm attendance',
  'calendar.waiting': 'Waiting for complete calendar data.',
  'calendar.emptyDay': 'No entries in the loaded sources for this day.',
  'calendar.footnote': 'Installations use saved work-order dates. Appointments use only jacky@artemisflooring.com. Other staff see private time as Busy; cancelled entries are excluded. Site quotations are identified by their saved category or title; other entries remain other appointments. Unmatched crews stay unconfirmed.',
  'operations.kicker': 'Artemis Flooring · Sydney',
  'operations.title': 'Operations',
  'operations.lede': 'Installation load first: this week, next week, and the work still waiting for a reliable date.',
  'operations.openCalendar': 'Open full calendar',
  'operations.scheduleTitle': 'Installation schedule',
  'operations.scheduleNote': 'Saved crew-days from the installation record. Nothing here invents a missing date.',
  'operations.refreshing': 'Refreshing…',
  'po.exception.savedQuotePrice': 'Price from the saved quote; check the current supplier price before ordering.',
  'operations.scheduleUnavailable': 'The installation schedule could not be loaded. The other sections load separately.',
  'operations.thisWeek': 'Installations this week',
  'operations.nextWeek': 'Next week',
  'operations.dayCount': '{count} working days',
  'operations.loadingSchedule': 'Loading installations…',
  'operations.loadingWork': 'Loading work…',
  'operations.crewTbc': 'Crew to be confirmed',
  'operations.taskTbc': 'Scope to be confirmed',
  'operations.noInstallations': 'No installations recorded in this week.',
  'operations.delayedTitle': 'Past scheduled date — review',
  'operations.delayedNote': 'The latest recorded installation date has passed. The job may be complete or may need another date.',
  'operations.lastRecordedDate': 'Last recorded date: {date}',
  'operations.noDelayed': 'No scheduled jobs with only past dates.',
  'operations.unscheduledTitle': 'Unscheduled phases & jobs',
  'operations.unscheduledNote': 'Open work with no next date recorded. Schedule it only when the date is known.',
  'operations.nextPhaseUnknown': 'Active job · next phase date not recorded',
  'operations.installDateUnknown': 'Pending schedule · installation date not recorded',
  'operations.noUnscheduled': 'No open work is waiting for a date.',
  'operations.itemCount': '{count} items',
  'operations.financialSummary': 'Monthly financial performance',
  'month.kicker': 'Performance · {month} · Sydney',
  'month.title': 'This Month',
  'month.ledeAttention': '{count} items need attention. They are collected with your saved follow-ups below.',
  'month.ledeClear': 'Monthly performance, recognised work, everything scheduled and pending, and how revenue and profit build up.',
  'month.topLine': 'Top line · revenue MTD',
  'month.operatingProfit': 'Operating profit',
  'month.operatingBasis': 'job profit less {target} monthly fixed-cost target',
  'month.grossMargin': 'Gross margin',
  'month.grossProfit': '{amount} gross profit before fixed cost',
  'month.recognisedJobs': 'Recognised jobs',
  'month.recognitionBasis': 'Invoiced + Fully Paid · by close date',
  'month.installValue': 'Install value',
  'month.scheduledWindow': 'Scheduled',
  'month.pendingSchedule': 'Pending Schedule',
  'month.coverageLine': '{profit} profit · {coverage}% of {target} · {count} jobs',
  'month.coverageIncomplete': 'Profit unavailable · cost known for {known}/{total} jobs',
  'month.opportunityValue': 'Open opportunity value',
  'month.opportunityCoverage': '{profit} profit · {coverage}% of {target} · {opportunities} opportunities · {leads} leads in 30 days',
  'month.opportunityCoveragePartial': 'At least {profit} profit · known for {known}/{priced} priced · {opportunities} opportunities · {leads} leads in 30 days',
  'month.drillDown': 'View details →',
  'month.detailKicker': 'Performance detail',
  'month.close': 'Close',
  'month.profitAmount': '{amount} profit',
  'month.noDetailRows': 'No records are behind this figure.',
  'month.recordedOpportunityValue': 'Recorded open opportunity value',
  'month.leadsLast30': 'Accepted leads registered · last 30 days',
  'month.unreferencedOpportunity': 'Opportunity reference pending',
  'month.legacySource': 'Legacy Pipeline',
  'month.aiwSource': 'AIW Pipeline',
  'month.valueUnavailable': 'Value unavailable',
  'month.profitUnavailable': 'Profit unavailable',
  'month.costsIncomplete': 'Costs incomplete · {count} items',
  'month.profitIncomplete': 'Profit unavailable · cost known for {known}/{total} records',
  'month.performanceTitle': 'Monthly performance',
  'month.performanceMeta': '{month} · recognised work',
  'month.loadingPerformance': 'Loading monthly performance…',
  'month.performanceUnavailable': 'Monthly performance could not be loaded. No figure is assumed.',
  'month.noRecognisedJobs': 'No revenue has been recognised this month yet.',
  'month.job': 'Job',
  'month.closed': 'Recognised',
  'month.revenue': 'Top line',
  'month.profit': 'Gross profit',
  'month.total': 'This month',
  'month.financialNote': 'Revenue is recognised when a Job reaches Invoiced or Fully Paid and is placed in the month by its saved close date. Operating profit is recognised Job profit less the saved monthly fixed-cost target; it is not cash received.',
  // This Month build-up and the date-passed flag (Jacky, 23 Oct 2026: no 30-day window on Scheduled).
  'month.datePassed': 'date passed',
  'month.datePassedCount': '{count} date passed · not invoiced yet',
  'month.buildUpTitle': 'Build-up',
  'month.buildUpMeta': 'Recognised → + Scheduled → + Pending schedule → + Opportunity · ex GST',
  'month.buildUp.recognised': 'Recognised',
  'month.buildUp.scheduled': 'Scheduled',
  'month.buildUp.pending_schedule': 'Pending schedule',
  'month.buildUp.opportunity': 'Opportunity',
  'month.buildUpRevenue': 'Revenue',
  'month.buildUpProfit': 'Profit (priced work)',
  'month.buildUpBase': 'Built by earlier steps',
  'month.buildUpUnpriced': '{count} without cost',
  'month.buildUpTarget': 'Fixed cost {amount}',
  'month.buildUpAria': 'Build-up of revenue and profit: {summary}',
  'month.buildUpOpportunityNote': 'Opportunity is not won yet, so it is drawn lighter.',
  'month.est': 'est.',
  'month.estimatedCount': '{count} estimated',
  'month.estimatedHint': 'Part of this Job\'s cost is still the estimate made at acceptance: catalogue cost for materials not yet ordered, Artemis standard rates for work no crew holds yet.',
  'month.buildUpEstimated': 'Profit resting on an estimate',
  'month.followUps': 'Follow Ups · immediate attention included',
  'month.openQueue': 'Open full queue',
  'month.newEnquiries': '{count} new enquiries need a response',
  'month.newEnquiriesDetail': 'A recorded contact attempt clears this attention; a newer customer reply opens it again.',
  'month.reviewNow': 'Review now',
  'month.materialRisk': 'Material risk · {name}',
  'month.openJob': 'Open Job',
  'month.followUpsUnavailable': 'Follow Ups could not be loaded. This does not mean the queue is clear.',
  'month.loadingFollowUps': 'Loading Follow Ups…',
  'month.nothingNeedsAttention': 'Nothing needs immediate attention and there are no saved follow-ups.',
  'month.late': '{age} late',
  'month.previousValue': 'Previous month {amount}',
  'month.upFromPrevious': 'Up {percent}% from previous month ({amount})',
  'month.downFromPrevious': 'Down {percent}% from previous month ({amount})',
  'customer.proposal.lastUpdated': 'Your Proposal · Last updated {date}',
  'customer.print.preparedFor': 'Prepared for {name}',
  'customer.print.address': 'Site address: {address}',
  'customer.print.lastUpdated': 'Last updated {date}',
  'customer.print.draftSaved': 'Draft saved {date}',
  'customer.print.openDraft': 'Open the saved draft from your staff workspace to review it.',
  'customer.print.draftTitle': 'WORKING DRAFT · INTERNAL REVIEW · Version {version}',
  'customer.print.draftNotice': 'This draft has not been released. Prices are EX GST; outward amounts show + GST.',
  'customer.print.draftBlockers': 'Unresolved before release',
  'customer.print.aboutFloor': 'About your floor',
  'customer.print.brand': 'by {brand}',
  'customer.print.colour': 'Colour: {colour}',
  'customer.print.specs': 'Specifications',
  'customer.print.unavailable': 'This Proposal print copy is not available.',
  'customer.pay.title': '{kind} payment',
  'customer.pay.deposit': 'Deposit',
  'customer.pay.balance': 'Final balance',
  'customer.pay.loading': 'Loading your payment details…',
  'customer.pay.unavailable': 'This payment link is not available',
  'customer.pay.unavailableHint': 'Please ask Artemis Flooring for a fresh link.',
  'customer.pay.job': 'Job {job}',
  'customer.pay.invoice': 'Tax invoice {invoice}',
  'customer.pay.viewInvoice': 'View invoice',
  'customer.pay.amountDue': '{kind} due',
  'customer.pay.paid': 'Payment received — thank you',
  'customer.pay.paidHint': 'Registered on {date}. Nothing more is due on this request.',
  'customer.pay.cancelled': 'This request was withdrawn by Artemis Flooring. If you were expecting to pay, please contact us.',
  'customer.pay.transferTitle': 'Pay by bank transfer',
  'customer.pay.accountName': 'Account name',
  'customer.pay.bsb': 'BSB',
  'customer.pay.account': 'Account number',
  'customer.pay.reference': 'Payment reference',
  'customer.pay.referenceNote': 'Please use your Job reference {job} as the payment description so we can match your transfer.',
  'customer.pay.onInvoice': 'The same account details are printed on your tax invoice.',
  'customer.pay.cardTitle': 'Pay by card',
  'customer.pay.cardButton': 'Pay {amount} by card',
  'customer.pay.cardHint': 'Opens the ANZ Worldline secure payment page in a new tab.',
  'customer.pay.contact': 'Questions about this payment: {issuer} · {phone} · {email}',
  'attachments.loading': 'Loading attachments…',
  'attachments.open': 'Open this file in a new tab',
  'payreq.open': 'Invoice & payment requests',
  'payreq.title': 'Invoice & payment requests',
  'payreq.close': 'Close',
  'payreq.loading': 'Loading payment requests…',
  'payreq.failed': 'Payment requests could not be loaded. Retry.',
  'payreq.agreement': 'Agreed amount (EX GST)',
  'payreq.gst': 'GST',
  'payreq.payable': 'Payable',
  'payreq.received': 'Received',
  'payreq.receivedFromXero': 'From Xero invoice {invoice}; the Job records {recorded}.',
  'payreq.balance': 'Balance outstanding',
  'payreq.invoice': 'Xero invoice',
  'payreq.noInvoice': 'Not issued yet',
  'payreq.issueInvoice': 'Issue invoice in Xero',
  'payreq.issued': 'Invoice {invoice} issued in Xero.',
  'payreq.invoiceOpen': 'Open online invoice',
  'payreq.linkExisting': 'Link existing Xero invoice',
  'payreq.linkNumber': 'Xero invoice number',
  'payreq.linkExistingHint': 'The invoice was raised in Xero by hand. It is read from Xero by its number and recorded on this Job; nothing in Xero changes and nothing is sent.',
  'payreq.linkAcceptDifference': 'Link even if the invoice total differs from the agreement',
  'payreq.linked': 'Invoice {invoice} linked from Xero.',
  'payreq.linkedUnder': 'Invoice {invoice} linked from Xero · under {contact}.',
  'payreq.notConfigured': 'Xero invoicing is not connected on the backbone yet.',
  'payreq.cashHint': 'This Job is a cash agreement: use Payments & receipts.',
  'payreq.legacyHint': 'Migrated Job: a registered receipt is kept here; the Job card deposit stays a staff edit.',
  'payreq.requests': 'Requests',
  'payreq.none': 'No deposit or balance request yet.',
  'payreq.raise': 'Request payment',
  'payreq.kind': 'Request',
  'payreq.kind.deposit': 'Deposit',
  'payreq.kind.balance': 'Final balance',
  'payreq.basis': 'Amount as',
  'payreq.basis.percent': '% of agreement',
  'payreq.basis.amount': 'Amount (EX GST)',
  'payreq.value': 'Value',
  'payreq.preview': '{amount} payable ({ex} + GST)',
  'payreq.channel': 'Send by',
  'payreq.channel.email': 'Email',
  'payreq.channel.sms': 'TXT',
  'payreq.link': 'Worldline payment link (optional)',
  'payreq.linkHint': 'Create the Pay by Link in the ANZ Worldline Merchant Portal (Create payments) for this amount and paste its URL here. Leave empty for bank transfer only.',
  'payreq.note': 'Note',
  'payreq.raised': 'Request raised and the message prepared for send review.',
  'payreq.copyLink': 'Copy pay page link',
  'payreq.linkCopied': 'Pay page link copied.',
  'payreq.state.requested': 'Waiting',
  'payreq.state.paid': 'Paid',
  'payreq.state.cancelled': 'Cancelled',
  'payreq.paidLine': '{amount} by {method} on {date}',
  'payreq.markReceived': 'Mark received',
  'payreq.method': 'Received by',
  'payreq.method.transfer': 'Bank transfer',
  'payreq.method.card': 'Card',
  'payreq.amount': 'Amount received',
  'payreq.date': 'Date received',
  'payreq.reference': 'Bank / Worldline reference',
  'payreq.register': 'Register receipt',
  'payreq.registered': 'Receipt registered.',
  'payreq.attachLink': 'Attach Worldline link',
  'payreq.linkAttached': 'Payment link saved.',
  'payreq.cardLink': 'Card link',
  'payreq.cardLink.none': 'none',
  'payreq.void': 'Void invoice',
  'payreq.reread': 'Re-read from Xero',
  'payreq.reread.done': 'Invoice re-read from Xero; nothing newer to record.',
  'payreq.reread.deposit': 'Invoice re-read from Xero; deposit {amount} recorded on the Job.',
  'payreq.reread.paid': 'Invoice re-read from Xero; paid in full on {date}.',
  'payreq.voidHint': 'Voids the AUTHORISED invoice in Xero (nothing registered against it) so a corrected one can be issued. Every accepted Proposal line is listed on the new invoice.',
  'payreq.voided': 'Invoice voided in Xero. Issue the corrected invoice.',
  'payreq.notify': 'Tell the customer',
  'payreq.notify.sms': 'TXT — payment received',
  'payreq.notify.email': 'Email — payment received',
  'payreq.notify.none': 'No message',
  'payreq.registeredNotify': 'Receipt registered. The customer message is ready for your send click.',
  'payreq.registeredNoMobile': 'Receipt registered. No mobile number on file, so no TXT was prepared.',
  'payreq.registeredNoEmail': 'Receipt registered. No email on file, so no message was prepared.',
  'payreq.cancel': 'Cancel request',
  'payreq.cancelReason': 'Reason',
  'payreq.cancelled': 'Request cancelled.',
  'payreq.reopen': 'Reopen request',
  'payreq.reopenHint': 'The same request comes back to open: the link the customer already holds works again. Nothing is sent.',
  'payreq.reopened': 'Request reopened. The customer\'s link works again.',
  'payreq.review': 'Send review',
  'payreq.balanceHint': 'Request the final balance once the installation is complete.',
 "site.nav.main":"Main",
 "site.nav.home":"Artemis Flooring home",
 "site.nav.floors":"Explore floors",
 "site.nav.commercial":"Commercial",
 "site.nav.services":"Services",
 "site.nav.contact":"Contact",
 "site.nav.myHome":"My home",
 "site.nav.book":"Book a site measure",
 "site.nav.bookShort":"Book",
 "site.nav.price":"Price my floor",
 "site.nav.menu":"Menu",
 "site.nav.language":"Language",
 "site.hero.eyebrow":"Beautiful floors · Everyday living",
 "site.hero.title1":"A little more",
 "site.hero.title2":"love for home.",
 "site.hero.intro":"The look you love, at a budget that feels right. Find your floor, see an indicative price, and let us measure before you decide.",
 "site.hero.explore":"Find my floor",
 "site.hero.note":"Start with your rooms. No account needed.",
 "site.hero.inPicture":"In the picture",
 "site.hero.tier.signature":"Signature",
 "site.hero.tier.premium":"Premium",
 "site.hero.tier.essential":"Essential",
 "site.hero.slide":"Show {n} of {total}",
 "site.hero.slides":"Featured floors",
 "site.hero.scroll":"A little inspiration below",
 "site.promise.1":"Beautiful choices, considered budgets",
 "site.promise.2":"Measured before you decide",
 "site.promise.3":"Expert help from first idea to installation",
 "site.proof.installs":"installations completed in 2026",
 "site.proof.reviews":"{count} Google reviews",
 "site.proof.note":"Counted from our completed installation records. Google rating checked 23 September 2026.",
 "site.look.eyebrow":"Find your feeling",
 "site.look.title1":"Your home has a story.",
 "site.look.title2":"Start from the floor.",
 "site.look.copy":"The quiet of a Sunday morning. A house full of friends. Find a floor that feels like you, then make it work for your life.",
 "site.look.tabs":"Style",
 "site.look.warm":"Warm & natural",
 "site.look.character":"A little character",
 "site.look.soft":"Soft & serene",
 "site.look.warmTitle":"Let the light in.",
 "site.look.warmCopy":"Gentle tones and beautiful grain. An easy starting point for a home that feels calm, open and yours.",
 "site.look.characterTitle":"A beautiful first impression.",
 "site.look.characterCopy":"A herringbone floor gives a room its own rhythm. A considered detail that makes an everyday space feel special.",
 "site.look.softTitle":"A softer kind of Sunday.",
 "site.look.softCopy":"Comfort underfoot and a little quiet in your day. Carpet for the rooms where you slow down.",
 "site.look.photo":"Supplier room photography",
 "site.look.cta":"Explore this floor",
 "site.edit.eyebrow":"The floor edit",
 "site.edit.title":"Good looks. Real possibilities.",
 "site.edit.collections":"{count} collections",
 "site.aiVisual":"AI visualisation",
 "site.emma.open":"Chat with EmmA",
 "site.emma.close":"Close chat",
 "site.emma.role":"Artemis Flooring's flooring consultant",
 "site.emma.greeting":"Hi, I'm EmmA. Ask me anything about floors — products, colours, how installation works, or booking a measure. You don't have to know it all at once; we'll choose together.",
 "site.emma.placeholder":"Type a message",
 "site.emma.send":"Send",
 "site.emma.typing":"EmmA is typing",
 "site.emma.ai":"EmmA is an AI assistant. A confirmed price follows a site measure.",
 "site.emma.whatsapp":"Continue on WhatsApp",
 "site.emma.whatsappText":"Hi EmmA, I'm on the Artemis Flooring website and have a question.",
 "site.emma.error":"I can't answer right now. Message me on WhatsApp, or call {phone}.",
 "site.emma.busy":"That's a lot of messages in a short time. Give me a moment, or continue on WhatsApp.",
 "site.emma.tooLong":"That message is a little long. Could you shorten it?",
 "site.emma.whatsappCta":"Message EmmA on WhatsApp",
 "site.edit.collectionOne":"1 collection",
 "site.edit.note":"Room photographs as supplied by each maker.",
 "site.edit.all":"See every floor",
 "site.cat.carpet":"Carpet",
 "site.cat.carpet-tile":"Carpet Tile",
 "site.cat.engineered-timber":"Engineered Timber",
 "site.cat.hybrid":"Hybrid",
 "site.cat.laminate":"Laminate",
 "site.cat.solid-timber":"Solid Timber",
 "site.cat.vinyl-plank":"Vinyl Plank",
 "site.cat.vinyl-tile":"Vinyl Tile",
 "site.cat.vinyl-sheet":"Vinyl Sheet",
 "site.work.eyebrow":"Real floors. Real Artemis work.",
 "site.work.title":"From an idea to everyday life.",
 "site.work.copy":"Floors we have laid, photographed on site, with the product behind each finish.",
 "site.work.oak":"Engineered timber",
 "site.work.carpet":"Carpet",
 "site.work.laminate":"Laminate",
 "site.work.stairs":"Laminate staircase",
 "site.reviews.title":"A few words from our customers.",
 "site.reviews.copy":"Excerpts from public Google reviews, in the customer's own words. Read them in full on Google.",
 "site.reviews.read":"Read our Google reviews",
 "site.quote.eyebrow":"Plan your floor",
 "site.quote.title1":"Big ideas.",
 "site.quote.title2":"Meet your budget.",
 "site.quote.copy":"A beautiful floor starts with a clear plan. Bring your rooms, your favourite look and your budget together in one place.",
 "site.quote.step1":"Tell us your rooms and choose a floor.",
 "site.quote.step2":"See an indicative price straight away.",
 "site.quote.step3":"Book a free measure to confirm your scope and quotation.",
 "site.quote.cta":"Start with my rooms",
 "site.quote.example":"Example home",
 "site.quote.cardTitle":"Your place, taking shape.",
 "site.quote.cardSub":"Explore your rooms and flooring before booking a measure.",
 "site.quote.living":"Living",
 "site.quote.dining":"Dining",
 "site.quote.hall":"Hall",
 "site.quote.rowFloor":"Your floor",
 "site.quote.rowRooms":"Your rooms",
 "site.quote.rowRoomsValue":"3 rooms · 60 m²",
 "site.quote.rowNext":"Next step",
 "site.quote.rowNextValue":"A free measure",
 "site.quote.open":"Step inside My home",
 "site.closing.eyebrow":"From your first idea to your first step",
 "site.closing.title":"Make yourself at home.",
 "site.footer.tagline":"Floors for Sydney homes, measured before you decide and laid with care.",
 "site.footer.residential":"Residential",
 "site.footer.commercial":"Commercial",
 "site.footer.help":"Help",
 "site.footer.showroom":"Showroom",
 "site.footer.hours":"Mon – Fri · 9:00am – 5:00pm",
 "site.footer.tollFree":"toll free",
 "site.footer.herringbone":"Herringbone & Chevron",
 "site.footer.place":"Sydney, NSW",
  "site.jobPage.preview": "View-only preview. This view does not record a customer visit. Choices and chat are disabled.",
  "site.footer.privacy": "Privacy & website storage",
  "site.analytics.title": "Help us improve your flooring journey",
  "site.analytics.copy": "May we use Google Analytics cookies to understand public page visits? It is optional. Advertising tracking stays off, and private customer pages are excluded.",
  "site.analytics.details": "Privacy details",
  "site.analytics.accept": "Accept analytics",
  "site.analytics.reject": "Reject analytics",
  "site.analytics.close": "Close",
  "site.analytics.settings": "Analytics choices",
  "site.analytics.accepted": "Analytics is currently accepted. You can withdraw it below.",
  "site.analytics.rejected": "Analytics is currently rejected.",
  "site.analytics.privacyTitle": "Optional Google Analytics",
  "site.analytics.privacyCopy": "Google Analytics loads only after you accept analytics. It uses first-party cookies to measure visits and activity on public pages, including our catalogue, Price my floor and enquiry pages. Google receives technical information such as your IP address and browser details and may process it outside Australia. We send public page paths and the referring website origin, without URL queries or fragments, form entries, chat messages, room details or private customer links. Advertising personalisation, Google signals and automatic form tracking are off. Your choice is stored for up to 180 days; analytics cookies expire within the same period. Use Analytics choices in the footer to change your decision. Withdrawing stops future collection, removes our analytics cookies and reloads the page; it does not delete data already received by Google. Our separate anonymous page counter continues regardless of this choice.",
  "site.analytics.googlePrivacy": "How Google uses information from websites that use its services",
  "site.privacy.title": "Privacy & website storage",
  "site.privacy.description": "How Artemis Flooring handles website enquiries, chat, anonymous page counts and information stored in your browser.",
  "site.privacy.updated": "Updated 1 November 2026",
  "site.privacy.intro": "This notice explains what happens to information when you browse our website, price a floor, contact us, book a measure or speak with EmmA.",
  "site.privacy.providedTitle": "Information you choose to send",
  "site.privacy.provided": "Enquiry and booking forms send the details you enter to Artemis Flooring so we can respond, prepare your flooring enquiry and arrange the requested service. This can include your name, contact details, site address, notes and indicative estimate. A confirmed measure is recorded in our booking system and calendar.",
  "site.privacy.chat": "Messages you send to EmmA are processed through our assistant service and its AI providers to produce a reply. Conversation records and usage information are retained in our systems. Please include only information relevant to your flooring enquiry.",
  "site.privacy.counterTitle": "Anonymous website counts",
  "site.privacy.counter": "We count page views and a small number of website actions to understand which parts of the site are useful. The counter records the page path, referring website hostname, broad device category and limited numeric or yes/no details. It does not store a cookie, visitor identifier, IP address, full referring URL, enquiry text or chat message. These limits describe the counter; forms, chat and technical service logs are separate.",
  "site.privacy.storageTitle": "What your browser remembers",
  "site.privacy.storageIntro": "Cookies and browser storage are different ways a website can remember information. Our page counter uses neither. Other website features use session storage or local storage for the purposes below.",
  "site.privacy.estimateTitle": "Estimates and booking recovery",
  "site.privacy.estimate": "Your room inputs, floor selections and estimate can carry from Price my floor to the booking form. An unfinished booking request may stay in the tab so a retry can recover the same request. A browser copy is not confirmation of a booking.",
  "site.privacy.chatTitle": "Chat continuity",
  "site.privacy.chatStorage": "A chat session identifier, recent messages and the chat panel state are stored for the browser tab so the conversation can continue as you browse.",
  "site.privacy.accessTitle": "Private customer access",
  "site.privacy.access": "If you use a private customer link or sign in, browser storage can hold access and return-to-page information. Some older booking summaries may also remain on the device. Keep private links to yourself, and sign out when using a shared device.",
  "site.privacy.attributionTitle": "Where an enquiry came from",
  "site.privacy.attribution": "Campaign labels and advertising click identifiers in a landing-page address, such as utm, gclid or fbclid values, can be held in session storage and included when you submit an enquiry or booking. This helps us understand its source. They are separate from the anonymous page counter.",
  "site.privacy.providersTitle": "Services that help the website run",
  "site.privacy.providers": "We use service providers for hosting, data storage, sign-in, calendar bookings, address suggestions and AI-assisted replies. Technical requests can include an IP address and device or request information needed to deliver and protect these services. Information may be processed outside Australia, depending on the provider. The site also loads fonts from Google Fonts.",
  "site.privacy.external": "If you follow a WhatsApp link or open an external payment or other service, that provider’s privacy practices also apply.",
  "site.privacy.choicesTitle": "Your choices and stored records",
  "site.privacy.choices": "You can clear or block website storage in your browser settings. Session storage normally lasts for the tab or browser session, although a browser may restore it. Local storage can remain until cleared. Blocking or clearing it may remove an unfinished estimate, chat history or sign-in state and affect recovery features. It does not delete enquiries, bookings or conversations already sent to us.",
  "site.privacy.contactTitle": "Privacy questions or requests",
  "site.privacy.contact": "Contact us to ask about information we hold, request access or a correction, ask about deletion, or raise a privacy concern. We will consider the request together with any record-keeping obligations. Clearing your browser is separate from these requests.",
  "site.privacy.contactLink": "Contact Artemis Flooring",
 "site.footer.disclaimer":"Prices shown are indicative; a quote from Artemis Flooring is the only firm price.",
 "site.footer.example":"Example catalogue · built without the live views",
  "measure.title": "Free measure",
  "measure.lede": "Available times in Jacky’s calendar — one tap books it.",
  "measure.loading": "Reading Jacky’s calendar…",
  "measure.failed": "Jacky’s calendar could not be read, so no time is shown as free. Nothing was booked.",
  "measure.none": "No free times in the next two weeks.",
  "measure.near": "near",
  "measure.nearHint": "Near: another measure is booked close by on that day, so the travel is shared.",
  "measure.booking": "Booking…",
  "measure.booked": "Measure booked: {label}",
  "measure.held": "Calendar write held: appointment actions are switched off, so this booking is recorded in AIW but is not on Jacky’s calendar yet.",
  "measure.heldOther": "Not on the calendar yet — {note}",
  "measure.queued": "Queued for Jacky’s calendar; waiting for saved confirmation.",
  "measure.move": "To move it, tap another time below.",
  "measure.cancel": "Cancel booking",
  "measure.cancelled": "Measure cancelled.",
  "measure.copyLink": "Copy booking link",
  "measure.linkCopied": "Booking link copied — valid until {date}. Paste it into your message.",
  "measure.linkShown": "Booking link below — valid until {date}. Copy it now; it is shown once.",
  "measure.needsApproval": "Waiting for approval — nothing has changed yet.",
  "measure.more": "Show more times",
  // Newest first, then Show more — shared list paging (13 Oct 2026, components/newest-list.tsx).
  "list.showing": "Showing {shown} of {total}, newest first",
  "list.showMore": "Show more ({remaining} remaining)",
  "list.loadingMore": "Loading more…",
  "list.moreFailed": "The next page could not be loaded. What is shown is the newest part only.",
  "list.retry": "Retry",
  "list.stageShowing": "{shown} of {total} in this stage shown",
  "list.onlyStage": "Only this stage",
  "list.proposalUntitled": "Proposal",
  "list.overdueMore": "{count} more overdue — open Follow-ups",
  "communications.truncated": "Older history is not in these threads: {sources} hold more than the newest {limit} read here.",
  "communications.source.drafts": "saved drafts",
  "communications.source.touches": "logged activity",
  "communications.source.supplier_evidence": "supplier evidence",
  "communications.source.installer_requests": "installer requests",
  "inv.truncated": "Only the first {limit} stock lines were read. Search and filters cover those lines only.",
  // New work for a customer already on file (14 Oct 2026).
  "newOpp.button": "New Opportunity / Proposal",
  "newOpp.forPerson": "New site",
  "newOpp.title": "New work for {name}",
  "newOpp.lede": "A new site for a customer already on file. Their details are linked, not entered again; the Opportunity opens ready for a Proposal.",
  "newOpp.siteAddress": "Site address",
  "newOpp.suburb": "Suburb",
  "newOpp.postcode": "Postcode",
  "newOpp.category": "Category",
  "newOpp.categoryUnsure": "Not sure yet",
  "newOpp.source": "How this work came",
  "newOpp.source.trade_network": "Existing trade network",
  "newOpp.source.referral": "Referral",
  "newOpp.source.phone": "Phone call",
  "newOpp.source.email": "Email",
  "newOpp.source.website": "Website",
  "newOpp.source.other": "Other",
  "newOpp.brief": "Brief",
  "newOpp.briefHint": "e.g. 3 units, hybrid throughout, handover December",
  "newOpp.submit": "Open Opportunity",
  "newOpp.cancel": "Cancel",
  "newOpp.done": "Opportunity opened",
  "newOpp.pending": "Awaiting approval — no change has been made.",
  "newOpp.failed": "Not opened: {reason}",
  // EmmA on the Proposal page and the customer's language (14 Oct 2026).
  "custLang.label": "EmmA speaks with them in",
  "custLang.hint": "Used by EmmA on their Proposal and Job pages. The pages stay in English.",
  "custLang.saved": "Saved. EmmA will use this language from their next message.",
  "custLang.pending": "Awaiting approval — no change has been made.",
  "custLang.failed": "Not saved: {reason}",
  "ppChat.title": "Conversation on the Proposal page",
  "ppChat.empty": "The customer has not written on their Proposal page yet.",
  "ppChat.failed": "The conversation could not be loaded.",
  "ppChat.handedOff": "EmmA handed to the office:",
  "ppChat.customer": "Customer",
  // Sharing a Proposal: addressee, undecided colour, how to accept (report 89bffef3, 16 Oct 2026).
  "customer.doc.site": "Site",
  "customer.doc.toGoAhead": "To go ahead, press Accept on your Proposal page, or contact Jacky on 0432 399 295.",
  "customer.print.colourNotChosen": "Colour: not yet chosen",
  "customer.print.chooseColour": "Choose your colour — the {collection} range",
  "customer.print.chooseColourHint": "Your colour is not chosen yet. Choose it on your Proposal page and press Confirm, or reply to our email with the colour name shown below. Tap a colour to see it on our website.",
  "customer.print.seeOnline": "{code} · see it online",
  "share.attachPdf": "Attach the Proposal PDF",
  "share.attachPdfHint": "The same document as the link, as a file the customer can keep.",
  "share.pdfPreparing": "Preparing the PDF…",
  "share.pdfFailed": "The Proposal PDF could not be prepared: {error}",
  "share.pdfReady": "{name} · {size}",
  "share.pdfRetry": "Try again",
  // Choose and confirm a classic Proposal's colour (report 89bffef3, 16 Oct 2026).
  "customer.colour.section": "Choose your colour",
  "customer.colour.title": "Choose your colour — {product}",
  "customer.colour.hint": "Your colour is not chosen yet. Tap a colour to see it larger, then confirm it.",
  "customer.colour.chosen": "Your colour: {colour}. You can change it until you accept.",
  "customer.colour.yourChoice": "Your choice",
  "customer.colour.confirm": "Confirm {colour}",
  "customer.colour.saving": "Saving…",
  "customer.colour.confirmed": "Thank you — {colour} is confirmed for {product}.",
  "customer.colour.priceToConfirm": "Thank you — {colour} is noted. Its price is different, so Artemis will confirm it and send you the updated Proposal.",
  // The Proposal's strata scope pack, in the Proposal's format (report 89bffef3, 16 Oct 2026).
  "strata.incomplete": "Strata pack is incomplete. Check the quotation, acoustic report and insurance documents before preparing the email.",
  "strata.fixed.builtIn": "The boards float on their factory-attached acoustic backing. This backing is included in the stated board thickness; it is not a separate underlay layer.",
  "strata.c.quotation": "Quotation — released Proposal (attached, original)",
  "strata.c.common": "Common areas — access, protection, dust control and clean-up",
  "strata.common.title": "3. Work in common strata areas",
  "strata.common.intro": "Floor replacement is limited to {rooms}. Common areas are used only for agreed access, deliveries and waste removal, with protection and cleaning as set out below. Changes to common-property finishes are not included.",
  "strata.common.hours.label": "Building conditions and hours",
  "strata.common.hours": "Obtain written building conditions before mobilisation. Follow the owners corporation’s approved working, noise, delivery and waste-collection hours; confirm notices, inductions and bookings with the building manager before scheduling.",
  "strata.common.protection.label": "Protect shared access",
  "strata.common.protection": "Record the condition of the agreed loading area and access route before work. Protect shared floors, corners, door frames and lift interiors where used. Keep entrances, fire doors, exits and resident access clear; check protection throughout the day.",
  "strata.common.movement.label": "Deliveries and product movement",
  "strata.common.movement": "Agree loading, parking and the lift or stair route with the building manager. Confirm any lift booking, size and load limits. Move boards and tools by protected trolley or controlled carrying; stage materials inside the lot and keep common areas free of storage.",
  "strata.common.dust.label": "Dust and cutting controls",
  "strata.common.dust": "Use an agreed contained cutting area with on-tool capture and task-appropriate commercial-grade HEPA dust extraction. Check hoses, filters and bags before use. Do not dry-sweep or blow dust with compressed air. Stop if containment fails; suspected hazardous material requires separate assessment and appropriate controls before work resumes.",
  "strata.common.clean.label": "Daily common-area clean-up",
  "strata.common.clean": "Check and vacuum the protected access route after material and waste movements and at the end of each workday. Remove loose debris, packaging and installation dust; spot-clean marks using suitable products. Leave shared routes clear, dry and safe for residents.",
  "strata.common.waste.label": "All construction waste off site",
  "strata.common.waste": "Contain removed carpet and underlay, offcuts, packaging and dust waste in closed bags or covered containers. Use the protected route and remove waste to an appropriate off-site facility. Do not use resident bins or chutes. Any skip or temporary collection point requires building approval.",
  "strata.common.handover.label": "Final inspection and handover",
  "strata.common.handover": "The Artemis site lead checks the work and common access route, removes temporary protection carefully, completes a final vacuum and clean, and records the condition with photos. Report any damage promptly and agree any rectification with the owner or building manager.",
  "strata.att.quotation": "Quotation — the current released Proposal, including its scope, quantities, terms and GST-exclusive amount, is attached in full.",
  "strata.title": "Strata scope pack",
  "strata.byline": "Artemis Flooring · Sydney · Proposal {ref} · {date}",
  "strata.bylineNoRef": "Artemis Flooring · Sydney · {date}",
  "strata.banner": "For the owners corporation: the quotation, proposed work areas, how the floor is fixed, protection and clean-up of common areas, the acoustic report and our insurance.",
  "strata.contents": "In this pack",
  "strata.c.scope": "Scope of work — the areas, what is included and what is not",
  "strata.c.fixed.floating": "How the floor is fixed to the subfloor — floating",
  "strata.c.fixed.glued": "How the floor is fixed to the subfloor — glued down",
  "strata.c.acoustic": "Acoustic report — {title} (attached, original)",
  "strata.c.certificate": "{title} (attached, original)",
  "strata.scope": "1. Scope of work",
  "strata.scope.roomsFromQuote": "the areas listed in the attached released quotation",
  "strata.scope.sentence": "Supply and install {product}, {method}{underlay} over the {subfloor}, in {rooms}.",
  "strata.scope.floating": "floating",
  "strata.scope.glued": "glued down",
  "strata.scope.onUnderlay": ", on {underlay}",
  "strata.scope.slab": "existing subfloor",
  "strata.scope.outOne": "{rooms} is not part of the works.",
  "strata.scope.outMany": "{rooms} are not part of the works.",
  "strata.included": "What is included",
  "strata.col.item": "Item",
  "strata.col.quantity": "Quantity",
  "strata.col.covers": "What the work covers",
  "strata.notIncluded": "Not included",
  "strata.fixed.floating": "2. How the floor is fixed to the subfloor — floating",
  "strata.fixed.glued": "2. How the floor is fixed to the subfloor — glued down",
  "strata.fixed.floatingIntro": "The boards click together into a floating floor over the specified underlay. The boards are not glued, nailed or screwed to the subfloor; any included preparation and trim fixings are separate parts of the quoted work.",
  "strata.fixed.gluedIntro": "The floor is bonded to the prepared subfloor with the manufacturer's adhesive across the whole area. Nothing is nailed or screwed into the slab.",
  "strata.fixed.floating1": "Underlay: {underlay} in the quoted floating-floor areas. Check the required moisture protection over the concrete before laying boards.",
  "strata.fixed.floating2": "Edges: retain the manufacturer-required movement gaps at walls, door frames and thresholds. Fix scotia to the wall or skirting without pinning the floating floor; confirm the transition fixing method onsite.",
  "strata.fixed.glued1": "Subfloor: clean, flat and dry before the adhesive goes down; moisture is tested over concrete.",
  "strata.fixed.glued2": "Edges: finished to the wall, door frames and thresholds with trims or scotia fixed to the wall only.",
  "strata.fixed.buildUp": "Build-up: about {mm} mm above the slab; door clearances are checked before fitting.",
  "strata.fig.wall": "Wall",
  "strata.fig.slab": "Concrete slab",
  "strata.fig.adhesive": "Adhesive, full spread",
  "strata.fig.scotia": "Scotia fixed to the wall only",
  "strata.fig.gap": "10 mm movement gap at every wall and frame",
  "strata.fig.note.floating": "Section, not to scale. Boards float; substrate preparation and trim fixing are separate.",
  "strata.fig.note.glued": "Section, not to scale. Bonded with adhesive; no nails or screws into the slab.",
  "strata.attached": "4. Quotation and supporting documents",
  "strata.att.acoustic": "Acoustic report — {title}. It tests this floor on this underlay over the slab and ceiling stated in the report; whether it meets this building's criterion is for the owners corporation to confirm.",
  "strata.att.certificate": "{title} — {note}{valid}.",
  "strata.att.validTo": ", valid to {date}",
  "strata.notOnFile": "Not on file yet: {items}",
  // The strata scope pack on the Proposal email (report 89bffef3, 16 Oct 2026).
  "share.attachStrata": "Include the strata scope pack",
  "share.attachStrataHint": "For the owners corporation: the quotation, scope, fixing detail, common-area protection and clean-up, acoustic report and both insurance certificates.",
  // The supplier hub (Supply → Suppliers & stock, 16 Oct 2026).
  "sup.kicker": "Supply",
  "sup.title": "Suppliers & stock",
  "sup.lede": "Every supplier in one place: orders, the Jobs they feed, terms, pricelists, the stock they supply and how they perform.",
  "sup.watchTitle": "{count} supplier(s) to talk to:",
  "sup.search": "Search supplier or email",
  "sup.all": "All",
  "sup.toOrders": "Orders",
  "sup.toInventory": "Inventory",
  "sup.unavailable": "Suppliers could not be read: {message}",
  "sup.listTitle": "Suppliers",
  "sup.listMeta": "orders and spend over the last {days} days",
  "sup.col.supplier": "Supplier",
  "sup.col.category": "Category",
  "sup.col.open": "Open",
  "sup.col.orders": "Orders",
  "sup.col.spend": "Spend",
  "sup.col.confirmed": "Confirmed",
  "sup.col.onTime": "On time",
  "sup.col.pricelist": "Pricelist",
  "sup.col.stock": "Stock",
  "sup.col.last": "Last order",
  "sup.col.order": "Order",
  "sup.col.job": "Job",
  "sup.col.status": "Status",
  "sup.col.sent": "Sent",
  "sup.col.promised": "Due / arrived",
  "sup.col.total": "Total",
  "sup.col.stage": "Stage",
  "sup.col.effective": "Effective",
  "sup.col.document": "Document",
  "sup.col.kind": "Kind",
  "sup.col.received": "Received",
  "sup.exGstHint": "EX GST",
  "sup.confirmedHint": "Orders the supplier confirmed: acknowledgement, pickup number, invoice, ready or collected.",
  "sup.onTimeHint": "Delivered or collected on or before the promised day, over the orders with both dates recorded.",
  "sup.none": "No supplier matches.",
  "sup.footnote": "A rate with nothing recorded behind it shows — , never 0 %. On time fills in as deliveries are received on Orders.",
  "sup.watch": "watch",
  "sup.loading": "Loading supplier…",
  "sup.back": "All suppliers",
  "sup.creditTerms": "credit terms",
  "sup.noCreditTerms": "pays per order",
  "sup.worksInChinese": "works in Chinese",
  "sup.watchReason": "On time is under {min} % over enough recorded deliveries — worth a conversation.",
  "sup.stat.open": "Open orders",
  "sup.stat.spend": "Spend, {days} days",
  "sup.stat.confirmed": "Confirmed",
  "sup.stat.onTime": "On time",
  "sup.stat.lead": "Average lead",
  "sup.stat.ofOrders": "of {count} order(s)",
  "sup.stat.ofDelivered": "of {count} delivered",
  "sup.stat.noDeliveries": "no delivery recorded yet",
  "sup.days": "{count} days",
  "sup.contact": "Contact",
  "sup.contactFootnote": "Orders go To the ordering email with sales in CC; accounts never receives a PO.",
  "sup.terms": "Ordering terms",
  "sup.termsDefault": "the default terms — none set for this supplier",
  "sup.f.ordering": "Ordering",
  "sup.f.sales": "Sales",
  "sup.f.accounts": "Accounts",
  "sup.f.phone": "Phone",
  "sup.f.warehouse": "Warehouse",
  "sup.f.notes": "Notes",
  "sup.f.lead": "Lead time",
  "sup.f.cutOff": "Cut-off",
  "sup.f.minOrder": "Minimum order",
  "sup.f.delivery": "Delivery",
  "sup.f.waste": "Waste allowance",
  "sup.pickup": "We collect",
  "sup.delivery": "They deliver",
  "sup.tab.orders": "Orders",
  "sup.tab.jobs": "Jobs",
  "sup.tab.stock": "Stock",
  "sup.tab.pricelists": "Pricelists",
  "sup.stockOrder": "for stock",
  "sup.noOrders": "No orders with this supplier yet.",
  "sup.noJobs": "No Job has ordered from this supplier yet.",
  "sup.noStock": "Nothing from this supplier is held in stock.",
  "sup.noPricelists": "No pricelist on file for this supplier.",
  "sup.pricelistPdf": "Pricelist PDF",
  "sup.priceRevision": "Price revision",
  // Supplier price lists and accessory matching (23 Oct 2026).
  "sup.tab.accessories": "Accessories",
  "sup.pl.searchTitle": "Search every price list",
  "sup.pl.searchMeta": "every supplier's current list · code, colour, collection · what we pay, EX GST",
  "sup.pl.searchPlaceholder": "Code, colour, collection or size — e.g. spotted gum 1900",
  "sup.pl.more": "More lines match — add a word to narrow it.",
  "sup.pl.col.code": "Code",
  "sup.pl.col.item": "Collection · colour",
  "sup.pl.col.size": "Size · pack",
  "sup.pl.col.cost": "Our cost",
  "sup.pl.priceOnApplication": "Price on application",
  "sup.pl.col.page": "Page",
  "sup.pl.col.weSell": "We sell it",
  "sup.pl.kind.floor": "Floors",
  "sup.pl.kind.accessory": "Accessory",
  "sup.pl.kind.missing": "Not in our catalogue",
  "sup.pl.basis.cod": "COD",
  "sup.pl.basis.partner": "Partner price",
  "sup.pl.basis.promotion": "Promotion",
  "sup.pl.yes": "Yes",
  "sup.pl.no": "No",
  "sup.pl.noMatch": "No line matches.",
  "sup.pl.none": "No price list is recorded for {name} yet.",
  "sup.pl.askLoad": "Supplier {name} (id {id}) has no price list recorded. Find their latest price list in mail or OneDrive, read every line and record it with record_supplier_price_list. What to do: ",
  "sup.pl.askLoadButton": "Ask EmmA to load the latest list",
  "sup.pl.current": "Current list · effective {day}",
  "sup.pl.summary": "{lines} lines · {floors} floors · {accessories} accessories · {sold} in our catalogue",
  "sup.pl.listPlaceholder": "Search this list",
  "sup.pl.collection": "Collection",
  "sup.pl.allCollections": "All collections",
  "sup.pl.shown": "{count} shown",
  "sup.pl.footnote": "As the supplier printed it. A list never changes our catalogue cost: an agreed rate below the list stays.",
  "sup.pl.history": "Price list history",
  // Carpet supplier B2B portals — supplier stock and the morning read (26 Oct 2026)
  "sup.tab.supplierStock": "Supplier stock",
  "sup.stock.readAt": "Read from {portal} at {at}",
  "sup.stock.pricesChanged": "prices changed since the last read",
  "sup.stock.pricesSame": "prices unchanged",
  "sup.stock.costsApplied": "catalogue costs updated {at}",
  "sup.stock.costsNotApplied": "catalogue costs not changed by this read",
  "sup.stock.report": "Portal price against our catalogue cost",
  "sup.stock.col.product": "Product",
  "sup.stock.col.portalLm": "Portal / LM",
  "sup.stock.col.portalM2": "Portal / m²",
  "sup.stock.col.catalogueM2": "Catalogue / m²",
  "sup.stock.col.change": "Result",
  "sup.stock.change.same": "Same",
  "sup.stock.change.up": "Portal higher",
  "sup.stock.change.down": "Portal lower",
  "sup.stock.change.mixed": "Mixed",
  "sup.stock.change.no_portal_price": "Not on the portal",
  "sup.stock.change.agreed": "Agreed price — kept",
  "sup.stock.change.applied": "Updated to the portal",
  "sup.stock.search": "Search range, colour or code",
  "sup.stock.shown": "{count} shown · {colours} colours · {rolls} rolls on the portal",
  "sup.stock.col.range": "Range",
  "sup.stock.col.colour": "Colour",
  "sup.stock.col.code": "Code",
  "sup.stock.col.metres": "Metres",
  "sup.stock.col.rolls": "Rolls",
  "sup.stock.col.largest": "Largest roll",
  "sup.stock.col.dyeLots": "Dye lots",
  "sup.stock.col.next": "Next production",
  "sup.stock.col.roll": "Roll",
  "sup.stock.col.dyeLot": "Dye lot",
  "sup.stock.col.location": "Location",
  "sup.stock.col.price": "Own price / LM",
  "sup.stock.notInCatalogue": "not in our catalogue",
  "sup.stock.empty": "No colour matches.",
  "sup.stock.footnote": "The supplier's own shelf, as their portal showed it at the morning read — not our warehouse (that is the Stock tab). Prices EX GST.",
  "sup.stock.line": "Supplier stock: {lm} m · {rolls} rolls · largest {largest} m · {lots} dye lots · as of {at}",
  "sup.stock.none": "Supplier stock: none on the shelf · as of {at}",
  "sup.stock.noneNext": "Supplier stock: none on the shelf · next production {next} · as of {at}",
  "sup.acc.counts": "{complete} of {colours} floor colours have both a scotia and a trim match",
  "sup.acc.gaps": "{count} to fill",
  "sup.acc.search": "Product or colour",
  "sup.acc.gapsOnly": "Gaps only",
  "sup.acc.askFill": "Supplier {name} (id {id}) has {count} hard-floor colours without a scotia or trim match. Read accessory_matches with gaps_only, match each from the supplier's price list and colour index, and save each with save_accessory_match. What to do: ",
  "sup.acc.askFillButton": "Ask EmmA to fill the gaps",
  "sup.acc.scotia": "Scotia",
  "sup.acc.trim": "Aluminium trim",
  "sup.acc.product": "Product",
  "sup.acc.floor": "Floor colour",
  "sup.acc.matchColour": "Matching colour",
  "sup.acc.spec": "Profile / spec",
  "sup.acc.gap": "No match",
  "sup.acc.fill": "Fill",
  "sup.acc.useSupplier": "Use supplier's",
  "sup.acc.retired": "Back to the supplier's match",
  "sup.acc.supplierSays": "Supplier's index: {colour}",
  "sup.acc.src.emma": "EmmA",
  "sup.acc.src.staff": "Ours",
  "sup.acc.src.supplier": "Supplier",
  "sup.acc.none": "No hard-floor colours for this supplier.",
  "sup.acc.noGaps": "No gaps: every colour has a scotia and a trim match.",
  "sup.acc.stateCounts": "{confirmed} confirmed · {legacy} legacy · {conflict} conflict · {missing} missing",
  "sup.acc.state.confirmed": "Confirmed",
  "sup.acc.state.legacy": "Legacy — confirm",
  "sup.acc.state.conflict": "Conflict",
  "sup.acc.state.missing": "Missing",
  "sup.acc.accessoryProduct": "Accessory product",
  "sup.acc.chooseVariant": "Choose exact specification",
  "sup.acc.evidence": "Supplier price-list evidence",
  "sup.acc.noEvidence": "No linked price line",
  "sup.acc.footnote": "A confirmed match uses the exact floor and accessory catalogue identities. Legacy supplier wording stays visible as evidence but never selects a purchase item. A price-list import never overwrites our match.",
  // Supplier hub: people, WeChat chats and the message timeline (16 Oct 2026).
  "sup.askEmma": "Ask EmmA",
  "sup.askEmmaPrompt": "Supplier {name} (id {id}) — get_supplier has their people, chats and orders. What to do: ",
  "sup.people": "People",
  "sup.peopleMeta": "who we deal with at this supplier",
  "sup.col.name": "Name",
  "sup.col.role": "Role",
  "sup.f.email": "Email",
  "sup.f.wechat": "WeChat name",
  "sup.f.language": "Language",
  "sup.role.rep": "Rep",
  "sup.role.ordering": "Orders desk",
  "sup.role.accounts": "Accounts",
  "sup.role.warehouse": "Warehouse",
  "sup.role.owner": "Owner",
  "sup.role.other": "Other",
  "sup.edit": "Edit",
  "sup.archive": "Archive",
  "sup.archiveWhy": "Why is {name} archived? (e.g. left the company)",
  "sup.archived": "Archived",
  "sup.noPeople": "No people recorded yet.",
  "sup.addPerson": "Add a person",
  "sup.save": "Save",
  "sup.saved": "Saved",
  "sup.cancel": "Cancel",
  "sup.wechat": "WeChat chats",
  "sup.wechatMeta": "their messages show under Messages",
  "sup.noWechat": "No WeChat chat linked.",
  "sup.linkChat": "Link a WeChat chat",
  "sup.pickChat": "Choose a chat EmmA has seen",
  "sup.chatMessages": "{count} messages",
  "sup.link": "Link",
  "sup.linked": "Linked",
  "sup.unlink": "Unlink",
  "sup.unlinkConfirm": "Unlink “{chat}” from this supplier? Its messages stay in WeChat.",
  "sup.unlinked": "Unlinked",
  "sup.tab.messages": "Messages",
  "sup.ch.email": "Email",
  "sup.ch.wechat": "WeChat",
  "sup.ch.record": "Order record",
  "sup.dir.in": "from them",
  "sup.dir.out": "from us",
  "sup.dir.note": "recorded",
  "sup.noMessages": "Nothing yet. Link their WeChat chat, or their email replies appear here once the PO Office reads them.",
  "sup.messagesFootnote": "Email replies come from the PO Office mailboxes; WeChat from EmmA's bridge. Anything sent to a supplier is still approved by a person.",
  // Receiving on an order (supplier hub PR 3, 16 Oct 2026).
  "po.receive.title": "Receive delivery",
  "po.receive.pending": "{count} line(s) still to arrive",
  "po.receive.allIn": "everything has arrived",
  "po.receive.closed": "what arrived",
  "po.receive.received": "Arrived",
  "po.receive.now": "Arriving now",
  "po.receive.nowFor": "Arriving now: {item}",
  "po.receive.where": "Put away at",
  "po.receive.toJob": "to the Job {job}",
  "po.receive.everything": "Everything arrived",
  "po.receive.on": "Arrived on",
  "po.receive.note": "Note",
  "po.receive.notePlaceholder": "e.g. 6 rolls, no damage",
  "po.receive.over": "More than was ordered — check the quantities.",
  "po.receive.nothing": "Enter what arrived, or tap Everything arrived.",
  "po.receive.closes": "Everything ordered will have arrived — the order closes.",
  "po.receive.part": "A part delivery — the order stays open for the rest.",
  "po.receive.record": "Record delivery",
  "po.receive.history": "{count} receipt(s) recorded",
  "po.receive.line": "Line {line}",
  // Supplier promotions (supplier hub PR 4, 16 Oct 2026).
  "sup.promo": "promo",
  "sup.promos": "Promotions",
  "sup.promosMeta": "what this supplier offers us — information, no price changes",
  "sup.promoNow": "{count} supplier promotion(s) on today:",
  "sup.promo.until": "until {day}",
  "sup.promo.title": "Promotion",
  "sup.promo.titlePlaceholder": "e.g. 10% off engineered oak",
  "sup.promo.covers": "Covers",
  "sup.promo.which": "Which",
  "sup.promo.offer": "Offer",
  "sup.promo.percent": "Percent off",
  "sup.promo.percentOff": "{value}% off",
  "sup.promo.priceExGst": "Price, EX GST",
  "sup.promo.unit": "Per",
  "sup.promo.dates": "Dates",
  "sup.promo.from": "From",
  "sup.promo.to": "To",
  "sup.promo.source": "Came by",
  "sup.promo.sourceRef": "Which message, chat, file or person",
  "sup.promo.terms": "Terms",
  "sup.promo.upcoming": "starts later",
  "sup.promo.none": "No current or upcoming promotion recorded.",
  "sup.promo.past": "{count} past promotion(s)",
  "sup.promo.add": "Record a promotion",
  "sup.promo.end": "End early",
  "sup.promo.endWhy": "Why does “{title}” end early? (e.g. withdrawn)",
  "sup.promo.ended": "Ended",
  "sup.promo.footnote": "A promotion changes no price. To quote or order at the promotional cost, set it as a catalogue price.",
  "sup.promo.onOrder": "This supplier is offering:",
  "sup.promo.see": "See promotions",
  "sup.offer.percent_off": "% off",
  "sup.offer.price": "Special price",
  "sup.offer.free_freight": "Free freight",
  "sup.offer.bonus": "Bonus stock",
  "sup.offer.other": "Other",
  "sup.scope.all": "Everything",
  "sup.scope.category": "Category",
  "sup.scope.collection": "Collection",
  "sup.scope.product": "Product",
  "sup.scope.variant": "Colour",
  "sup.src.pdf": "PDF / flyer",
  "sup.src.rep": "Their rep",
  // Files on every Opportunity and Job (18 Oct 2026): the Files panel.
  "files.title": "Files",
  "files.lede": "Plans, photos, supplier and customer papers for this work. Up to 100 MB each. A hidden file is never deleted.",
  "files.carried": "Files added on the opportunity are listed here too.",
  "files.types": "PDF, photos, videos, DWG/DXF drawings, Word, Excel, CSV",
  "files.drop": "Drop files here, or",
  "files.choose": "choose files",
  "files.category": "Category",
  "files.cat.plans": "Plans",
  "files.cat.photos": "Photos",
  "files.cat.supplier": "Supplier",
  "files.cat.customer": "Customer",
  "files.cat.other": "Other",
  "files.note": "Note (optional)",
  "files.notePlaceholder": "What is this file?",
  "files.tooBig": "{name} is over 100 MB and was not uploaded.",
  "files.empty": "No files yet.",
  "files.loading": "Loading files…",
  "files.loadFailed": "Files could not be loaded.",
  "files.retry": "Retry",
  "files.showHidden": "Show hidden",
  "files.hide": "Hide",
  "files.restore": "Restore",
  "files.hidden": "Hidden",
  "files.planTag": "Plan",
  "files.fromOpportunity": "From the opportunity",
  "files.unfinished": "Upload not finished: drop the same file again",
  "files.open": "Open",
  "files.openLink": "Open {name}",
  "files.openFailed": "The file could not be opened.",
  "files.editNote": "Note",
  "files.saveNote": "Save note",
  "files.cancel": "Cancel",
  "files.by.you": "You",
  "files.by.staff": "Staff",
  "files.hashing": "Checking {name}…",
  "files.reserving": "Reserving {name}…",
  "files.uploading": "Uploading {name}: {pct}%",
  "files.verifying": "Confirming {name}…",
  "files.done": "{name} kept.",
  "files.already": "{name} was already kept here.",
  "files.pending": "{name} is waiting for approval and was not uploaded.",
  "files.failed": "{name} was not uploaded: {reason}",
  "files.saveFailed": "Not saved: {reason}",
  // Carpet Tile is its own finish, not roll carpet (18 Oct 2026).
  "canvas.carpetTileNote": "Carpet Tile · {area} m² — priced per m². No roll cut plan is needed.",
  // Room choice at sell prices — the customer picks a floor per area or room by room (18 Oct 2026, Minto).
  "customer.roomChoice.aria": "Choose a floor for each area",
  "customer.roomChoice.title": "Choose your floor, area by area",
  "customer.roomChoice.intro": "Installed prices, excluding GST. Our recommendation is set; change any area and the total updates.",
  "customer.roomChoice.total": "Your total",
  "customer.roomChoice.reset": "Back to our recommendation",
  "customer.roomChoice.groupSize": "{count} rooms · {area} m²",
  "customer.roomChoice.area": "{area} m²",
  "customer.roomChoice.areaAmount": "{area} m² · {amount}",
  "customer.roomChoice.pinned": "One floor only here",
  "customer.roomChoice.recommended": "Recommended",
  "customer.roomChoice.mixed": "Mixed",
  "customer.roomChoice.mixRooms": "Choose room by room",
  "customer.roomChoice.hideRooms": "Hide the rooms",
  "customer.roomChoice.otherRooms": "Other rooms",
  "customer.roomChoice.fixedTitle": "Included whatever you choose",
  "customer.roomChoice.planTitle": "Your plan",
  "customer.roomChoice.stepTotal": "This step",
  "customer.roomChoice.budgetTitle": "Only if needed, approved by you on the day",
  "customer.roomChoice.budgetNote": "Not in the price. Priced and agreed now; charged only if we use it, and you approve it on the day.",
  "customer.roomChoice.grandTotal": "Your price",
  "customer.roomChoice.pricedThen": "priced then",
  "customer.doc.callJacky": "To go ahead, call Jacky on 0432 399 295. We plan the floors into your renovation and work to your schedule.",
  "customer.roomChoice.colourTitle": "Colour",
  "customer.roomChoice.colourNone": "Choose a colour, or leave it for later",
  "customer.roomChoice.details": "Details",
  "customer.roomChoice.cutting": "Rates are per m² of board. The amounts include {percent}% for cutting and waste on {area} m² measured; the final charge is by actual measurement.",
  "commercial.roomChoiceTitle": "Room choice at sell prices (working draft)",
  "commercial.roomChoiceTotal": "Recommended total {amount} + GST",
  "commercial.roomChoiceNote": "Saved by EmmA (save_room_choice_offer). \"Release version\" publishes this room choice at the same private link instead of the builder's lines; the customer chooses per area and nothing cost-like is in the copy.",
  "commercial.roomChoiceProducts": "Products",
  "commercial.roomChoiceAnyRoom": "any room",
  "commercial.roomChoiceRooms": "{count} rooms",
  "commercial.roomChoiceCutting": "+{percent}% cutting allowance on the m²",
  "commercial.roomChoiceGroups": "Groups",
  "commercial.roomChoicePinned": "pinned to {product}: {reason}",
  "commercial.roomChoiceFixed": "Fixed lines",
  "commercial.roomChoiceBudget": "budget, charged on what is used",
  "commercial.roomChoiceProfile": "{count} colours and the spec on the page",
  // Room choice vs CRM total before release (23 Oct 2026, Minto)
  "commercial.roomChoiceMismatch": "The CRM prices this Proposal at {crm} but the room choice the customer sees prices it at {page} (EX GST). Add the missing work to the room choice as a fixed line, then release.",
  // EmmA's private Proposal brief (18 Oct 2026)
  "proposal.emmaBrief.title": "EmmA brief",
  "proposal.emmaBrief.blurb": "What EmmA holds to when she talks with this customer on their Proposal page. Staff only: the customer never sees it.",
  "proposal.emmaBrief.kind": "Site",
  "proposal.emmaBrief.residential": "Residential",
  "proposal.emmaBrief.commercial": "Commercial",
  "proposal.emmaBrief.conditions": "Conditions",
  "proposal.emmaBrief.conditionsHint": "A condition of this job EmmA must hold to",
  "proposal.emmaBrief.scenarios": "If the customer… / EmmA says…",
  "proposal.emmaBrief.if": "If the customer…",
  "proposal.emmaBrief.say": "EmmA says…",
  "proposal.emmaBrief.avoid": "Leave alone",
  "proposal.emmaBrief.avoidHint": "Something EmmA should not raise or promise",
  "proposal.emmaBrief.add": "Add",
  "proposal.emmaBrief.remove": "Remove",
  "proposal.emmaBrief.private": "Saved with the draft. Never in the released copy; EmmA can also set it on your word.",
  "proposal.showcase.from": "Starting from {amount} + GST",
  "proposal.showcase.options": "Your flooring options",
  "proposal.showcase.product": "Product",
  "proposal.showcase.rate": "Rate basis / m²",
  "proposal.showcase.flooring": "Flooring total",
  "proposal.showcase.upgrade": "Step up",
  "proposal.showcase.total": "Total",
  "proposal.showcase.exGst": "All amounts are EX GST.",
  "proposal.showcase.base": "Starting option",
  "proposal.showcase.view": "View layout",
  "proposal.showcase.choose": "Choose this floor",
  "proposal.showcase.chosen": "Selected floor",
  "proposal.showcase.whole": "Charges are rounded down to whole dollars before totals are added. All amounts are EX GST.",
  "proposal.showcase.included": "Included in every option",
  "proposal.showcase.scope": "Scope & conditions",
  "proposal.showcase.schedule": "Your proposed sequence",
  "proposal.showcase.proposed": "Proposed dates, subject to access, stock, preparation and confirmation.",
  "proposal.showcase.specs": "Product specifications",
  "proposal.showcase.colours": "Colour references",
  "proposal.showcase.digital": "Digital colours are indicative. Confirm with physical samples. Swatches do not recolour the plan.",
  "proposal.showcase.aiSwatches": "AI illustrative swatches; confirm using physical samples.",
  "proposal.showcase.hero": "AI room and finish illustration; not the customer’s property or a dimensionally accurate board layout.",
  "proposal.showcase.source": "Manufacturer reference",
  "proposal.showcase.package": "Agreed flooring package",
  "proposal.showcase.details": "Detailed scope, pricing & additional choices",
  "proposal.showcase.plan": "Explore the plank layout",
  "proposal.showcase.planIntro": "A neutral oak illustration shows format, direction and joint rhythm. It does not simulate product colour or revise the quoted quantities.",
  "proposal.showcase.estimated": "Estimated plan · not site measured",
  "proposal.showcase.measured": "Saved site measurements",
  "proposal.showcase.all": "All included rooms",
  "proposal.showcase.zoom": "Enlarge layout",
  "proposal.showcase.close": "Close",
  "proposal.showcase.horizontal": "Across",
  "proposal.showcase.vertical": "Lengthwise",
  "proposal.showcase.room": "Room",
  "proposal.showcase.area": "{area} m²",
  "proposal.showcase.rules": "Random stagger ≥ {percent}% · end pieces ≥ {end} mm · balanced side cuts",
  "proposal.showcase.irregular": "This room needs a site-confirmed set-out before individual board cuts can be shown.",
  "proposal.showcase.herringbone": "Herringbone at 45° · spine set square to the {room} wall and centred down it · both sides balanced",
  "proposal.showcase.oneRun": "One continuous run through every room in scope, boards drawn at their real size on your plan; lines are shown only where there is a wall.",
  "proposal.showcase.robe": "BIR",
  "proposal.showcase.visual": "Visual layout only; not an installation or cutting plan. Follow the selected product’s instructions and confirmed site conditions.",
  "proposal.showcase.faq": "Flooring FAQ",
  "proposal.showcase.method": "How Artemis lays your floor",
  "proposal.showcase.unpriced": "Price to be confirmed",
  "presentation.title": "Presentation",
  "presentation.intro": "Product stories use the existing quote and customer choices. Apply edits before changing tabs; then save or preview the Proposal.",
  "presentation.enable": "Create from selected products",
  "presentation.apply": "Apply presentation edits",
  "presentation.disable": "Use standard presentation",
  "presentation.rounding": "Round every charge line down to whole dollars",
  "presentation.heading": "Heading",
  "presentation.copy": "Description",
  "presentation.basis": "Measurement basis",
  "presentation.inclusions": "Inclusions · one per line",
  "presentation.rules": "Neutral layout rules",
  "presentation.stagger": "Minimum joint stagger (%)",
  "presentation.end": "Minimum end piece (mm)",
  "presentation.balance": "Balance opposite side cuts",
  "presentation.name": "Name",
  "presentation.tier": "Level description",
  "presentation.benefit": "What the upgrade adds",
  "presentation.hero": "Hero image URL",
  "presentation.alt": "Image description",
  "presentation.colour": "Illustrated colour",
  "presentation.illustrative": "AI illustration",
  "presentation.source": "Manufacturer source URL",
  "presentation.image": "Image URL",
  "presentation.scheduleHint": "Optional and specific to this job. These stages do not book the installation calendar.",
  "presentation.date": "Proposed date",
  "presentation.newStage": "New stage",
  "presentation.addStage": "Add stage",
  "presentation.scheduleNote": "Schedule conditions",
  "presentation.applied": "Applied to the draft. Save or preview to review the resulting Proposal.",
  "presentation.configureProducts": "Price a recommended product first; the presentation follows the pricing.",
  "presentation.configError": "Could not load presentation defaults.",
  "proposal.showcase.commonCharges": "Common charges · included in the totals above",
  "presentation.measurementNote": "Measurement disclaimer",
  "proposal.showcase.optionalWork": "Optional work to review onsite",
  "proposal.showcase.separatePrice": "Excluded from this Proposal total. Any option needs its own scope, rate and agreement before proceeding.",
  "presentation.addOption": "Add separate work option",
  "presentation.newOption": "Separate work option",
  "presentation.informational": "Information copy — no online choices or acceptance",
  "proposal.showcase.information": "For information and discussion. No online selection or acceptance is required.",
  // Installer scope sheet and the crew's WeChat group (24 Oct 2026).
  "scopeLink.title": "Scope to assess",
  "scopeLink.unavailable": "This link is not available. Ask the Artemis office for a new one.",
  "scopeLink.site": "Site",
  "scopeLink.work": "Work for you",
  "scopeLink.included": "included in the floor rate",
  "scopeLink.pleaseQuote": "Please give your price",
  "scopeLink.total": "total",
  "scopeLink.totalExGst": "Your total (ex GST)",
  "scopeLink.toQuote": "{count} to price",
  "scopeLink.days": "About {days} day(s) of work.",
  "scopeLink.available": "Days you can start",
  "scopeLink.availableHelp": "Tick every day you can come. Pick as many as you like.",
  "scopeLink.note": "Questions or notes",
  "scopeLink.decline": "Can't take it",
  "scopeLink.accept": "Accept and send",
  "scopeLink.pickDay": "Tick at least one day you can start.",
  "scopeLink.needPrice": "Give a price for every task marked \"Please give your price\".",
  "scopeLink.saved": "Sent to the Artemis office.",
  "scopeLink.superseded": "The office sent a newer version of this job. Open the latest link.",
  "scopeLink.approved": "The office has agreed this job. Call the office to change anything.",
  "scopeLink.answeredAccept": "You accepted. You can change your answer until the office agrees it.",
  "scopeLink.answeredDecline": "You declined. You can still accept below.",
  "scopeLink.exGst": "Amounts are ex GST.",
  "scope.title": "Scope for installer",
  "scope.help": "The work from the latest saved Proposal, without customer prices. Tick what this crew does, set their rate or ask them to quote, then send a short message with their private link.",
  "scope.crew": "Crew",
  "scope.pickCrew": "Choose a crew",
  "scope.loading": "Loading…",
  "scope.unavailable": "Could not load: {message}",
  "scope.noTasks": "This Job has no accepted work to send yet.",
  "scope.mode.book": "Book",
  "scope.mode.manual": "Manual",
  "scope.mode.quote": "Quote",
  "scope.included": "Included in floor rate",
  "scope.ownCrew": "Own crew · no fee",
  "scope.fromBook": "From their book",
  "scope.toQuote": "Installer to quote",
  "scope.notAssigned": "Not assigned",
  "scope.lumpSum": "Fee",
  "scope.known": "Known so far (ex GST)",
  "scope.plusQuote": "{amount} + {count} to quote",
  "scope.language": "Crew language",
  "scope.note": "Note to crew",
  "scope.noteLocal": "Note in Chinese",
  "scope.labelLocal": "Chinese",
  "scope.days": "About how many days (optional)",
  "scope.translate": "Translate with EmmA",
  "scope.translating": "Translating…",
  "scope.translateFailed": "EmmA could not translate just now; type the Chinese.",
  "scope.sendBy": "Send by",
  "scope.sms": "Text message",
  "scope.wechat": "EmmA in WeChat · {group}",
  "scope.wechatHelp": "The WeChat option appears only when the crew has a WeChat group linked (on the crew page).",
  "scope.recipient": "To",
  "scope.noRecipient": "No mobile number is on file for this crew.",
  "scope.message": "Message",
  "scope.regenerate": "Rewrite from the ticks",
  "scope.linkNote": "{link} becomes the crew's private link when sent. Nothing else changes.",
  "scope.preview": "Preview as crew",
  "scope.hidePreview": "Hide preview",
  "scope.send": "Send text",
  "scope.sendWechat": "Send in WeChat",
  "scope.sending": "Sending…",
  "scope.sent": "Sent. The crew's answer will show here.",
  "scope.queued": "Queued; leaves at {time} (quiet hours).",
  "scope.pending": "Waiting for approval on the desk.",
  "scope.failed": "Not sent: {message}",
  "scope.history": "Sent to crews",
  "scope.sheetLine": "v{version} · {date} · {channel} · {state}",
  "scope.replaced": "replaced",
  "scope.noAnswer": "No answer yet",
  "scope.accepted": "{crew} accepted",
  "scope.declined": "{crew} declined",
  "scope.availableOn": "Available {dates}",
  "scope.asks": "Asks {price} for {task}",
  "scope.feeIfApproved": "Installer fee if approved: {total} + GST",
  "scope.approve": "Approve price",
  "scope.approving": "Approving…",
  "scope.approved": "Agreed · fee recorded {total} + GST",
  "scope.book": "Book these dates",
  "scope.resend": "Correct and resend",
  "scope.chooseOne": "Tick at least one task.",
  "scope.needFee": "Give a rate or a fee for every Manual task.",
  "scope.msgState": "Message: {state}",
  "crewWechat.title": "WeChat group",
  "crewWechat.help": "EmmA can announce scope sheets in this crew's WeChat group. Only a linked group is offered.",
  "crewWechat.none": "No group linked.",
  "crewWechat.linked": "Linked: {group}",
  "crewWechat.pick": "Choose a group EmmA is in",
  "crewWechat.link": "Link group",
  "crewWechat.unlink": "Unlink",
  "crewWechat.saved": "Saved.",
  "crewWechat.failed": "Not saved: {message}",
  "crewWechat.loading": "Loading groups…",
  "scope.msg.hello": "Hi {crew}, a new job to assess:",
  "scope.msg.days": "About {days} day(s).",
  "scope.msg.quote": "Open the link for the details, your price and the dates you can start:",
  "scope.msg.open": "Open the link for the details and the dates you can start:",
  "scope.msg.signEmma": "— EmmA · Artemis Flooring",
  "scope.msg.sign": "— Artemis Flooring",
  "scope.msg.more": "and more",
  "scope.msg.join": ", ",
  "scopeLink.failed": "Not sent. Your answer is kept here; try again, or call the Artemis office.",
  // Installer fee payments, per Job (25 Oct 2026)
  "crewpay.title": "Jobs and payments",
  "crewpay.meta": "Each Job this crew worked on, its agreed fee and whether it is paid. Ex GST.",
  "crewpay.loading": "Loading payments…",
  "crewpay.unavailable": "Payments are unavailable right now. Refresh to try again.",
  "crewpay.ownCrew": "An Artemis crew is paid as staff. Own-crew labour is always $0 on a Job.",
  "crewpay.none": "No agreed fee on any Job for this crew yet.",
  "crewpay.paysBy": "Pays by {method}",
  "crewpay.noPrefYet": "No payment preference recorded",
  "crewpay.stillToPay": "Still to pay",
  "crewpay.col.job": "Job",
  "crewpay.col.agreed": "Agreed",
  "crewpay.col.paid": "Paid",
  "crewpay.col.status": "Status",
  "crewpay.status.unpaid": "Not paid",
  "crewpay.status.part_paid": "Part paid",
  "crewpay.status.paid": "Paid",
  "crewpay.noFee": "no agreed fee",
  "crewpay.invoiceNo": "invoice {number}",
  "crewpay.markPaid": "Mark paid",
  "crewpay.details": "Payments",
  "crewpay.open": "Crew payments",
  "crewpay.cancel": "Cancel",
  "crewpay.failed": "Not saved: {reason}",
  "crewpay.pending": "waiting for approval",
  "crewpay.pdfNote": "Crew invoice · {crew} · {number}",
  "crewpay.pay.amount": "Amount paid, ex GST",
  "crewpay.pay.method": "Paid by",
  "crewpay.pay.cash": "Cash",
  "crewpay.pay.transfer": "Transfer",
  "crewpay.pay.noPref": "{crew} has no preference recorded: choose one",
  "crewpay.pay.date": "Date paid (Sydney)",
  "crewpay.pay.refCash": "Who handed the cash (optional)",
  "crewpay.pay.refTransfer": "Bank reference (optional)",
  "crewpay.pay.invoice": "Invoice no. (only if they sent one)",
  "crewpay.pay.invoiceBlank": "leave blank",
  "crewpay.pay.invoiceDate": "Invoice date",
  "crewpay.pay.gst": "GST on it (only if they charge it)",
  "crewpay.pay.pdf": "Invoice PDF (optional, kept in the Job's files)",
  "crewpay.pay.bank": "Pay to: {name} · BSB {bsb} · account {account}",
  "crewpay.pay.bankMissing": "No bank details on file for this crew. Add them with Edit this crew.",
  "crewpay.pay.save": "Save",
  "crewpay.pay.help": "Up to {balance} still to pay on this Job. No money moves from here.",
  "crewpay.pay.saved": "Marked paid.",
  "crewpay.void": "Void",
  "crewpay.void.reason": "Why is it wrong?",
  "crewpay.void.confirm": "Void it",
  "crewpay.void.done": "Voided. The record and its reason are kept.",
  "crewpay.jobcard.paid": "Paid {method} {date}",
  "crewpay.jobcard.stillToPay": "{amount} ex GST still to pay",
  // One lump sum, oldest Job first (4 Nov 2026).
  "crewpay.lump.open": "Pay a lump sum",
  "crewpay.lump.title": "One lump sum, oldest Job first",
  "crewpay.lump.amount": "Lump sum, ex GST",
  "crewpay.lump.split": "How it is offset",
  "crewpay.lump.pays": "pays {amount}",
  "crewpay.lump.paidInFull": "paid in full",
  "crewpay.lump.left": "{amount} left",
  "crewpay.lump.skipped": "Not paid from a lump sum while a crew-day is still booked ahead: {jobs}",
  "crewpay.lump.over": "That is more than the worked Jobs owe ({owed} ex GST).",
  "crewpay.lump.after": "Still to pay after this: {amount} ex GST",
  "crewpay.lump.help": "No money moves from here. Each Job card will show what it still owes.",
  "crewpay.lump.saved": "Lump sum recorded.",
  "crewpay.lump.tag": "part of a lump sum of {amount} paid {date}",
  "crewpay.lump.void": "Void the lump sum",
  "crewpay.lump.voidHint": "This voids its share on every Job it paid: {jobs}",
  "crewpay.jobcard.paidOf": "{paid} of {agreed} paid",
  "crewpay.jobcard.lump": "from a lump sum",
  "job.ov.feeLeft": "{amount} still to pay",
  "job.ov.feePaid": "paid in full",
  "inst.col.owed": "Still to pay",
  "inst.col.owedHint": "Agreed fees on this crew's Jobs not paid yet, ex GST",
  // The AI agent account on Settings (ADR-034, 25 Oct 2026).
  "agentAccount.title": "AI agent account",
  "agentAccount.scope": "Reads only · can change nothing",
  "agentAccount.what": "Claude Code sessions use this account to see the live system as staff while building and troubleshooting, instead of anyone's own sign-in or browser. Every use is listed here.",
  "agentAccount.status.pending": "Waiting for authorization",
  "agentAccount.status.active": "Authorized",
  "agentAccount.status.disabled": "Disabled",
  "agentAccount.since": "Authorized {when}",
  "agentAccount.disabledAt": "Disabled {when}",
  "agentAccount.waiting": "It can read nothing until its owner authorizes it.",
  "agentAccount.authorize": "Authorize",
  "agentAccount.disable": "Disable",
  "agentAccount.authorized": "Authorized · sessions can read within 30 seconds",
  "agentAccount.disabled": "Disabled · sessions lose access within 30 seconds",
  "agentAccount.ownerOnly": "Only its owner can authorize it.",
  "agentAccount.recent": "Recent use",
  "agentAccount.none": "Not used yet.",
  // Services with no Artemis standard rate (25 Oct 2026).
  "rates.missing.title": "{count} of {total} services have no standard rate",
  "rates.missing.none": "Every service has a standard rate.",
  "rates.missing.help": "Type a figure (ex GST) to add it; it joins the rates below and is kept when you save. {priced} of {total} priced. A crew's own approved rates always come first.",
  // Our projects — the website showcase and the CRM Showcase page (25 Oct 2026).
  "rail.showcase": "Showcase",
  "site.nav.projects": "Our projects",
  "showcase.kicker": "Website",
  "showcase.title": "Showcase",
  "showcase.lede": "Past projects for the website's Our projects page. EmmA prepares and releases every Job with photos; take down anything you don't want. Anonymous: no names, addresses, suburbs or Job numbers.",
  "showcase.loading": "Loading…",
  "showcase.unavailable": "The showcase could not be loaded. Try again shortly.",
  "showcase.pending": "Sent for approval.",
  "showcase.refused": "Not done: {reason}",
  "showcase.start.done": "Draft started. Add photos and write the summary.",
  "showcase.start.existing": "This Job already has an entry; it is open below.",
  "showcase.pick": "Choose a Job on the left.",
  "showcase.status.draft": "Draft",
  "showcase.status.released": "On the website",
  "showcase.status.taken_down": "Taken down",
  // Showcase Jobs list — EmmA prepares and releases (25 Oct 2026)
  "showcase.status.not_started": "Not started",
  "showcase.filter.all": "All",
  "showcase.jobs.title": "Jobs with photos",
  "showcase.jobs.meta": "{released} of {jobs} on the website · {todo} for EmmA",
  "showcase.jobs.search": "Search name, suburb or Job",
  "showcase.jobs.line": "{ref} · laid {laid} · {n} photos",
  "showcase.jobs.notLaid": "no install day",
  "showcase.jobs.empty": "No Job matches.",
  "showcase.jobs.none": "No Job has installation photos yet.",
  "showcase.jobs.waiting": "EmmA has not prepared this Job yet. She looks at every photo, keeps the ones where the floor is the subject, touches them up, writes the summary and puts the entry on the website herself. Take it down at any time.",
  "showcase.jobs.startNow": "Start it myself",
  "showcase.entry.title": "Entry",
  "showcase.entry.job": "From {ref}. The Job number is never shown on the website.",
  "showcase.field.title": "Title",
  "showcase.field.summary": "Summary",
  "showcase.field.summaryHint": "What was done, in a few lines: rooms, floor, finishing. No names or addresses.",
  "showcase.field.anonymous": "No customer names, streets, suburbs, Job numbers, e-mails or phone numbers. Release checks this.",
  "showcase.field.season": "Season",
  "showcase.field.year": "Year",
  "showcase.field.period": "Shown as {period}",
  "showcase.field.floors": "Floor types",
  "showcase.season.summer": "Summer",
  "showcase.season.autumn": "Autumn",
  "showcase.season.winter": "Winter",
  "showcase.season.spring": "Spring",
  "showcase.draft.button": "Ask EmmA to draft",
  "showcase.draft.prompt": "Draft the website showcase summary for {ref} (showcase entry {id}). Read the Job and its accepted Proposal, then write two or three plain sentences on the rooms, the floor and the finishing, and save them with update_showcase_project. No customer name, street, suburb, Job number, e-mail or phone. Do not release it.",
  "showcase.save": "Save",
  "showcase.saved": "Saved.",
  "showcase.saveFirst": "Save your changes before releasing.",
  "showcase.needPhoto": "Add at least one photo before releasing.",
  "showcase.releaseHint": "Release puts the title, summary, season, floor types and photos on the website.",
  "showcase.release": "Release to website",
  "showcase.released": "On the website now.",
  "showcase.takeDown": "Take down",
  "showcase.takenDown": "Taken off the website.",
  "showcase.photos.title": "Photos ({n})",
  "showcase.photos.add": "Add photos from this Job",
  "showcase.photos.closePicker": "Close",
  "showcase.photos.none": "No photos on this entry yet.",
  "showcase.photos.added": "Photo added.",
  "showcase.photos.remove": "Remove",
  "showcase.photos.removed": "Photo removed from the entry. The original is untouched.",
  "showcase.picker.title": "This Job's installation photos",
  "showcase.picker.check": "Pick finished floors where the floor is the subject.",
  "showcase.picker.none": "This Job has no installation photos.",
  "showcase.picker.alt": "Installation photo",
  "showcase.picker.crew": "crew",
  "showcase.picker.on": "On the entry",
  "showcase.picker.add": "Add",
  "showcase.picker.more": "Show more ({n})",
  "showcase.photo.loading": "Loading photo…",
  "showcase.photo.unavailable": "Photo unavailable",
  "showcase.photo.alt": "Showcase photo",
  "showcase.touch.title": "Touch up",
  "showcase.touch.after": "As shown",
  "showcase.touch.before": "Original",
  "showcase.touch.left": "Turn left",
  "showcase.touch.right": "Turn right",
  "showcase.touch.straighten": "Straighten",
  "showcase.touch.trim.top": "Trim top",
  "showcase.touch.trim.right": "Trim right",
  "showcase.touch.trim.bottom": "Trim bottom",
  "showcase.touch.trim.left": "Trim left",
  "showcase.touch.brightness": "Brightness",
  "showcase.touch.contrast": "Contrast",
  "showcase.touch.smooth": "Smooth (gentle clean-up)",
  "showcase.touch.watermark": "Watermark",
  "showcase.touch.save": "Save touch-up",
  "showcase.touch.reset": "Undo changes",
  "showcase.touch.saved": "Touch-up saved.",
  "showcase.touch.savedStale": "Touch-up saved. EmmA's retouch was made before this change, so it is no longer used. Ask her again.",
  "showcase.watermark.bottom-right": "Bottom right",
  "showcase.watermark.bottom-left": "Bottom left",
  "showcase.watermark.top-right": "Top right",
  "showcase.watermark.top-left": "Top left",
  "showcase.watermark.none": "None",
  "showcase.retouch.title": "EmmA's retouch: your decision",
  "showcase.retouch.before": "Now",
  "showcase.retouch.after": "Retouched",
  "showcase.retouch.check": "Check the floor itself: the boards or carpet must look as installed (colour, grain, texture).",
  "showcase.retouch.accept": "Use the retouch",
  "showcase.retouch.reject": "Keep as it was",
  "showcase.retouch.accepted": "The retouch is now the photo shown.",
  "showcase.retouch.rejected": "Kept as it was.",
  "showcase.retouch.inUse": "EmmA's retouch in use",
  "showcase.retouch.stale": "Retouch out of date",
  "showcase.retouch.again": "Ask EmmA to retouch again",
  "showcase.retouch.askEmma": "Ask EmmA to retouch",
  "showcase.retouch.prompt": "Retouch showcase photo {id} with retouch_showcase_photo: remove clutter and even the lighting, keep the floor exactly as installed. It comes back here for a before/after decision.",
  // Price my floor page (25 Oct 2026): summary, filters, sort, cost breakdown, EmmA briefing.
  "website.kicker": "Digital operations",
  "website.title": "Website",
  "website.lede": "One place to operate what customers see: product pricing, acquisition performance and completed-project stories.",
  "website.nav.label": "Website sections",
  "website.nav.overview": "Overview",
  "website.nav.pricing": "Pricing",
  "website.nav.marketing": "Marketing",
  "website.nav.showcase": "Showcase",
  "website.pricingTitle": "Website pricing",
  "website.pricingDescription": "Choose which products display a Price my floor amount, review the cost basis and keep customer prices current.",
  "website.pricingMetric": "{listed} of {total} products currently listed with website pricing",
  "website.pricingAction": "Manage pricing",
  "website.marketingTitle": "Marketing",
  "website.marketingDescription": "Review channel spend, lead outcomes, website traffic and product interest in the existing performance workspace.",
  "website.marketingMetric": "Spend, leads, won jobs and website engagement",
  "website.marketingAction": "Review performance",
  "website.showcaseTitle": "Project showcase",
  "website.showcaseDescription": "Select installation photos, prepare anonymous project stories and control what is released to the public website.",
  "website.showcaseMetric": "{released} released · {drafts} drafts · {total} jobs with photos",
  "website.showcaseAction": "Curate showcase",
  "website.loading": "Loading current figures…",
  "website.unavailable": "Current figures are unavailable; open the workspace to retry.",
  "website.operatingTitle": "One website, three operational workflows",
  "website.operatingNote": "These cards open the existing pricing, marketing and showcase workspaces. Counts come from their saved records; no publishing or communication happens from this overview.",
  "pmf.kicker": "Website",
  "pmf.title": "Price my floor",
  "pmf.lede": "Listed products show their price on the website and can be chosen in Price my floor. Everything else reads “Available upon request”.",
  "pmf.search": "Find a product, supplier or category",
  "pmf.segment.residential": "Residential",
  "pmf.segment.commercial": "Commercial",
  "pmf.segment.all": "All",
  "pmf.filter.category": "All categories",
  "pmf.filter.supplier": "All suppliers",
  "pmf.filter.status": "Any status",
  "pmf.filter.listed": "Listed",
  "pmf.filter.ready": "Ready to list",
  "pmf.filter.blocked": "Can't list",
  "pmf.filter.loss": "Below total cost",
  "pmf.filter.unresolved": "Cost not resolved",
  "pmf.briefEmma": "Brief EmmA",
  "pmf.briefEmma.prompt": "Price my floor — read price_my_floor first (and price_my_floor_cost for any product whose margin matters). Then make this change, and report each product's list price before → after: ",
  "pmf.sum.category": "Category",
  "pmf.sum.products": "Products",
  "pmf.sum.listed": "Listed with price",
  "pmf.sum.ready": "Ready",
  "pmf.sum.blocked": "Can't list",
  "pmf.sum.loss": "Below cost",
  "pmf.sum.unresolved": "Cost not resolved",
  "pmf.sum.all": "All categories",
  "pmf.units": "All figures per m² · ex GST",
  "pmf.col.product": "Product",
  "pmf.col.supplier": "Supplier",
  "pmf.col.supplierCost": "Supplier cost",
  "pmf.col.install": "+ Install",
  "pmf.col.total": "= Total cost",
  "pmf.col.price": "List price",
  "pmf.col.gm": "GM",
  "pmf.col.website": "Website shows",
  "pmf.col.listed": "Listed",
  "pmf.col.sortBy": "Sort by {column}",
  "pmf.install.separate": "separate",
  "pmf.cat.listedOf": "{listed} of {total} listed",
  "pmf.cat.listReady": "List all {count} ready",
  "pmf.cat.included": "Standard installation included:",
  "pmf.cat.noInstall": "No standard installation on file — GM cannot be shown",
  "pmf.cat.supplyOnly": "Installation per method (part of each price):",
  "pmf.cat.cost": "cost {amount}",
  "pmf.cat.price": "price",
  "pmf.cat.setPrice": "set price",
  "pmf.cat.gm": "{pct}% GM",
  "pmf.cat.costUnknown": "cost unknown",
  "pmf.cat.perM2": "{amount}/m²",
  "pmf.cat.installPrice": "{name} price per m²",
  "pmf.status.from": "From {price} + GST",
  "pmf.status.request": "Available upon request",
  "pmf.status.notOnWebsite": "not on the website",
  "pmf.refusal.noPrice": "No sell price",
  "pmf.refusal.notM2": "Not priced per m²",
  "pmf.refusal.atCost": "Sells at or below cost",
  "pmf.tip.unresolved": "The Proposal engine cannot cost this product: the figure is a raw catalogue row. Expand the row to see why.",
  "pmf.tip.price": "{inc} inc. GST — click to change",
  "pmf.tip.setPrice": "Click to set a price",
  "pmf.tip.gmNeedsCost": "GM needs the supplier cost and the installation cost",
  "pmf.tip.gmLoss": "Cost is above the list price — click to set a margin",
  "pmf.tip.gm": "Click to set a margin; the price follows, rounded up to the whole dollar",
  "pmf.tip.listed": "Listed — switch off to show “Available upon request”",
  "pmf.tip.list": "Switch on to price it on the website",
  "pmf.tip.noInstall": "No standard installation for this product's method",
  "pmf.tip.separate": "{name} — priced separately",
  "pmf.switch.list": "List {name}",
  "pmf.switch.unlist": "Unlist {name}",
  "pmf.edit.price": "{name} list price",
  "pmf.edit.gm": "{name} margin",
  "pmf.set": "set",
  "pmf.done.listed": "Listed",
  "pmf.done.unlisted": "Unlisted",
  "pmf.done.price": "List price {price}",
  "pmf.loading": "Loading the catalogue…",
  "pmf.none": "No products match.",
  "pmf.tally.listed": "listed on the website",
  "pmf.tally.ready": "ready to list",
  "pmf.tally.blocked": "can't be listed until the catalogue price is fixed",
  "pmf.tally.loss": "priced below total cost",
  "pmf.tally.unresolved": "costs not resolved",
  "pmf.foot": "Every price is supplied & installed. Engineered timber has one per installation method (Floating, Direct glued). Click a product to see how its cost is worked out; click a price or GM to change it (rounded up to the whole dollar). Cost = supplier cost + installation + the included underlay and scotia at the manufacturer's prices, with no waste — the customer pays waste. Costs and GM are for staff and never leave the CRM.",
  "pmf.sort.group": "Group by category",
  "pmf.col.included": "+ Included",
  "pmf.tip.includedMissing": "No {what} for this manufacturer yet — set it under Included accessories by manufacturer",
  "pmf.inc.missing": "missing",
  "pmf.inc.m.underlay": "underlay price",
  "pmf.inc.m.scotia": "scotia price",
  "pmf.method.floating": "Floating",
  "pmf.method.glued": "Direct glued",
  "pmf.method.herringbone": "Herringbone",
  "pmf.method.chevron": "Chevron",
  "pmf.method.standard": "Installed",
  "pmf.inc.includes": "Includes:",
  "pmf.inc.item.installation": "Installation",
  "pmf.inc.item.underlay": "Underlay",
  "pmf.inc.item.scotia": "Scotia",
  "pmf.inc.optional": "optional: trims, moisture barrier",
  "pmf.inc.saved": "Included accessory prices saved",
  "pmf.inc.title": "Included accessories by manufacturer",
  "pmf.inc.scotiaRule": "Underlay on floating floors + scotia at {lm} LM per m² of floor, no waste",
  "pmf.inc.missingCount": "{count} missing a price",
  "pmf.inc.manufacturer": "Manufacturer",
  "pmf.inc.underlay": "Underlay $/m²",
  "pmf.inc.scotia": "Scotia $/LM",
  "pmf.inc.note": "Note",
  "pmf.inc.underlayFor": "Underlay price for {name}",
  "pmf.inc.underlayTip": "0 = attached to the board, or not used",
  "pmf.inc.attached": "attached",
  "pmf.inc.glued": "glued — no underlay",
  "pmf.inc.scotiaFor": "Scotia price for {name}",
  "pmf.inc.help": "Ex GST. Every hard floor of that manufacturer and category uses its row. A blank price means no estimator cost and no GM for those floors. Trims and moisture barrier are optional and not included.",
  "pmf.inc.unknown": "not set",
  "pmf.inc.none": "none",
  "pmf.x.included": "Included · underlay + scotia",
  "pmf.x.includedHow": "underlay {underlay}/m² + scotia {scotia}/m² · manufacturer table, no waste",
  "pmf.x.methodHow": "cost {cost} · {gm}% GM",
  "pmf.edit.methodPrice": "{name} {method} price",
  "pmf.tip.methodPrice": "This method's supplied & installed price — click to change",
  "pmf.cat.timberMethods": "Each installation method has its own supplied & installed price — set the price or the GM on each line.",
  "pmf.edit.methodGm": "{name} {method} margin",
  "pmf.x.title": "How the cost is worked out",
  "pmf.x.loading": "Working out the cost…",
  "pmf.x.colour": "Colour",
  "pmf.x.cost": "Supplier cost",
  "pmf.x.perM2": "Per m²",
  "pmf.x.source": "Source",
  "pmf.x.src.price_history": "Price list, effective {date}",
  "pmf.x.src.catalogue_column": "Catalogue column",
  "pmf.x.src.default_row": "Inherits the default row",
  "pmf.x.src.none": "No cost",
  "pmf.x.dearest": "dearest — used",
  "pmf.x.perUnit": "{amount} / {unit}",
  "pmf.x.width": "Roll width {width} m: per LM ÷ width = per m²",
  "pmf.x.noColours": "No customer colours on file.",
  "pmf.x.fallback": "The Proposal engine cannot cost this product ({issues}). The page uses {cost}/m² from the catalogue row “{row}”{unit}. Fix the colours' costs (or the roll width) in the catalogue to resolve it.",
  "pmf.x.fallbackUnit": " (unit recorded: {unit})",
  "pmf.x.fallbackNoUnit": " (no unit recorded)",
  "pmf.x.issue.material": "colours have no material cost",
  "pmf.x.issue.carpet": "colours have no per-LM cost or roll width",
  "pmf.x.issue.width": "no roll width to state the per-LM cost per m²",
  "pmf.x.install": "Installation · {name}",
  "pmf.x.installParts": "labour {labour}{glue} per {unit}{width}{underlay} · Proposal pricing",
  "pmf.x.glue": " + glue {amount}",
  "pmf.x.underlay": " + underlay {amount}/m²",
  "pmf.x.perWidth": " ÷ {width} m roll",
  "pmf.x.noInstall": "No installation cost: the product's method has no rate, or a carpet has no roll width.",
  "pmf.x.installSeparate": "{name} — sold separately at {price}/m² (not in this cost)",
  "pmf.x.installUnpriced": "{name} — sold separately, not priced yet",
  "pmf.x.total": "Total cost",
  "pmf.x.totalHow.installed": "supplier cost + installation + included underlay/scotia",
  "pmf.x.totalHow.supply": "dearest colour (supply only)",
  "pmf.x.gm": "GM",
  "pmf.x.gmHow": "({price} − {cost}) ÷ {price}",
  "pmf.x.check": "The Listed switch checks {cost} (the dearest raw catalogue row), not the {page} used here.",
  "pmf.x.notIncluded": "Not in this cost",
  "pmf.x.ni.waste": "waste and offcuts (the customer pays for them)",
  "pmf.x.ni.accessories": "trims and moisture barrier (optional)",
  "pmf.x.ni.prep": "floor preparation and removal",
  "pmf.x.changed": "Listing changed {day}",
  "pmf.x.brief": "Brief EmmA on this product",
  "pmf.x.briefPrompt": "Price my floor · {name} ({id}): read price_my_floor_cost for it first, then make this change and report the list price before → after: ",
  "scope.fromStandard": "Artemis standard rate",
  "scope.headerButton": "Send to installer",

  // Build tracker (25 Oct 2026): what was added, changed or fixed on each feature, in plain words.
  "bt.kicker": "System",
  "bt.title": "Build tracker",
  "bt.lede": "What was added, changed or fixed on each page, newest first. Every code change that touches a page is written down here in plain words.",
  "bt.all": "All pages",
  "bt.feature": "Page",
  "bt.count": "{count} changes",
  "bt.kind.added": "Added",
  "bt.kind.changed": "Changed",
  "bt.kind.fixed": "Fixed",
  "bt.kind.internal": "Internal",
  "bt.showInternal": "Show internal changes",
  "bt.hideInternal": "Hide internal changes",
  "bt.pr": "PR #{pr}",
  "bt.backfill": "backfilled from the pull request",
  "bt.empty": "Nothing registered for this page yet.",
  "bt.unavailable": "The build tracker could not be loaded.",
  "bt.search": "Search changes",
  // Crew payment link (26 Oct 2026)
  "paylink.title": "Payment summary",
  "paylink.unavailable": "This link is not available. Ask Artemis Flooring for a new one.",
  "paylink.expired": "This link has expired. Ask Artemis Flooring for a new one.",
  "paylink.replaced": "A newer link was sent to you. Please open the latest one.",
  "paylink.codeTo": "We'll text a 6-digit code to the mobile ending {tail}.",
  "paylink.codeToUnknown": "We'll text a 6-digit code to the mobile on your crew record.",
  "paylink.sendCode": "Text me a code",
  "paylink.codeSent": "Code sent to the mobile ending {tail}. It works for 10 minutes.",
  "paylink.enterCode": "Enter the 6-digit code",
  "paylink.open": "Open",
  "paylink.wrongCode": "That code is not right. Try again, or text yourself a new one.",
  "paylink.failed": "Something went wrong. Try again, or call the Artemis office.",
  "paylink.asOf": "As of {date}",
  "paylink.stillToPay": "Still to pay",
  "paylink.nothingOwing": "Nothing owing on finished Jobs.",
  "paylink.paid": "Paid",
  "paylink.nothingPaid": "No payment recorded yet.",
  "paylink.upcoming": "Upcoming Jobs",
  "paylink.nothingUpcoming": "No Job booked ahead.",
  "paylink.starts": "starts {date}",
  "paylink.feeNotConfirmed": "fee not confirmed yet",
  "paylink.cash": "cash",
  "paylink.transfer": "bank transfer",
  "paylink.partPaid": "Part paid",
  "paylink.notPaid": "Not paid",
  "paylink.invoice": "invoice {number}",
  "paylink.exGst": "Amounts ex GST. Fees shown are the ones Artemis confirmed to you.",
  "paylink.expires": "This link works until {date}.",
  "paylink.help": "Questions? Call the Artemis office.",
  "crewpay.link.title": "Payment link",
  "crewpay.link.lede": "Send this crew a private page with what is still to pay, what was paid and their upcoming Jobs. They open it with a code texted to their mobile. It works for 7 days; sending again replaces it.",
  "crewpay.link.by": "Send by",
  "crewpay.link.sms": "Text",
  "crewpay.link.wechat": "WeChat group",
  "crewpay.link.noWechat": "No WeChat group linked",
  "crewpay.link.noMobile": "Save a mobile for this crew first: the code is texted to it.",
  "crewpay.link.to": "To {to}",
  "crewpay.link.quiet": "A text waits out the SMS quiet hours.",
  "crewpay.link.send": "Send payment link",
  "crewpay.link.sent": "Sent. The link works for 7 days.",
  "crewpay.link.history": "Links sent",
  "crewpay.link.row": "{when} · {channel} · until {until}",
  "crewpay.link.opened": "opened {count}×",
  "crewpay.link.replaced": "replaced",
  "crewpay.link.expired": "expired",
  "crewpay.link.message": "Hi {crew}, your Artemis payment summary is ready: what is still to pay, what was paid and your upcoming jobs. Open it and enter the code we text you: {link} (works for 7 days)",
  "scope.msgFailed": "Not delivered: {reason}. Fix the cause, then resend.",
  "scope.resendFailed": "Resend",
  "scope.msg.pay": "Your pay for this job: {amount} + GST",
  "scope.msg.payPlusQuote": "Your pay for this job: {amount} + GST, plus {count} task(s) for you to price",
  "scope.msg.quoteOnly": "{count} task(s) for you to price",
  // Customer carpet page without a cut diagram, and the rounding line in its own words (P-1126-10336, 3 Nov 2026).
  'customer.doc.floorCovered': 'Floor covered',
  'customer.doc.roundingLine': 'Rounded down to the whole dollar',
  // Supplier statements and Xero bills (4 Nov 2026).
  'sup.view.suppliers': "Suppliers",
  'sup.view.accounts': "Statements & bills",
  'sup.tab.accounts': "Statements & bills",
  'acct.title': "Statements & bills",
  'acct.meta': "What we owe each supplier, the credit they hold for us, and the Xero bills waiting for your approval. Amounts inc GST, as on the statement.",
  'acct.col.supplier': "Supplier",
  'acct.col.terms': "Terms",
  'acct.col.asAt': "Statement",
  'acct.col.balance': "Balance",
  'acct.col.overdue': "Overdue",
  'acct.col.toBill': "To bill",
  'acct.col.billed': "In Xero, unpaid",
  'acct.col.look': "Needs a look",
  'acct.terms.credit': "Credit terms",
  'acct.terms.perInvoice': "Per invoice",
  'acct.terms.creditHint': "Paid from the month-end statement; each order's invoice is recorded only.",
  'acct.terms.perInvoiceHint': "Each invoice becomes its own bill as it arrives.",
  'acct.weOwe': "We owe {amount}",
  'acct.creditHeld': "Holding {amount} credit for us",
  'acct.settled': "Settled",
  'acct.noStatement': "No statement yet",
  'acct.incGst': "inc GST",
  'acct.approvals': "Waiting for your approval",
  'acct.none': "No supplier statements or invoices recorded yet. EmmA reads them from the supplier mail the PO Office files.",
  'acct.checksCount': "{count} to check",
  'acct.heldCount': "{count} held",
  'acct.unavailable': "Supplier statements could not be read: {message}",
  'acct.statementTitle': "Latest statement · as at {day}",
  'acct.checksTitle': "Statement check",
  'acct.check.not_recorded': "On the statement, not in our records",
  'acct.check.amount_differs': "Statement says {statement}, the invoice says {recorded}",
  'acct.check.not_on_statement': "Invoice not on the statement, so it would not be paid",
  'acct.docs': "Invoices, credit notes and statements",
  'acct.col.document': "Document",
  'acct.col.dated': "Dated",
  'acct.col.due': "Due",
  'acct.col.job': "Job",
  'acct.col.amount': "Inc GST",
  'acct.col.state': "State",
  'acct.kind.invoice': "Invoice",
  'acct.kind.credit_note': "Credit note",
  'acct.kind.statement': "Statement",
  'acct.state.recorded': "Recorded",
  'acct.state.toBill': "Waiting for a bill",
  'acct.state.held': "Held: {reason}",
  'acct.state.billed': "Bill {number} · {status}",
  'acct.hold': "Hold",
  'acct.release': "Release",
  'acct.holdReason': "Why is it held off the bills?",
  'acct.void': "Void bill",
  'acct.voidReason': "Why void this bill in Xero?",
  'acct.createBills': "Create {count} bills in Xero · {amount}",
  'acct.createBillsHint': "Your click is the approval. The bills go to Xero awaiting payment. Match the bank payment to them in Xero; never enter it again as spend money.",
  'acct.refresh': "Refresh from Xero",
  'acct.openEmail': "Open email",
  'acct.open': "Open",
} as const;

export type MessageId = keyof typeof EN;
